"use strict";

import "../scss/black-dashboard.scss";

import { CountUp, CountUpOptions } from "countup.js";
import * as Plotly from "plotly.js-dist";
import noUiSlider from "nouislider";
import * as Sentry from "@sentry/browser";
import { BrowserTracing } from "@sentry/tracing";
import Swal from "sweetalert2";

import { manageJobs, cancelJob, startJob, apiUrl, get, sleep } from "./jobs";

// Set up Sentry
try {
    let sentryDsn = JSON.parse(
        document.getElementById("sentry-dsn").textContent
    );
    let sentryRelease = JSON.parse(
        document.getElementById("sentry-release").textContent
    );
    let sentryEnv = JSON.parse(
        document.getElementById("sentry-env").textContent
    );
    Sentry.init({
        dsn: sentryDsn,
        environment: sentryEnv,
        integrations: [new BrowserTracing()],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        release: sentryRelease,
        tracesSampleRate: 0,
    });
} catch {
    console.log("Unable to setup Sentry.");
}

// We need to disable caching so that we always get the latest data from the API
let headers = new Headers();
headers.append("pragma", "no-cache");
headers.append("cache-control", "no-cache");
let noCacheRequest = {
    method: "GET",
    headers: headers,
};

function fetchWithNoCache(url) {
    if (url.includes("?")) {
        return fetch(`${url}&t=${Date.now()}`, noCacheRequest);
    }
    return fetch(`${url}?t=${Date.now()}`, noCacheRequest);
}

let state = {
    maps: {},
};

let detailAssetUserEdited = false;

// When the user picks "Save & calculate" in the unsaved-changes prompt, the
// component form is saved via htmx and the job is started once the swap settles.
let pendingCalcAfterSave = false;

L.Map.addInitHook(function () {
    state.maps[this._container.attributes.id.value] = this;
});

interface State {
    [x: string]: any;
}

// Utils //
///////////

function debounce(callback, delay: number) {
    let timeout;
    return function () {
        clearTimeout(timeout);
        timeout = setTimeout(callback, delay);
    };
}

function triggerEvent(el, type) {
    var e = document.createEvent("HTMLEvents");
    e.initEvent(type, false, true);
    el.dispatchEvent(e);
}

function getPlotDiv(plotId) {
    return document.getElementById(plotId);
}

function getPlotData(plotId) {
    return JSON.parse(document.getElementById(`${plotId}-data`).textContent);
}

async function getChartData(chartId) {
    let projectId = document.getElementById(`${chartId}-plot`).dataset
        .project_id;
    let url = `/api/v1/projects/${projectId}/charts/${chartId}`;
    let response = await fetchWithNoCache(url, noCacheRequest);
    // A results page asks for every one of its charts at once, and a chart
    // that loses a race for a database connection comes back 5xx. That is
    // transient, so it is worth one more try before the chart is given up on:
    // the alternative is an empty panel on a page whose other charts are fine.
    if (!response.ok && response.status >= 500) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        response = await fetchWithNoCache(url, noCacheRequest);
    }
    let data = await response.json();
    return data;
}

function renderPlot(state) {
    let config: any = {
        displayModeBar: false,
        displaylogo: false,
        responsive: false,
    };
    // Solution-B time-series charts (makeTimeSeriesLayout sets dragmode:false for
    // body.solution-b only) get a hover modebar: zoom / pan / zoom-in / zoom-out +
    // reset-to-selected-range + a styled PNG download. fixedrange is off on the
    // x-axis so the buttons work, but dragmode:false + scrollZoom:false keep the
    // chart "locked" (no accidental zoom/pan) until a toolbar button is used — so
    // the fast default windowed view is unchanged. Bar charts and the entire
    // original UI keep displayModeBar:false. The body.solution-b check makes that
    // isolation explicit (dragmode:false already only happens in Solution-B).
    if (
        state.layout &&
        state.layout.dragmode === false &&
        document.body.classList.contains("solution-b")
    ) {
        config = {
            displayModeBar: "hover",
            displaylogo: false,
            responsive: false,
            scrollZoom: false,
            modeBarButtonsToRemove: [
                "select2d",
                "lasso2d",
                "autoScale2d",
                "resetScale2d",
                "toImage",
            ],
            modeBarButtonsToAdd: [
                {
                    name: "resetRange",
                    title: "Reset to selected range",
                    icon: (Plotly as any).Icons.home,
                    click: function (gd) {
                        let checked = document.querySelector(
                            `input[name="${gd.id}-timerange"]:checked`
                        );
                        if (checked) {
                            checked.dispatchEvent(new Event("change"));
                        }
                    },
                },
                {
                    name: "downloadStyled",
                    title: "Download PNG",
                    icon: (Plotly as any).Icons.camera,
                    click: function (gd) {
                        downloadImage(gd.id, "png", gd.id);
                    },
                },
            ],
        };
    }
    const reacted = Plotly.react(state.div, state.traces, state.layout, config);
    Promise.resolve(reacted).then(function () {
        bindRangePresetReset(
            state.div,
            `${state.prefix ?? state.id}-timerange`
        );
        bindAutoRescale(state.div);
    });
    // Solution-B structured multi-axis charts: position the per-unit axes for the
    // currently-visible series and build the custom collapsible legend, then keep
    // both in sync on every toggle (axes appear/disappear with enabled series).
    const tsPlotName = (state.id || "").replace("-plot", "");
    if (
        document.body.classList.contains("solution-b") &&
        TS_MULTIAXIS_PLOTS.has(tsPlotName)
    ) {
        const currency = state.plotData && state.plotData.currency;
        Promise.resolve(reacted).then(() => {
            tsApplyAxes(state.div, currency);
            buildTsLegend(state.div);
            if (!state._tsListener) {
                state._tsListener = true;
                (state.div as any).on("plotly_restyle", function () {
                    tsApplyAxes(state.div, currency);
                    refreshTsLegend(state.div);
                });
            }
        });
    }
    // Once the dispatch line chart is rendered, (re)build the dispatch heat map
    // from its series — it reads that chart's data rather than re-fetching.
    if (
        document.body.classList.contains("solution-b") &&
        tsPlotName === "electricity-generation"
    ) {
        Promise.resolve(reacted).then(() => {
            // Stash the project resolution on the chart so the heat map can map
            // by frequency (60/15 min) instead of trusting the timestamps.
            (state.div as any)._heatmapResolution =
                state.plotData && state.plotData.resolution;
            initDispatchHeatmap();
        });
    }
}

function downloadImage(prefix, format, filename) {
    // Get the original plot
    let originalPlot = document.getElementById(`${prefix}`);
    // Create a plot div offscreen
    let clonedPlot = document.createElement("div");
    clonedPlot.style.position = "absolute";
    clonedPlot.style.left = "-5000px";
    document.body.appendChild(clonedPlot);
    // @ts-ignore
    Plotly.newPlot(clonedPlot, originalPlot.data, originalPlot.layout);
    // Isolation: downloadImage is shared by BOTH the original and Solution-B
    // designs, so gate the reduced export font sizes on the body.solution-b
    // marker — the original UI keeps its existing 30px title / 20px axis sizes.
    const isSolutionB = document.body.classList.contains("solution-b");
    const exportAxisTitleSize = isSolutionB ? 13 : 20;
    const exportPlotTitleSize = isSolutionB ? 18 : 30;
    // Update the cloned plot to remove the range slider and reposition the legend
    let params = {
        paper_bgcolor: "#27293d",
        plot_bgcolor: "#27293d",
        font: {
            color: "#FFF",
            family: "Poppins, sans-serif",
        },
    };
    params["margin"] = {
        t: 120,
    };
    let originalLegend = originalPlot.layout.legend;
    if (originalLegend) {
        params.legend = originalLegend;
        params.legend.y = -0.3;
    }
    let originalXAxis = originalPlot.layout.xaxis;
    if (originalXAxis) {
        originalXAxis.rangeslider = null;
        params.xaxis = originalXAxis;
        params.xaxis.title["font"] = { size: exportAxisTitleSize };
    }
    let originalYAxis2 = originalPlot.layout.yaxis2;
    if (originalYAxis2) {
        params.yaxis2 = originalYAxis2;
        params.yaxis2.title["font"] = { size: exportAxisTitleSize };
    }
    let originalYAxis = originalPlot.layout.yaxis;
    if (originalYAxis) {
        params.yaxis = originalYAxis;
        params.yaxis.title["font"] = { size: exportAxisTitleSize };
        params.yaxis["gridcolor"] = "#FFF";
        params.yaxis["gridwidth"] = 2;
        params.yaxis["zeroline"] = true;
        params.yaxis["zerolinewidth"] = 4;
    }
    let plotTitle = document.getElementById(`${prefix}-header`);
    if (plotTitle) {
        plotTitle = plotTitle.firstChild.textContent;
    } else {
        plotTitle =
            document.getElementById(prefix).previousElementSibling.firstChild
                .textContent;
    }
    if (!plotTitle) {
        plotTitle = document.getElementById(`${prefix}-header`).firstChild
            .textContent;
    }
    params["title"] = {
        text: plotTitle,
        font: {
            size: exportPlotTitleSize,
        },
    };

    Plotly.update(clonedPlot, {}, params);
    // Create and download the image
    Plotly.downloadImage(clonedPlot, {
        format: format,
        width: 1000,
        height: 700,
        filename: filename,
    });
}

function enablePlotDownloads(state) {
    // Add download functionality //
    ////////////////////////////////
    ["jpeg", "png"].forEach((format) => {
        try {
            document.getElementById(
                `${state.prefix}-${format}-download-link`
            ).onclick = function (e) {
                e.preventDefault();
                let filename = state.prefix;
                downloadImage(state.prefix, format, filename);
            };
        } catch {
            console.log(`No ${state.prefix} ${format} download link available`);
        }
    });
}

function getContainerWidth(state: State): number {
    let container = document.getElementById(`${state.id}-container`);
    if (container && container.clientWidth > 0) {
        return container.clientWidth;
    }
    // Prefer modal panel when the plot is inside the upload modal
    let inModal = container && container.closest("#uploadModal");
    // Hidden tab (Solution-B background render): use the results content width
    // so the generated overview image comes out at full width, not the narrow
    // legacy plot-panel fallback.
    if (!inModal) {
        let resultsContent = document.querySelector(
            "#project-detail-card .tab-content"
        ) as HTMLElement | null;
        if (resultsContent && resultsContent.clientWidth > 0) {
            return resultsContent.clientWidth;
        }
    }
    let plotPanel = inModal
        ? document.getElementById("modal-plot-panel") ||
          document.getElementById("plot-panel")
        : document.getElementById("plot-panel") ||
          document.getElementById("modal-plot-panel");
    if (plotPanel && plotPanel.clientWidth > 0) {
        return plotPanel.clientWidth;
    }
    return 800; // Sensible fallback
}

function setRangeSelectorSliderWidth(state: State) {
    let maxWidth = state.forReport ? 1000 : getContainerWidth(state) - 10;
    let sliderWidth: number;
    if (state.timeRange === null) {
        sliderWidth = maxWidth - 12;
    } else {
        sliderWidth = maxWidth * (state.timeRange / state.days);
    }
    // The handle is drawn as wide as the window it selects, which on a long
    // series is a hairline: seven days of a twenty-year project is 1.3px of a
    // 1383px navigator, and a 2px floor is not something a cursor can catch.
    // Solution-B keeps a grabbable minimum; the original UI keeps its 2px.
    const minSliderWidth =
        typeof document !== "undefined" &&
        document.body.classList.contains("solution-b")
            ? 14
            : 2;
    if (sliderWidth < minSliderWidth) {
        sliderWidth = minSliderWidth;
    }
    state.slider.style.width = `${sliderWidth}px`;
}

function setRangeSelectorSliderPosition(state: State) {
    let w = state.forReport ? 1000 : getContainerWidth(state) - 10;
    let perc = state.startLeft / state.slices;
    let leftPos = w * perc;
    if (leftPos < 8) {
        leftPos = 13;
    }
    let correctionFactor = state.forReport ? 4 : 8;
    state.slider.style.left = leftPos - correctionFactor + "px";
}

function renderRangeSelectorSliderImage(state: State) {
    let width = state.forReport ? 1000 : getContainerWidth(state) - 10;
    if (!width || width <= 0) {
        return;
    }
    // The navigator thumbnail is just a shape overview for picking a time range,
    // so keep it single-axis even when the main plot uses per-unit axes (the
    // multi-axis layout's extra y-axes are dropped from imageLayout in setup).
    const imageData = state.traces.map(function (t) {
        return t && t.yaxis && t.yaxis !== "y" ? { ...t, yaxis: "y" } : t;
    });
    Plotly.toImage(
        // @ts-ignore
        { data: imageData, layout: state.imageLayout },
        {
            height: 120,
            width: width,
        }
    ).then(function (url) {
        state.selectorImageElement.setAttribute("src", url);
    });
}

/*
 * Drop the range preset when the user zooms or pans the plot by hand.
 *
 * The preset buttons are radios, and clicking one that is already checked
 * fires no change event — so a preset left looking selected after a manual
 * zoom could never be re-applied without first picking a different one. Once
 * the window on screen is no longer the one the button describes, no button
 * should be lit.
 */
/*
 * Fit every value axis to the data actually on screen.
 *
 * Plotly's own autorange fits the whole series, so zooming into a quiet week
 * leaves the traces pressed flat against an axis scaled for the busiest hour of
 * the year. This recomputes each axis from the points inside the current x
 * range, counting only the traces that are switched on.
 */
function rescaleVisibleY(div: any) {
    const data = div && div.data;
    // Plotly keeps an unset range in _fullLayout until something ranges the
    // axis explicitly, so read both.
    const range =
        (div && div.layout && div.layout.xaxis && div.layout.xaxis.range) ||
        (div &&
            div._fullLayout &&
            div._fullLayout.xaxis &&
            div._fullLayout.xaxis.range) ||
        null;
    if (!data || !range) return;
    const stamp = (value: any) =>
        typeof value === "number" ? value : new Date(value).getTime();
    const from = stamp(range[0]);
    const to = stamp(range[1]);
    if (!isFinite(from) || !isFinite(to)) return;

    const bounds: Record<string, { low: number; high: number }> = {};
    data.forEach(function (trace: any) {
        if (trace.visible === false || trace.visible === "legendonly") return;
        if (!trace.x || !trace.y) return;
        const key = (trace.yaxis || "y").replace("y", "yaxis");
        for (let i = 0; i < trace.x.length; i++) {
            const at = stamp(trace.x[i]);
            if (at < from || at > to) continue;
            const value = trace.y[i];
            if (typeof value !== "number" || !isFinite(value)) continue;
            const seen = bounds[key] || { low: Infinity, high: -Infinity };
            if (value < seen.low) seen.low = value;
            if (value > seen.high) seen.high = value;
            bounds[key] = seen;
        }
    });

    const update: Record<string, number[]> = {};
    Object.keys(bounds).forEach(function (key) {
        const low = bounds[key].low;
        const high = bounds[key].high;
        if (!isFinite(low) || !isFinite(high)) return;
        const pad = (high - low || Math.abs(high) || 1) * 0.08;
        update[`${key}.range`] = [low - pad, high + pad];
    });
    if (!Object.keys(update).length) return;
    div._autoRescaling = true;
    Promise.resolve(Plotly.relayout(div, update)).then(function () {
        div._autoRescaling = false;
    });
}

function bindAutoRescale(target: any) {
    if (!document.body.classList.contains("solution-b")) return;
    const div: any =
        typeof target === "string" ? document.getElementById(target) : target;
    if (!div || div._autoRescaleBound || typeof div.on !== "function") return;
    // Not on the report page. Fitting y to the visible x window is right for a
    // time series being panned, and wrong for a bar chart bound for a document:
    // it crops the axis so that bars no longer start at zero, and 1,053 kW
    // beside 2,000 kWh reads as two bars of nearly equal height.
    if (document.body.classList.contains("report-b")) return;
    div._autoRescaleBound = true;
    div.on("plotly_relayout", function (event: any) {
        if (div._autoRescaling) return;
        const movedX = Object.keys(event).some((key) =>
            /^xaxis\d*\.(range\[[01]\]|autorange)$/.test(key)
        );
        if (!movedX) return;
        rescaleVisibleY(div);
    });
    // Series come and go from the legend; refit to what is left.
    div.on("plotly_restyle", function () {
        if (div._autoRescaling) return;
        rescaleVisibleY(div);
    });
    rescaleVisibleY(div);
}

function bindRangePresetReset(div: any, radioName: string) {
    if (!document.body.classList.contains("solution-b")) return;
    if (!div || !radioName || div._presetResetBound || !div.on) return;
    div._presetResetBound = true;
    div.on("plotly_relayout", function (event: any) {
        const movedX = Object.keys(event).some((key) =>
            /^xaxis\d*\.(range\[[01]\]|autorange)$/.test(key)
        );
        // Ranges the code applied — a preset click, a slider drag — are not the
        // user reaching past the presets.
        if (!movedX || div._applyingRange) return;
        clearRangePreset(radioName);
    });
}

function clearRangePreset(radioName: string) {
    document
        .querySelectorAll<HTMLInputElement>(
            `input[type=radio][name="${radioName}"]`
        )
        .forEach(function (radio) {
            radio.checked = false;
            radio.closest("label")?.classList.remove("active");
        });
}

function relayoutPlotOnRangeSelect(state) {
    const div: any = state.div;
    if (div) div._applyingRange = true;
    Promise.resolve(
        Plotly.relayout(state.div, {
            "xaxis.range": [
                state.plotData.x[state.selectorBox.left],
                state.plotData.x[state.selectorBox.right],
            ],
        })
    ).then(function () {
        if (div) div._applyingRange = false;
        rescaleVisibleY(div);
    });
}

function setUpRangeSelector(state) {
    // Re-measure width each time in case the container was hidden on first render
    state.containerWidth = getContainerWidth(state);

    var slider, w, h;
    var clicked = 0;
    w = state.forReport ? 1000 : state.containerWidth - 10;
    h = 120;
    /* Create slider: */
    if (state.slider) {
        state.slider.remove();
    }
    renderRangeSelectorSliderImage(state);
    state.slider = document.createElement("DIV");
    slider = state.slider;
    slider.setAttribute("class", "img-comp-slider");
    slider.setAttribute("id", `${state.id}-selector-slider`);
    let sliderMarker = document.createElement("DIV");
    sliderMarker.setAttribute("class", "slider-marker");
    slider.appendChild(sliderMarker);
    /* Insert slider */
    state.selectorImageElement.parentElement.insertBefore(
        state.slider,
        state.selectorImageElement
    );
    state.slider.style.height = `${h}px`;
    slider.style.top = "0px";
    setRangeSelectorSliderWidth(state);
    setRangeSelectorSliderPosition(state);
    /* Execute a function when the mouse button is pressed: */
    slider.addEventListener("mousedown", slideReady);
    /* And another function when the mouse button is released: */
    window.addEventListener("mouseup", slideFinish);
    /* Or touched (for touch screens: */
    slider.addEventListener("touchstart", slideReady);
    /* And released (for touch screens: */
    window.addEventListener("touchend", slideFinish);
    // Solution-B: press anywhere on the navigator, not only on the handle. On a
    // long series the handle covers a fraction of a per cent of the strip, so
    // hunting for it is the whole of the difficulty; pressing where you want to
    // be moves the window there and then drags with the cursor as before. The
    // original UI keeps the handle as its only grip.
    if (document.body.classList.contains("solution-b")) {
        state.selectorImageElement.addEventListener("mousedown", jumpAndSlide);
        state.selectorImageElement.addEventListener("touchstart", jumpAndSlide);
    }
    function jumpAndSlide(e) {
        slideReady(e);
        slideMove(e);
    }
    function slideReady(e) {
        /* Prevent any other actions that may occur when moving over the image: */
        e.preventDefault();
        /* The slider is now clicked and ready to move: */
        clicked = 1;
        /* Execute a function when the slider is moved: */
        window.addEventListener("mousemove", slideMove);
        window.addEventListener("touchmove", slideMove);
    }
    function slideFinish() {
        if (clicked !== 1) {
            return false;
        }
        clicked = 0;
        relayoutPlotOnRangeSelect(state);
    }
    function slideMove(e) {
        var pos;
        /* If the slider is no longer clicked, exit this function: */
        if (clicked == 0) return false;
        /* Get the cursor's x position: */
        pos = getCursorPos(e);
        /* Prevent the slider from being positioned outside the image: */
        if (pos < 0) pos = 0;
        if (pos > w) pos = w;
        /* Execute a function that will resize the overlay image according to the cursor: */
        slide(pos);
    }
    function getCursorPos(e) {
        var a,
            x = 0;
        e = e || window.event;
        /* Get the x positions of the image: */
        a = state.selectorImageElement.getBoundingClientRect();
        /* Calculate the cursor's x coordinate, relative to the image: */
        x = e.pageX - a.left;
        /* Consider any page scrolling: */
        x = x - window.pageXOffset;
        return x;
    }
    function slide(x) {
        var halfWidth = slider.offsetWidth / 2;
        var imgWidth = state.selectorImageElement.offsetWidth - 10;
        if (x <= halfWidth) {
            state.selectorBox.left = 0;
            state.selectorBox.right = state.timeRange * 24 - 1;
            slider.style.left = "5px";
        } else if (x + halfWidth >= state.selectorImageElement.offsetWidth) {
            var startLeft = state.slices - state.timeRange * 24;
            if (state.timeRange >= 1) {
                startLeft = startLeft - (startLeft % 24);
            }
            state.selectorBox.right = state.slices - 1;
            state.selectorBox.left = startLeft;
            slider.style.left = `${
                state.selectorImageElement.offsetWidth - slider.offsetWidth - 5
            }px`;
        } else {
            var steps = imgWidth / state.slices;
            var hWidth = (halfWidth / imgWidth) * steps;
            var xPos = (x / imgWidth) * state.slices;
            var left = Math.floor(xPos - hWidth);
            var left = x - halfWidth;
            var steps = state.slices / imgWidth;
            var leftIndex = Math.floor(left * steps);
            if (state.timeRange >= 1) {
                // Check if it's the start of a day
                if (leftIndex <= 24) {
                    leftIndex = 24;
                } else {
                    leftIndex = leftIndex - (leftIndex % 24);
                }
            }
            state.selectorBox.left = leftIndex;
            let maxRight = state.slices - 1;
            let rightIndex = leftIndex + state.timeRange * 24 - 1;

            if (state.resolution === "hourly" && !state.isMultiYear) {
                if (state.selectedRange === "6-hours") {
                    rightIndex = leftIndex + state.timeRange * 6;
                }
                rightIndex = leftIndex + state.timeRange * 24;
            }

            if (rightIndex >= maxRight) {
                rightIndex = maxRight;
            }
            state.selectorBox.right = rightIndex;
            slider.style.left = x - slider.offsetWidth / 2 + "px";
        }
        state.startLeft = state.selectorBox.left;
        state.startRight = state.selectorBox.right;
    }
    let radios = document.querySelectorAll(
        `input[type=radio][name="${state.prefix}-timerange"]`
    );
    let correctionFactor = state.resolution === "fifteen_minutes" ? 4 : 1;
    let oneDay = 1 * correctionFactor;
    let sevenDays = 7 * correctionFactor;
    let oneMonth = 30 * correctionFactor;
    let sixMonths = 30 * 6 * correctionFactor;
    let oneYear = 365 * correctionFactor;
    let sixHours = (1 / 4) * 6 * correctionFactor;
    if (state.isMultiYear || state.resolution === "fifteen_minutes") {
        sixHours = sixHours / 6;
    } else {
        sixHours = sixHours / 6;
    }
    for (let radio of radios) {
        radio.onchange = function () {
            state.selectedValue = radio.value;
            switch (radio.value) {
                case "1-day":
                    state.timeRange = oneDay;
                    break;
                case "7-days":
                    state.timeRange = sevenDays;
                    break;
                case "1-month":
                    state.timeRange = oneMonth;
                    break;
                case "6-months":
                    state.timeRange = sixMonths;
                    break;
                case "1-year":
                    state.timeRange = oneYear;
                    break;
                case "6-hours":
                    state.timeRange = sixHours;
                    break;
                case "all-years":
                    state.timeRange = null;
                    break;
            }
            if (
                state.timeRange === null ||
                (state.timeRange === oneYear && !state.isMultiYear)
            ) {
                state.startLeft = 0;
                state.endRight = state.slices - 1;
            } else {
                if (state.slices > 8760 && !state.isMultiYear) {
                    state.endRight = state.startLeft + state.timeRange * 24;
                } else {
                    // Optional if we want to make the selector jump to the left
                    // if (state.isMultiYear) {
                    //     state.startLeft = 0;
                    // }
                    state.endRight = state.startLeft + state.timeRange * 24;
                }
                let diff = state.endRight - (state.slices - 1);
                if (diff > 0) {
                    state.startLeft = state.startLeft - diff;
                    state.endRight = state.endRight - diff;
                }
            }
            // Pin the start to the start of a day if the time range is not fractional
            if (state.timeRange < 1 && state.timeRange > 0) {
                state.startLeft = state.startLeft - (state.startLeft % 24);
            }
            state.layout.xaxis.range = [
                state.plotData.x[state.startLeft],
                state.plotData.x[state.endRight],
            ];
            state.selectorBox.right = state.endRight;
            state.selectorBox.left = state.startLeft;
            setRangeSelectorSliderWidth(state);
            setRangeSelectorSliderPosition(state);
            relayoutPlotOnRangeSelect(state);
        };
    }
}

function makeStackedAnnualTraces(plotData, order, labels, colors) {
    let xValues = plotData.x;
    let traces = [];
    // The server already maps each component's model name to a display type
    // before sending (get_stacked_annual_generation_plot_data), so what arrives
    // is "solar" and "chp", never "pv" and "gaschp". This table kept only the
    // pre-mapping keys for those two, so both series looked up as undefined and
    // were dropped: an array's whole year of generation never reached the
    // chart. The model names stay as well, for any payload that still carries
    // them.
    let nameMap = {
        battery: "battery",
        Battery: "battery",
        hydro: "hydro",
        wind: "wind",
        solar: "solar",
        pv: "solar",
        chp: "chp",
        gaschp: "chp",
        "Gas genset": "genset",
        "Diesel genset": "genset",
        genset: "genset",
        demand: "demand",
    };
    for (let yD of plotData.y) {
        let componentType = nameMap[yD.name.replace(": electricity", "")];
        if (componentType) {
            let trace = {
                x: xValues,
                y: yD.values,
                mode: "lines",
                name: labels[componentType],
                opacity: 1,
                fillcolor: colors[componentType],
                line: { color: colors[componentType] },
                hovertemplate: "%{y} [kW]",
            };
            if (componentType === "demand") {
                trace.line.dash = "dash";
            } else {
                trace.stackgroup = "one";
            }
            traces.push(trace);
        }
    }
    return traces;
}

const seriesNames = {
    wind: "Wind: source",
    solar: "Solar: source",
    primary_reserve_pos: "Primary reserve pos",
    primary_reserve_neg: "Primary reserve neg",
    secondary_reserve_pos: "Secondary reserve pos",
    secondary_reserve_neg: "Secondary reserve neg",
    tertiary_reserve_pos: "Tertiary reserve pos",
    tertiary_reserve_neg: "Tertiary reserve neg",
    primary_reserve_pos_neg_price: "Primary reserve pos/neg price",
    primary_reserve_pos_price: "Primary reserve pos price",
    primary_reserve_neg_price: "Primary reserve neg price",
    secondary_reserve_pos_price: "Secondary reserve pos price",
    secondary_reserve_neg_price: "Secondary reserve neg price",
    tertiary_reserve_pos_price: "Tertiary reserve pos price",
    tertiary_reserve_neg_price: "Tertiary reserve neg price",
    electricity_prices_buy: "Electricity prices buy",
    electricity_prices_sell: "Electricity prices sell",
    intraday_prices_buy: "Intraday prices buy",
    intraday_prices_sell: "Intraday prices sell",
    real_time_prices_buy: "Real time prices buy",
    real_time_prices_sell: "Real time prices sell",
    gas_prices: "Gas prices",
    diesel_prices: "Diesel prices",
    coal_prices: "Coal prices",
    additional_prices: "Additional prices",
    max_grid_inflow: "Max grid inflow",
    max_grid_outflow: "Max grid outflow",
    ambient_temperatures: "Ambient temperatures",
    demand: "Demand",
    "solar generation kW": "Solar generation: source [kW]",
    "wind generation kW": "Wind generation: source [kW]",
    allow_market_charge: "Allow market charge",
    consider_max_load_in_grid_charges: "Consider max load in grid charges",
    hydrogen_consumption: "Hydrogen consumption",
    hydrogen_price: "Hydrogen price",
    "day-ahead market revenues": "Day-ahead",
    "intraday market revenues": "Intraday",
    "real-time market revenues": "Real-time",
    "final settlement (ID) market revenues": "SoC-Mgt. (final settlement)",
    "reserve (energy) market revenues": "Reserve (energy)",
    // Read through reserveCapacityName: the battery is only worth naming when
    // something else is providing reserve too.
    "reserve (full revenue capacity) market revenues": "Reserve (capacity)",
    "reserve (full revenue capacity, others) market revenues":
        "Reserve (capacity, others)",
    "total market revenues": "Total",
};

//: The two capacity streams. Where the second is present the first has to say
//: which is which; where it is not, "battery" tells the reader nothing they did
//: not already know from a system whose only reserve comes from one.
const RESERVE_CAPACITY = "reserve (full revenue capacity) market revenues";
const RESERVE_CAPACITY_OTHERS =
    "reserve (full revenue capacity, others) market revenues";

/** The display name of a series, given everything drawn beside it. */
function seriesNameIn(rawName: string, siblings: string[]): string {
    if (
        rawName === RESERVE_CAPACITY &&
        siblings.indexOf(RESERVE_CAPACITY_OTHERS) !== -1
    ) {
        return "Reserve (capacity, battery)";
    }
    return seriesNames[rawName] ? seriesNames[rawName] : rawName;
}

function makeScatterPlotTraces(plotData) {
    let xValues = plotData.x;
    let traces = [];
    plotData.y.forEach((obj) => {
        if (plotData.for_report) {
            obj.visible = true;
        }
        let trace = {
            x: xValues,
            y: obj.values,
            mode: "lines",
            name: seriesNameIn(
                obj.name,
                (plotData.y || []).map((s: any) => s.name)
            ),
            // Raw (unmapped) name, so unit/domain classification keeps the
            // keywords that seriesNames strips (e.g. "...market revenues" -> "Total").
            _rawName: obj.name,
            opacity: 0.9,
            hovertemplate: "%{y:.2f} [kW]",
            visible: obj.visible,
        };
        if (obj.line) {
            trace.line = obj.line;
        }
        if (obj.name === "available stored energy") {
            trace.hovertemplate = "%{y:.2f} [kWh]";
        }
        if (obj.name === "ambient_temperatures") {
            trace.hovertemplate = "%{y:.2f} °C";
        }
        if (
            obj.name.endsWith("number of plants operating") ||
            obj.name.endsWith("number of starts") ||
            obj.name.endsWith("allow_market_charge") ||
            obj.name.endsWith("consider_max_load_in_grid_charges")
        ) {
            trace.hovertemplate = "%{y:.0f}";
        }
        if (
            obj.name.endsWith(": state of charge") ||
            obj.name.endsWith("average efficiency per plant")
        ) {
            trace.yaxis = "y2";
            trace.hovertemplate = "%{y:.2f} [%]";
        }
        if (
            obj.name.endsWith("price") ||
            obj.name.endsWith("prices") ||
            obj.name.endsWith("prices") ||
            obj.name.endsWith("prices_buy") ||
            obj.name.endsWith("prices_sell")
        ) {
            trace.hovertemplate = "%{y:.2f} " + `[${plotData.currency}/kWh]`;
        }
        if (obj.name.endsWith("revenues")) {
            trace.hovertemplate = "%{y:,.2f} " + `[${plotData.currency}]`;
        }
        traces.push(trace);
    });
    return traces;
}

// ── Solution-B: structured multi-axis timeseries (overview + inputs charts) ──
// The "Multi-timeseries" overview and the "Other inputs" chart pile many series
// of incompatible units (kW, %, currency/kWh, currency, kWh, °C, m/s, kW/m²,
// flags) onto a single value axis, with a flat, overwhelming legend. Instead we
// (a) give each UNIT family its own y-axis that appears only while a series of
// that unit is shown, and (b) group the legend BY DOMAIN. Battery state-of-charge
// keeps a persistent % axis so it can be read against any other metric. All of
// this is gated on body.solution-b and applied only to these plots.
const TS_MULTIAXIS_PLOTS = new Set([
    "additional-timeseries",
    "inputs-timeseries",
    "modal-inputs-timeseries",
]);
// Axis order = visual priority + fixed axis id per unit. percent is index 1 so it
// reuses the y2 axis makeTimeSeriesLayout already builds (0-100, right side).
const TS_UNIT_ORDER = [
    "power",
    "percent",
    "price",
    "money",
    "energy",
    "env", // temperature (°C) + wind speed (m/s): same numeric range, one axis
    "irradiance",
    "flag",
];
function tsAxisId(unit) {
    const i = TS_UNIT_ORDER.indexOf(unit);
    return i <= 0 ? "y" : "y" + (i + 1);
}
function tsAxisKey(unit) {
    const id = tsAxisId(unit);
    return id === "y" ? "yaxis" : "yaxis" + id.slice(1);
}
function tsUnitLabel(unit, currency) {
    const cur = currency || "€";
    return (
        {
            power: "kW",
            percent: "%",
            price: cur + "/kWh",
            money: cur,
            energy: "kWh",
            env: "°C / m/s",
            irradiance: "kW/m²",
            flag: "0/1",
        }[unit] || ""
    );
}
// Classify a series name into { unit, domain, isSoc } from its text.
function classifyTsSeries(name) {
    const n = String(name || "")
        .toLowerCase()
        .trim();
    const isSoc = /state of charge/.test(n);
    // Renewable input-file "sources" series, matched EXACTLY so the bare radiation
    // ("solar") / wind-speed ("wind") columns don't capture the kW generation ones:
    //   - "solar/wind generation kW" → power (kW), with the generators
    //   - "solar"                    → irradiance (kW/m²), environment
    //   - "wind"                     → wind speed (m/s, env axis), environment
    if (n === "solar generation kw" || n === "wind generation kw")
        return { unit: "power", domain: "Demand & generation", isSoc };
    if (n === "solar")
        return { unit: "irradiance", domain: "Environment", isSoc };
    if (n === "wind") return { unit: "env", domain: "Environment", isSoc };
    let unit;
    if (/m\/s|wind speed|wind \[|temperature|ambient/.test(n)) unit = "env";
    else if (/kw\/m²|kw\/m2|irradiance|solar \[/.test(n)) unit = "irradiance";
    else if (/state of charge|state of health|efficiency|\[%\]/.test(n))
        unit = "percent";
    // revenues AND profits are a currency amount (not a per-kWh price), so they
    // belong on the money axis even when the name also contains "...prices".
    else if (/revenue|profit/.test(n)) unit = "money";
    else if (/price/.test(n)) unit = "price";
    else if (/available stored energy|\[kwh\]|stored energy/.test(n))
        unit = "energy";
    else if (
        /allow_market_charge|consider_max_load|number of plants|number of starts/.test(
            n
        )
    )
        unit = "flag";
    else unit = "power";
    // Domain = legend group (finer, by instrument). Order matters: forecasts/bids
    // and revenues are pulled out before the price/volume buckets, and reserve
    // VOLUMES before plain reserve PRICES (which fall through to "Prices").
    let domain;
    if (
        /state of charge|state of health|available stored energy|stored energy/.test(
            n
        )
    )
        domain = "Storage";
    else if (
        /expected prices|foresight|expected total profits/.test(n) ||
        /\bbids? /.test(n)
    )
        domain = "Forecasts & bids";
    else if (/revenue/.test(n)) domain = "Revenues";
    else if (/reserve/.test(n) && /transaction|committed|reserve power/.test(n))
        domain = "Reserve volumes";
    else if (
        /transaction|grid connection|market and grid|max_grid|allow_market_charge|consider_max_load/.test(
            n
        )
    )
        domain = "Traded volumes";
    else if (/price/.test(n)) domain = "Prices";
    else if (
        /m\/s|wind speed|wind \[|irradiance|solar \[|temperature|ambient/.test(
            n
        )
    )
        domain = "Environment";
    else domain = "Demand & generation";
    return { unit, domain, isSoc };
}
// Tag each trace with its unit/domain, route it to the unit's axis, and group the
// legend by domain (with a unit-aware hover). The "_ts*" props persist in gd.data
// so the toggle handler can recompute axes later.
function applyTsTraceGrouping(traces, currency) {
    traces.forEach((t) => {
        if (!t) return;
        const { unit, domain, isSoc } = classifyTsSeries(t._rawName || t.name);
        t._tsUnit = unit;
        t._tsDomain = domain;
        t._tsIsSoc = isSoc;
        t.yaxis = tsAxisId(unit);
        const lbl = tsUnitLabel(unit, currency);
        t.hovertemplate =
            "%{y:,.2f}" +
            (lbl ? " " + lbl : "") +
            "<extra>%{fullData.name}</extra>";
    });
    // Plotly only renders a legend group (and its title) cleanly when that
    // group's traces are contiguous, so cluster traces by domain. Stable sort
    // keeps the original order within each group.
    traces.sort(
        (a, b) =>
            (TS_DOMAIN_ORDER.indexOf(a._tsDomain) + 1 || 99) -
            (TS_DOMAIN_ORDER.indexOf(b._tsDomain) + 1 || 99)
    );
}
const TS_DOMAIN_ORDER = [
    "Demand & generation",
    "Storage",
    "Prices",
    "Traded volumes",
    "Reserve volumes",
    "Revenues",
    "Forecasts & bids",
    "Environment",
];
// Create the non-base y-axes for the unit families present in the data (the base
// y and the percent y2 already exist from makeTimeSeriesLayout). They start
// hidden; tsApplyAxes shows/positions the ones in use.
function ensureTsAxes(layout, traces) {
    const present = new Set();
    traces.forEach((t) => t && t._tsUnit && present.add(t._tsUnit));
    TS_UNIT_ORDER.forEach((u, idx) => {
        if (idx === 0 || !present.has(u)) return;
        const key = tsAxisKey(u);
        if (layout[key]) return; // keep existing (e.g. y2 percent)
        layout[key] = {
            ...solutionBAxis(),
            overlaying: "y",
            side: "right",
            visible: false,
            showgrid: false,
            automargin: false,
            tickfont: { size: 10 },
        };
    });
}
// Show + position only the axes whose unit has a visible series (the base kW axis
// and, if any state-of-charge series exists, the % axis are always kept). Extra
// axes stack on the right; the plot domain shrinks to make room. Runs at render
// and on every legend toggle, so axes appear/disappear with the enabled series.
function tsApplyAxes(gd, currency) {
    const data = (gd && gd.data) || [];
    if (!data.length) return;
    const present = [];
    const visibleUnits = new Set();
    let hasSoc = false;
    data.forEach((t) => {
        const u = t._tsUnit;
        if (!u) return;
        if (present.indexOf(u) === -1) present.push(u);
        if (t.visible === true || t.visible === undefined) visibleUnits.add(u);
        if (t._tsIsSoc) hasSoc = true;
    });
    if (!present.length) return;
    // base (left) axis: power if present, else the highest-priority present unit
    const base =
        present.indexOf("power") !== -1
            ? "power"
            : TS_UNIT_ORDER.find((u) => present.indexOf(u) !== -1);
    visibleUnits.add(base);
    if (hasSoc) visibleUnits.add("percent"); // SoC axis always available
    const rightUnits = TS_UNIT_ORDER.filter(
        (u) => u !== base && visibleUnits.has(u)
    );
    const STEP = 0.08;
    const domainRight = Math.max(0.45, 1 - rightUnits.length * STEP);
    const relayout: any = { "xaxis.domain": [0, domainRight] };
    present.forEach((u) => {
        relayout[tsAxisKey(u) + ".visible"] = false;
    });
    const baseKey = tsAxisKey(base);
    relayout[baseKey + ".visible"] = true;
    relayout[baseKey + ".title"] = tsUnitLabel(base, currency);
    relayout[baseKey + ".side"] = "left";
    relayout[baseKey + ".showgrid"] = true;
    if (tsAxisId(base) !== "y") {
        relayout[baseKey + ".overlaying"] = "y";
        relayout[baseKey + ".anchor"] = "free";
        relayout[baseKey + ".position"] = 0;
    }
    rightUnits.forEach((u, j) => {
        const key = tsAxisKey(u);
        relayout[key + ".visible"] = true;
        relayout[key + ".title"] = tsUnitLabel(u, currency);
        relayout[key + ".overlaying"] = "y";
        relayout[key + ".side"] = "right";
        relayout[key + ".anchor"] = "free";
        relayout[key + ".position"] = Math.min(1, domainRight + j * STEP);
        relayout[key + ".showgrid"] = false;
    });
    Plotly.relayout(gd, relayout);
}
// One-shot setup at render: route/group traces, create the per-unit axes, keep
// the navigator thumbnail single-axis, and turn OFF Plotly's native legend (a
// 60-entry legend dwarfs the plot and Plotly 1.x can't collapse groups — we
// render our own compact collapsible legend instead, see buildTsLegend).
function setupTsMultiAxis(traces, layout, imageLayout, plotData) {
    const currency = plotData && plotData.currency;
    applyTsTraceGrouping(traces, currency); // tags + sorts by domain in place
    ensureTsAxes(layout, traces);
    layout.showlegend = false;
    // The range-selector thumbnail stays single-axis (it's only a navigation
    // shape); drop the extra y-axes the clone would otherwise carry.
    if (imageLayout) {
        Object.keys(imageLayout).forEach((k) => {
            if (/^yaxis[2-9]$/.test(k)) delete imageLayout[k];
        });
    }
    return traces;
}

// ── Custom collapsible legend for the multi-axis timeseries charts ──────────
// A thin full-width bar above the plot (so the navigator stays aligned): chips
// for the currently-shown series (the readable legend) + a "Series" button that
// opens a floating, per-domain COLLAPSIBLE picker. Toggling a series restyles
// the plot, which drives the dynamic axes. Keeps the plot the dominant element.
function tsTraceColor(gd, i) {
    try {
        const fd = (gd._fullData && gd._fullData[i]) || gd.data[i];
        return (
            (fd.line && fd.line.color) ||
            (fd.marker && fd.marker.color) ||
            "#8fb8db"
        );
    } catch (e) {
        return "#8fb8db";
    }
}
function tsIsOn(t) {
    return t && (t.visible === true || t.visible === undefined);
}
function toggleTsTrace(gd, i) {
    Plotly.restyle(gd, { visible: tsIsOn(gd.data[i]) ? "legendonly" : true }, [
        i,
    ]);
}
function refreshTsLegend(gd) {
    const bar = document.getElementById(gd.id + "-ts-legend");
    if (!bar) return;
    bar.querySelectorAll(".ts-lg-item").forEach((el: any) => {
        el.classList.toggle("on", tsIsOn(gd.data[+el.dataset.idx]));
    });
    bar.querySelectorAll(".ts-lg-group").forEach((grp: any) => {
        const idxs = JSON.parse(grp.dataset.idxs || "[]");
        const on = idxs.filter((i) => tsIsOn(gd.data[i])).length;
        const c = grp.querySelector(".ts-lg-count");
        if (c) c.textContent = on + "/" + idxs.length;
    });
    const active = bar.querySelector(".ts-legend-active");
    if (active) {
        active.innerHTML = "";
        gd.data.forEach((t, i) => {
            if (!t._tsDomain || !tsIsOn(t)) return;
            const chip = document.createElement("button");
            chip.type = "button";
            chip.className = "ts-active-chip";
            chip.title = "Hide " + t.name;
            chip.innerHTML =
                `<span class="ts-lg-sw" style="background:${tsTraceColor(
                    gd,
                    i
                )}"></span>` +
                `<span class="ts-active-name">${t.name}</span><span class="ts-active-x">×</span>`;
            chip.onclick = () => toggleTsTrace(gd, i);
            active.appendChild(chip);
        });
        if (!active.children.length)
            active.innerHTML =
                '<span class="ts-legend-empty">No series shown — add from “Series”.</span>';
    }
}
function buildTsLegend(gd) {
    const plotDiv = gd;
    if (document.getElementById(plotDiv.id + "-ts-legend")) {
        refreshTsLegend(gd);
        return;
    }
    const bar = document.createElement("div");
    bar.id = plotDiv.id + "-ts-legend";
    bar.className = "ts-legend-bar";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ts-legend-pick-btn";
    btn.innerHTML = '<i class="bi bi-sliders"></i> Series';
    const active = document.createElement("div");
    active.className = "ts-legend-active";
    const picker = document.createElement("div");
    picker.className = "ts-legend-picker";
    const groups = {};
    gd.data.forEach((t, i) => {
        if (!t || !t._tsDomain) return;
        (groups[t._tsDomain] = groups[t._tsDomain] || []).push(i);
    });
    TS_DOMAIN_ORDER.forEach((domain) => {
        const idxs = groups[domain];
        if (!idxs || !idxs.length) return;
        const grp = document.createElement("div");
        grp.className = "ts-lg-group";
        grp.dataset.idxs = JSON.stringify(idxs);
        const head = document.createElement("button");
        head.type = "button";
        head.className = "ts-lg-group-head";
        const on = idxs.filter((i) => tsIsOn(gd.data[i])).length;
        head.innerHTML =
            `<i class="bi bi-chevron-right ts-lg-chev"></i>` +
            `<span class="ts-lg-domain">${domain}</span>` +
            `<span class="ts-lg-count">${on}/${idxs.length}</span>`;
        const body = document.createElement("div");
        body.className = "ts-lg-items";
        idxs.forEach((i) => {
            const t = gd.data[i];
            const item = document.createElement("button");
            item.type = "button";
            item.className = "ts-lg-item" + (tsIsOn(t) ? " on" : "");
            item.dataset.idx = String(i);
            item.innerHTML = `<span class="ts-lg-sw" style="background:${tsTraceColor(
                gd,
                i
            )}"></span><span class="ts-lg-name">${t.name}</span>`;
            item.onclick = () => toggleTsTrace(gd, i);
            body.appendChild(item);
        });
        head.onclick = () => grp.classList.toggle("open");
        grp.appendChild(head);
        grp.appendChild(body);
        picker.appendChild(grp);
    });
    btn.onclick = (e) => {
        e.stopPropagation();
        bar.classList.toggle("picker-open");
    };
    document.addEventListener("click", (e) => {
        if (
            bar.classList.contains("picker-open") &&
            !bar.contains(e.target as Node)
        )
            bar.classList.remove("picker-open");
    });
    bar.appendChild(btn);
    bar.appendChild(active);
    bar.appendChild(picker);
    plotDiv.parentNode.insertBefore(bar, plotDiv);
    refreshTsLegend(gd);
}

// ── Dispatch heat map ───────────────────────────────────────────────────────
// hour-of-day (y) × calendar day (x) for a dispatch source picked from the
// dispatch line chart. Reads that chart's already-rendered series (no extra
// fetch — dispatch JSON can be large). Solution-B only.
//
// Steps-per-hour from the project RESOLUTION, not the timestamps: the series is
// a fixed-frequency sequence (60 or 15 min) from a start time, but the per-point
// timestamps can be unreliable about the frequency (e.g. 15-min data carrying
// hourly-looking stamps), which would scramble the hour/day mapping.
function tsStepsPerHour(resolution) {
    const r = String(resolution || "").toLowerCase();
    if (r.indexOf("fifteen") !== -1 || r === "pt15m") return 4;
    if (r === "pt30m") return 2;
    return 1; // "hour" / "PT60M" / default
}
function reshapeToHourDayGrid(xs, ys, stepsPerHour) {
    const sph = stepsPerHour > 0 ? stepsPerHour : 1;
    const stepsPerDay = sph * 24;
    const nDays = Math.max(1, Math.ceil(ys.length / stepsPerDay));
    const sum = [];
    const cnt = [];
    for (let h = 0; h < 24; h++) {
        sum.push(new Array(nDays).fill(0));
        cnt.push(new Array(nDays).fill(0));
    }
    // Map purely by POSITION in the sequence (index → day, hour).
    for (let i = 0; i < ys.length; i++) {
        const v = Number(ys[i]);
        if (!isFinite(v)) continue;
        const day = Math.floor(i / stepsPerDay);
        const hour = Math.floor((i % stepsPerDay) / sph);
        sum[hour][day] += v;
        cnt[hour][day] += 1;
    }
    const z = [];
    for (let h = 0; h < 24; h++) {
        const row = [];
        for (let d = 0; d < nDays; d++) {
            row.push(cnt[h][d] ? sum[h][d] / cnt[h][d] : null);
        }
        z.push(row);
    }
    // Column (day) labels = START date + day offset (the timestamps mark the
    // start reliably; the frequency does not come from them).
    const startMs = Date.parse(String(xs[0] || "").slice(0, 10) + "T00:00:00Z");
    const base = isFinite(startMs) ? startMs : 0;
    const days = [];
    for (let d = 0; d < nDays; d++) {
        days.push(new Date(base + d * 86400000).toISOString().slice(0, 10));
    }
    return { z, days, hours: Array.from({ length: 24 }, (_, h) => h) };
}
function renderDispatchHeatmap(name) {
    const srcDiv: any = document.getElementById("electricity-generation-plot");
    const plotDiv = document.getElementById("dispatch-heatmap-grid");
    if (!srcDiv || !srcDiv.data || !plotDiv) return;
    const t = srcDiv.data.find((d) => d.name === name);
    if (!t || !t.x || !t.y) return;
    const stepsPerHour = tsStepsPerHour(srcDiv._heatmapResolution);
    const { z, days, hours } = reshapeToHourDayGrid(t.x, t.y, stepsPerHour);
    const flat = z.flat().filter((v) => v != null);
    const hasNeg = flat.some((v) => v < 0);
    const unit = /state of charge|state of health|efficiency/i.test(name)
        ? "%"
        : "kW";
    // Signed series (e.g. battery charge/discharge) get a diverging scale around
    // zero; non-negative series a sequential dark-blue→teal scale. The "zero"
    // colour is OPAQUE (not transparent) and is also used as the plot background,
    // so idle cells read as a filled grid that fills the whole plot box rather
    // than showing the page background through them.
    const zeroColor = hasNeg ? "#2a2f4a" : "#0d1b2a";
    const colorscale = hasNeg
        ? [
              [0, "#1d8cf8"],
              [0.5, zeroColor],
              [1, "#fd5d93"],
          ]
        : [
              [0, zeroColor],
              [0.5, "#1d8cf8"],
              [1, "#00f2c3"],
          ];
    const traces: any = [
        {
            type: "heatmap",
            z,
            x: days,
            y: hours,
            colorscale,
            zmid: hasNeg ? 0 : undefined,
            hovertemplate:
                "%{x} &nbsp; %{y}:00<br>%{z:,.1f} " + unit + "<extra></extra>",
            colorbar: {
                title: { text: unit, side: "right" },
                thickness: 12,
                tickfont: { size: 10, color: "rgba(255,255,255,0.6)" },
                outlinewidth: 0,
            },
        },
    ];
    const layout: any = {
        paper_bgcolor: "transparent",
        // Solid plot background = the colorscale's zero colour, so the data
        // rectangle reads as a fully-filled grid (idle cells blend into it).
        plot_bgcolor: zeroColor,
        font: {
            color: "rgba(255,255,255,0.7)",
            family: "Poppins, sans-serif",
            size: 12,
        },
        margin: { l: 52, r: 20, t: 10, b: 40 },
        height: 360,
        xaxis: { ...solutionBAxis(), type: "date", showgrid: false },
        yaxis: {
            ...solutionBAxis(),
            title: { text: "Hour of day" },
            dtick: 3,
            range: [-0.5, 23.5],
            showgrid: false,
        },
    };
    Plotly.react(plotDiv, traces, layout, {
        responsive: false,
        displayModeBar: false,
    });
}
function initDispatchHeatmap() {
    if (!document.body.classList.contains("solution-b")) return;
    const container = document.getElementById("dispatch-heatmap-container");
    const sel: any = document.getElementById("dispatch-heatmap-source");
    const srcDiv: any = document.getElementById("electricity-generation-plot");
    if (!container || !sel || !srcDiv || !srcDiv.data) return;
    const sources = srcDiv.data
        .filter((d) => d && d.name && d.x && d.y)
        .map((d) => d.name);
    if (!sources.length) {
        container.style.display = "none";
        return;
    }
    container.style.display = "";
    if (!sel.options.length) {
        sources.forEach((nm) => {
            const o = document.createElement("option");
            o.value = nm;
            o.textContent = nm;
            sel.appendChild(o);
        });
        // default to the first real dispatch source (skip state-of-charge etc.)
        sel.value =
            sources.find((nm) => !/state of (charge|health)/i.test(nm)) ||
            sources[0];
        sel.onchange = () => renderDispatchHeatmap(sel.value);
    }
    // Only render once the grid actually has width. The dispatch chart often
    // renders (and triggers this) while the Technical tab is still hidden, where
    // the grid has 0 width and Plotly would fall back to a 700px default that
    // fills only half the box. The shown.bs.tab hook re-runs this when visible.
    const grid = document.getElementById("dispatch-heatmap-grid");
    if (!grid || grid.offsetParent === null || grid.clientWidth < 50) return;
    renderDispatchHeatmap(sel.value);
}
// Resize/build the dispatch heat map when the Technical tab becomes visible (it
// may have been built at the wrong width while hidden, or not built yet).
function resizeDispatchHeatmap() {
    const grid: any = document.getElementById("dispatch-heatmap-grid");
    if (!grid || grid.offsetParent === null || grid.clientWidth < 50) return;
    if (grid.classList.contains("js-plotly-plot")) {
        Plotly.Plots.resize(grid);
    } else {
        initDispatchHeatmap();
    }
}

// Layouts //
/////////////

// Solution B (the new design) themes its charts to the dark UI: muted gridlines
// and axis lines instead of Plotly's default light ones, so plots sit in the page
// rather than reading as bright boxes. Gated on the body.solution-b marker (set in
// new_ui/__base.html) so the original design's charts are unchanged. Spread into
// fresh axis literals only — never mutate a live layout, because the PNG/report
// export reuses layout.yaxis by reference and intentionally forces a solid look.
const SOLUTION_B_AXIS = {
    gridcolor: "rgba(255,255,255,0.08)",
    zerolinecolor: "rgba(255,255,255,0.18)",
    linecolor: "rgba(255,255,255,0.25)",
    tickcolor: "rgba(255,255,255,0.25)",
};
function solutionBAxis() {
    return document.body.classList.contains("solution-b")
        ? SOLUTION_B_AXIS
        : {};
}

// ── Solution-B polish for the non-timeseries result plots ──────────────────
// Applied (gated on body.solution-b) after each plot's base config so the
// original UI is untouched. Fixes: clashing colours, the clumsy pie, the
// redundant duplicate second-axis numbers, over-wide bars, the bottom legend
// eating ~120px, and verbose chrome. The y-axis *title* is kept (some hovers
// read %{yaxis.title.text} for the unit) — only the redundant right-axis
// numbers and the self-explanatory x-axis title are dropped.
const NON_TS_RESULT_PLOTS = new Set([
    "peak-demand-vs-installed-capacity",
    "installed-capacity",
    "initial-investment",
    "costs",
    "annual-cash-flows-per-component",
    "production",
]);
// Cohesive light-blue family (alternating tones), for the single-comparison
// result plots — initial investment, cost overview and the summary peak-demand
// chart — where one calm colour reads cleaner than a categorical mix.
const RESULT_PLOT_BLUES = [
    "#6699cc",
    "#aecde6",
    "#7aa9d4",
    "#c8dcef",
    "#5b93c9",
    "#9dc0e2",
    "#8fb8db",
    "#bdd5ec",
];

// High-contrast categorical palette for the genuinely multi-series result charts
// (the per-component cash flows: many "Capex X"/"O&M X" bars stacked together).
// Hues are ordered to maximise contrast between ADJACENT entries — warm/cool and
// light/dark alternate — and near-duplicate tones (two blues, pink+purple) are
// avoided so stacked segments stay distinguishable.
const RESULT_PLOT_PALETTE = [
    "#1d8cf8", // blue
    "#ff8d72", // orange
    "#00d29a", // teal
    "#fd5d93", // pink
    "#f5a623", // amber
    "#9b59f6", // purple
    "#2dce89", // green
    "#ffd166", // yellow
    "#08acde", // cyan
    "#e14eca", // magenta
];

// Result plots that read better in one calm blue family than a categorical mix.
const BLUE_FAMILY_RESULT_PLOTS = new Set([
    "initial-investment",
    "costs",
    "peak-demand-vs-installed-capacity",
]);

// Canonical colour per generator/storage/market TYPE (Solution-B only), so a
// given entity keeps ONE colour across every chart and across projects (PV is
// always amber, wind always blue, …) rather than shifting with trace order.
// Drawn from the palette above; the original UI is untouched (callers gate on
// solution-b).
const ENTITY_COLORS = {
    solar: "#f5a623", // amber — evokes the sun
    wind: "#1d8cf8", // blue
    battery: "#00d29a", // teal / mint
    demand: "#fd5d93", // pink — stands out as the dashed reference line
    market: "#9b59f6", // purple
    genset: "#ff8d72", // orange
    hydro: "#08acde", // cyan
    chp: "#e0c341", // gold
};

// Component name/type → canonical entity key. STRICT equality (after lowering and
// dropping a trailing ": qualifier"): "Pv"/"Pv: state of charge" → solar, but
// "Capex Pv" → null (so cash-flow series stay individually coloured, not all
// collapsed onto the PV colour).
const ENTITY_SYNONYMS = {
    // short type / display synonyms
    pv: "solar",
    solar: "solar",
    wind: "wind",
    battery: "battery",
    basicstorage: "battery",
    storage: "battery",
    demand: "demand",
    load: "demand",
    "peak demand": "demand",
    market: "market",
    grid: "market",
    genset: "genset",
    gasgenset: "genset",
    dieselgenset: "genset",
    hydro: "hydro",
    chp: "chp",
    gaschp: "chp",
    // Django verbose_name labels (used by the dispatch + capacity charts, where
    // the series name is the component's verbose_name|capfirst). Listing the
    // whole label keeps the STRICT match — "Capex battery" still won't match
    // "battery", so cash-flow series stay individually coloured.
    photovoltaic: "solar",
    "wind turbine": "wind",
    "thermal storage": "battery",
    "market and grid connection": "market",
    "gas genset": "genset",
    "diesel genset": "genset",
    "gas chp": "chp",
};
function normalizeEntityKey(name) {
    if (!name) return null;
    let s = String(name).toLowerCase().trim();
    const colon = s.indexOf(": ");
    if (colon !== -1) s = s.slice(0, colon).trim();
    return ENTITY_SYNONYMS[s] || null;
}
function entityColor(name) {
    const key = normalizeEntityKey(name);
    return key ? ENTITY_COLORS[key] : null;
}
// Solution-B: give each dispatch/generation series its canonical per-type colour
// so PV/wind/battery/demand/market read identically across charts AND across
// projects (the server sends no colour for these, so Plotly would otherwise
// assign by trace order, which shifts when a project lacks a producer). No-op on
// the original UI; series whose name isn't a known entity keep Plotly's default.
function applyDispatchColors(traces) {
    if (!document.body.classList.contains("solution-b")) return traces;
    traces.forEach((t) => {
        const c = entityColor(t && t.name);
        if (c) t.line = { ...(t.line || {}), color: c };
    });
    return traces;
}

// Canonical colour per market-revenue stream (Solution-B only), keyed by the
// server's raw series stem (the part before " market revenues"), so the
// revenues line chart and the revenue-composition bar use IDENTICAL colours and
// each stream keeps its colour across projects.
const REVENUE_STREAM_COLORS = {
    "day-ahead": "#1d8cf8", // blue
    intraday: "#ff8d72", // orange
    "real-time": "#00d29a", // teal
    "final settlement (id)": "#9b59f6", // purple
    "reserve (energy)": "#f5a623", // amber
    "reserve (full revenue capacity)": "#fd5d93", // pink
    "reserve (full revenue capacity, others)": "#08acde", // cyan
    total: "#c7d0d9", // neutral — the dashed total line
};
function revenueStreamColor(name) {
    if (!name) return null;
    const stem = String(name)
        .toLowerCase()
        .replace(/\s*market revenues\s*$/, "")
        .trim();
    return REVENUE_STREAM_COLORS[stem] || null;
}

// Blue-only ramp for the efficiency Sankey: the user wants it to stay within the
// colour family (no complementary/contrast hues), so nodes cycle through tones
// of blue and the links use a single translucent blue.
const SANKEY_BLUES = [
    "#1d8cf8",
    "#08acde",
    "#5b93c9",
    "#6699cc",
    "#7aa9d4",
    "#8fb8db",
    "#9dc0e2",
    "#aecde6",
    "#bdd5ec",
    "#c8dcef",
];

// The text of a Plotly axis title, which may be a bare string or a {text} object.
function axisTitleText(title): string {
    if (!title) return "";
    return typeof title === "string" ? title : title.text || "";
}
// Merge two axis-unit titles into one honest label, factoring out a shared
// prefix: ("Amount in kW", "Amount in kWh") → "Amount in kW / kWh";
// ("kW", "kWh") → "kW / kWh".
function combineAxisUnits(t1, t2): string {
    const a = axisTitleText(t1).trim();
    const b = axisTitleText(t2).trim();
    if (!a) return b;
    if (!b || a === b) return a;
    const wa = a.split(" ");
    const wb = b.split(" ");
    let i = 0;
    while (i < wa.length && i < wb.length && wa[i] === wb[i]) i++;
    const prefix = wa.slice(0, i).join(" ");
    const restA = wa.slice(i).join(" ");
    const restB = wb.slice(i).join(" ");
    if (prefix && restA && restB) return `${prefix} ${restA} / ${restB}`;
    return `${a} / ${b}`;
}

function styleResultPlotB(plotName, layout, traces) {
    // 1. Pie → horizontal bar (cleaner, no wasted circle/corners, sorted).
    const pie = traces.find((t) => t && t.type === "pie");
    if (pie) {
        const labels = pie.labels || [];
        const values = (pie.values || []).map(Number);
        const unit = Array.isArray(pie.text)
            ? pie.text[0] || ""
            : pie.text || "";
        const order = labels
            .map((_, i) => i)
            .sort((a, b) => values[a] - values[b]); // ascending → largest on top
        traces = [
            {
                type: "bar",
                orientation: "h",
                x: order.map((i) => values[i]),
                y: order.map((i) => labels[i]),
                text: order.map(
                    (i) => values[i].toLocaleString() + (unit ? " " + unit : "")
                ),
                textposition: "auto",
                cliponaxis: false,
                hovertemplate: "%{y}<br>%{x:,} " + unit + "<extra></extra>",
            },
        ];
        layout.xaxis = {
            ...SOLUTION_B_AXIS,
            tickformat: "s",
            title: null,
            automargin: true,
            zeroline: false,
        };
        layout.yaxis = {
            ...SOLUTION_B_AXIS,
            title: null,
            automargin: true,
            gridcolor: "rgba(0,0,0,0)",
        };
    }

    // 2. Colour traces. The single-comparison plots (initial investment, cost
    //    overview, summary peak-demand) read cleaner in one calm blue family;
    //    the multi-series cash-flow chart needs the high-contrast categorical
    //    palette so its many stacked components stay distinguishable.
    const palette = BLUE_FAMILY_RESULT_PLOTS.has(plotName)
        ? RESULT_PLOT_BLUES
        : RESULT_PLOT_PALETTE;
    traces.forEach((t, i) => {
        if (!t || t.type === "pie") return;
        t.marker = {
            ...(t.marker || {}),
            color: palette[i % palette.length],
        };
        t.opacity = 1;
    });

    // 3. Drop the self-explanatory x-axis title; ensure soft gridlines.
    if (layout.xaxis) {
        layout.xaxis.title = null;
        if (!layout.xaxis.gridcolor)
            layout.xaxis.gridcolor = "rgba(255,255,255,0.06)";
    }
    if (layout.yaxis && !layout.yaxis.gridcolor)
        layout.yaxis.gridcolor = "rgba(255,255,255,0.08)";

    // 4. Honest unit labelling when a chart mixes kW (power) and kWh (energy).
    //    The two y-axes share an identical forced range, so the right axis only
    //    duplicated the scale while the left "kW" label wrongly implied every
    //    bar — including the kWh storage-capacity bar — was a power. Bake the
    //    correct unit into each bar's hover, then collapse onto ONE axis
    //    labelled with BOTH units; moving the y2 bars to y1 leaves their
    //    positions unchanged (same range).
    if (layout.yaxis2) {
        const u1 = axisTitleText(layout.yaxis && layout.yaxis.title);
        const u2 = axisTitleText(layout.yaxis2.title);
        traces.forEach((t) => {
            if (!t) return;
            if (typeof t.hovertemplate === "string") {
                t.hovertemplate = t.hovertemplate.replace(
                    /%\{yaxis\.title\.text\}/g,
                    t.yaxis === "y2" ? u2 : u1
                );
            }
            if (t.yaxis === "y2") t.yaxis = "y1";
        });
        layout.yaxis = layout.yaxis || {};
        layout.yaxis.title = { text: combineAxisUnits(u1, u2) };
        delete layout.yaxis2;
    }

    // 5. Slimmer bars (no full-width balloons for 2–4 categories).
    layout.bargap = 0.55;

    // 6. Legend: drop it for a single series; otherwise a compact horizontal
    //    strip under the title — reclaims the ~120px reserved below the plot.
    const series = traces.filter((t) => t && t.type !== "pie").length;
    layout.annotations = (layout.annotations || []).filter(
        (a) => a && a.text !== "Legend"
    );
    if (series <= 1) {
        layout.showlegend = false;
        layout.legend = null;
    } else {
        layout.showlegend = true;
        layout.legend = {
            orientation: "h",
            x: 0,
            y: 1.06,
            yanchor: "bottom",
            font: { size: 11 },
        };
    }

    // 7. Reclaim margins, compact height, muted font.
    layout.margin = {
        l: 64,
        r: 18,
        t: series > 1 ? 30 : 12,
        b: 40,
        pad: 4,
    };
    layout.height = 340;
    layout.font = {
        ...(layout.font || {}),
        color: "rgba(255,255,255,0.7)",
        family: "Poppins, sans-serif",
        size: 12,
    };
    return { layout, traces };
}

// Compact display of large numbers, mirroring the Django `abbreviate_number`
// template filter (project_manager/templatetags/qsystem_extras.py) used for the
// result tables. Keep the two in sync: scale into K/M/B/T with one decimal
// (trailing ".0" stripped) and keep the sign.
function abbreviateNumber(value: number): string {
    const abs = Math.abs(value);
    const units: [number, string][] = [
        [1e12, "T"],
        [1e9, "B"],
        [1e6, "M"],
        [1e3, "K"],
    ];
    for (const [threshold, suffix] of units) {
        if (abs >= threshold) {
            const scaled = (value / threshold).toFixed(1).replace(/\.0$/, "");
            return `${scaled} ${suffix}`;
        }
    }
    return value.toLocaleString();
}

function makeTimeSeriesLayout(
    yAxisLabel,
    convertPercent = true,
    xAxisLabel = "Intervals",
    yAxisLabels = []
) {
    let hoverformat;
    let tickvals;
    let range;
    let yAxis1Label;
    if (yAxisLabels.length) {
        yAxis1Label = yAxisLabels[0];
    } else {
        yAxis1Label = yAxisLabel;
    }
    let yAxis2Label;
    if (yAxisLabels.length > 1) {
        yAxis2Label = yAxisLabels[1];
    } else {
        yAxis2Label = "Percent [%]";
    }
    if (!convertPercent) {
        hoverformat = ",.4r";
        tickvals = null;
        range = [0, 100];
    } else {
        hoverformat = ",.2%";
        tickvals = [0, 0.2, 0.4, 0.6, 0.8, 1];
        range = [0, 1];
    }

    // ISOLATION: the time-series interaction/sizing below differs only for
    // Solution-B. The original UI keeps its locked axes (no zoom/pan), no
    // dragmode and a fixed 600px height — see the gates on isSolutionB.
    const isSolutionB =
        typeof document !== "undefined" &&
        document.body.classList.contains("solution-b");

    // Solution-B only: size the plot so the tabs + section selector, the chart
    // and the navigator/overview image all fit on one page: viewport height minus
    // the chrome above/below, clamped so it never gets cramped (<360) nor
    // unnecessarily tall (>560). The original UI keeps a fixed 600px.
    let tsHeight = 600;
    if (isSolutionB && typeof window !== "undefined" && window.innerHeight) {
        tsHeight = Math.min(560, Math.max(360, window.innerHeight - 320));
    }

    // Solution-B: put the legend ABOVE the plot. A legend below the x-axis
    // shares the bottom margin with the (two-line) date tick labels; when it
    // wraps to several rows on a narrow/notebook viewport, Plotly shrinks the
    // plotting area to fit it and — because legend.y is a fraction of that
    // shrinking area — the legend rides UP into the tickers (the reported
    // overlap; worst on the many-series multi-timeseries chart). Anchoring it
    // above the plot, growing upward into the top margin, makes overlap with the
    // x-axis tickers structurally impossible at any size. Compact font so it
    // wraps to fewer rows. The original UI keeps its fixed bottom { y: -0.2 }.
    //
    // The gap to the plot's TOP edge is (y - 1) * plotAreaHeight, so a fixed
    // y just above 1 (e.g. 1.02) leaves only ~6-9px — the legend reads as
    // sitting on the plot and a tall data line could touch it. Derive the
    // offset from the chart height for a roughly constant, comfortable gap
    // (the est. plot-area height accounts for the top legend band + bottom
    // margin); cap it so very short charts still keep a real gap.
    const tsTopGapFrac = Math.min(0.3, 48 / Math.max(tsHeight - 120, 130));
    const tsLegend = isSolutionB
        ? {
              orientation: "h",
              yanchor: "bottom",
              y: 1 + tsTopGapFrac,
              x: 0,
              xanchor: "left",
              font: { size: 11 },
          }
        : { orientation: "h", y: -0.2 };

    return {
        xaxis: {
            ...solutionBAxis(),
            automargin: true,
            showgrid: false,
            // Solution-B unlocks the x-axis so the hover toolbar can zoom; the
            // original UI keeps it locked (its previous behaviour).
            fixedrange: isSolutionB ? false : true,
            hoverformat: "%d-%m-%Y %H:%M",
            title: {
                // The date axis is self-explanatory; the centred "Intervals"
                // title collided with the horizontal legend, so drop it in
                // Solution-B (the original UI keeps it).
                text: isSolutionB ? "" : xAxisLabel,
                standoff: 10,
            },
        },
        yaxis: {
            ...solutionBAxis(),
            tickformat: ",",
            automargin: true,
            title: {
                text: yAxis1Label,
                standoff: 15,
            },
            fixedrange: true,
            hoverformat: ",.2f",
        },
        yaxis2: {
            ...solutionBAxis(),
            automargin: true,
            title: {
                text: yAxis2Label,
                standoff: -15,
            },
            fixedrange: true,
            range: [0, 100],
            overlaying: "y",
            side: "right",
            tickmode: "array",
            tickvals: tickvals,
            ticktext: ["0", "20", "40", "60", "80", "100"],
            hoverformat: hoverformat,
            showgrid: false,
        },
        showlegend: true,
        legend: tsLegend,
        paper_bgcolor: "transparent",
        plot_bgcolor: "transparent",
        // Solution-B sets dragmode:false (locked until the hover toolbar is used);
        // this also flags renderPlot to attach the hover modebar. The original UI
        // leaves dragmode at Plotly's default and gets no modebar.
        dragmode: isSolutionB ? false : undefined,
        font: {
            color: "#FFF",
            family: "Poppins, sans-serif",
        },
        margin: {
            r: 20,
            b: 50,
            t: isSolutionB ? 28 : 20,
            pad: 4,
        },
        height: tsHeight,
        hovermode: "x unified",
        hoverlabel: { bgcolor: "#6c757d", namelength: -1 },
    };
}

function makeStackedAreaLayout(yAxisLabel) {
    let layout = makeTimeSeriesLayout(yAxisLabel);
    // The original UI tightens the legend gap; Solution-B keeps the responsive
    // gap from makeTimeSeriesLayout so the legend never rides into the tickers.
    if (!document.body.classList.contains("solution-b")) {
        // @ts-ignore
        layout.legend.y = -0.12;
    }
    return layout;
}

function makeScatterPlotLayout(yAxisLabel, convertPercent = true, xAxisLabel) {
    let layout = makeTimeSeriesLayout(yAxisLabel, convertPercent, xAxisLabel);
    return layout;
}

function makeBasicPlotLayout(plotData) {
    return {
        title: null,
        xaxis: {
            ...solutionBAxis(),
            automargin: true,
            title: plotData.x_label,
        },
        yaxis: {
            ...solutionBAxis(),
            title: plotData.y_label,
            tickformat: "s",
            hoverformat: ",",
            range: [],
        },
        showlegend: false,
        paper_bgcolor: "transparent",
        plot_bgcolor: "transparent",
        font: { color: "rgba(255,255,255, .8)", family: "Poppins" },
        margin: {
            r: 20,
            b: 50,
            t: 20,
            pad: 4,
        },
        hovermode: "closest",
        height: 600,
    };
}

function makeSelectorPlotLayout(mainPlotLayout) {
    let selectorPlotLayout = JSON.parse(JSON.stringify(mainPlotLayout));
    selectorPlotLayout.showlegend = false;
    selectorPlotLayout.xaxis.showgrid = false;
    selectorPlotLayout.yaxis.showgrid = false;
    selectorPlotLayout.title = null;
    selectorPlotLayout.xaxis.title = null;
    selectorPlotLayout.yaxis.title = null;
    selectorPlotLayout.xaxis.showticklabels = false;
    selectorPlotLayout.yaxis.showticklabels = false;
    selectorPlotLayout.margin = {
        r: 5,
        b: 10,
        t: 10,
        l: 5,
        pad: 4,
    };
    if (selectorPlotLayout.yaxis2) {
        selectorPlotLayout.yaxis2.title = null;
        selectorPlotLayout.yaxis2.showticklabels = false;
    }
    return selectorPlotLayout;
}

// Traces //
////////////

function makeBarPlotTraces(plotData) {
    let xValues = plotData.x;
    let traces = [];
    plotData.y.forEach((obj) => {
        traces.push({
            x: xValues,
            y: obj.values,
            type: "bar",
            name: obj.name,
            opacity: 0.9,
            hovertemplate: obj.hovertemplate,
        });
    });
    return traces;
}

/* Give an all-zero bar chart a plain axis.
 *
 * With nothing but zeros Plotly autoranges to a hair either side of the origin
 * and, under the "s" tick format, labels it in thousandths — "80m, 60m, 40m" —
 * so a chart that correctly has nothing in it reads as a broken one. */
function fixAxisForAllZeroBars(layout, traces) {
    const anyValue = traces.some((trace) =>
        (trace.y ?? []).some((value) => value)
    );
    if (!anyValue && layout && layout.yaxis) {
        layout.yaxis.range = [0, 1];
        layout.yaxis.tickformat = null;
    }
    return layout;
}

// Plots //
///////////

function sum(array) {
    if (array.length) {
        return array.reduce((a, b) => {
            return a + b;
        });
    }
    return 0;
}

async function getPlotState(plotName) {
    // Get the initial state
    let plotId = `${plotName}-plot`;
    var div = document.getElementById(plotId);
    let selectorImageElement = document.getElementById(
        `${plotId}-selector-image`
    );
    let containerElement = document.getElementById(`${plotId}-container`);
    if (containerElement === null) {
        return null;
    }
    let containerWidth = containerElement.clientWidth;
    if (containerWidth === 0) {
        // Container is on a hidden tab (Solution-B background render). Lay the
        // chart out at the width it will actually be shown at — the results
        // tab-content width — so the layout (legend/axis titles) and the
        // generated overview image come out at full width and need no fix-up on
        // show. Falls back to the legacy panels otherwise.
        let resultsContent = document.querySelector(
            "#project-detail-card .tab-content"
        ) as HTMLElement | null;
        if (resultsContent && resultsContent.clientWidth > 0) {
            containerWidth = resultsContent.clientWidth;
        } else {
            let fallback =
                document.getElementById("plot-panel") ||
                document.getElementById("modal-plot-panel");
            if (fallback) {
                containerWidth = fallback.clientWidth;
            }
        }
    }
    let plotData = null;

    if (
        [
            "efficiency",
            "installed-capacity",
            "electricity-generation",
            "additional-timeseries",
            "heat1-generation",
            "heat2-generation",
            "stacked-annual-generation",
            "initial-investment",
            "dimensions",
            "costs",
            "production",
            "annual-cash-flows-per-component",
            "inputs-timeseries",
            "modal-inputs-timeseries",
            "load-profile",
            "modal-load-profile",
            "revenues",
            "peak-demand-vs-installed-capacity",
        ].includes(plotName)
    ) {
        let attempts = 10;
        try {
            while (attempts > 0 && plotData === null) {
                try {
                    plotData = await getChartData(plotName);
                } catch (err) {
                    attempts -= 1;
                    console.log("Error getting chart data", err);
                    if (attempts === 0) {
                        throw err;
                    }
                }
            }
        } catch (err) {
            console.log("Error getting chart data", err);
            alert("Error getting chart data--please reload the page");
            Sentry.captureException(err);
        }
    } else {
        const dataEl = document.getElementById(`${plotId}-data`);
        if (!dataEl) {
            return null;
        }
        plotData = getPlotData(plotId);
    }
    plotData.for_report = div.dataset.for_report;
    let slices;
    let days;
    let timeRange;
    let startLeft;
    let endRight;
    if (plotData.x) {
        slices = plotData.x.length;
        days = slices / 24;
        timeRange = 7;
        if (plotData.resolution === "fifteen_minutes") {
            timeRange = timeRange * 4;
        }
        startLeft = slices - timeRange * 24;
        startLeft = startLeft - (startLeft % 24);
        endRight = slices - 1;
    } else {
        slices = null;
        days = null;
        timeRange = null;
        startLeft = null;
        endRight = null;
    }
    let imageLayout = null;
    let includeSelector = false;
    let timeRangeSelect = document.getElementById(
        `${plotId}-time-range-select`
    );
    let layout;
    let traces;
    let plotPanel: HTMLElement | null = document.getElementById("plot-panel");
    // If the plot lives inside the upload modal, use the modal's plot panel for width
    if (containerElement.closest("#uploadModal")) {
        plotPanel = document.getElementById("modal-plot-panel") || plotPanel;
    }
    let fontColor = plotData.for_report ? "#212529" : "#FFF";
    // ISOLATION: a few interactive chart heights + the peak-demand colours below
    // are Solution-B-only; the original UI keeps its previous sizes/colours.
    const isSolutionB = document.body.classList.contains("solution-b");
    switch (plotName) {
        case "stacked-annual-generation":
            // Solution-B uses the canonical per-type colours so the annual chart
            // matches the dispatch chart (PV/wind/battery/… identical); the
            // original UI keeps its previous palette.
            let colors = isSolutionB
                ? {
                      battery: ENTITY_COLORS.battery,
                      hydro: ENTITY_COLORS.hydro,
                      wind: ENTITY_COLORS.wind,
                      solar: ENTITY_COLORS.solar,
                      genset: ENTITY_COLORS.genset,
                      market: ENTITY_COLORS.market,
                      demand: ENTITY_COLORS.demand,
                  }
                : {
                      battery: "#414141", /// Black
                      hydro: "#A6E2FF", // Light blue
                      wind: "#5A67FF", // Blue
                      solar: "#FFE200", // Yellow
                      genset: "#788075", // Gray
                      market: "#c7dada", // Light gray
                  };
            let order = [
                "battery", // Battery
                "market", // Market
                "hydro", // Hydro
                "wind", // Wind
                "solar", // Solar
                "genset", // Diesel and gas gensets
                "demand", // Demand
            ];
            let labels = {
                battery: "Battery", // Battery
                hydro: "Hydro", // Hydro
                wind: "Wind", // Wind
                solar: "Solar", // Solar
                genset: "Genset", // Diesel and gas gensets
                market: "Market", // Market
                demand: "Demand", // Demand
            };
            layout = makeStackedAreaLayout("Electricity generation [kW]");
            if (plotData.for_report) {
                layout.height = 525;
                layout.width = 1000;
            }
            imageLayout = makeSelectorPlotLayout(layout);
            layout.xaxis.range = [plotData.x[startLeft], plotData.x[endRight]];
            traces = makeStackedAnnualTraces(plotData, order, labels, colors);
            includeSelector = plotData.for_report ? true : true;
            break;
        case "initial-investment":
            let plotType = "pie";
            layout = {
                paper_bgcolor: "transparent",
                plot_bgcolor: "transparent",
                font: {
                    color: "#FFF",
                    family: "Poppins, sans-serif",
                },
                margin: {
                    r: 20,
                    l: 20,
                    b: 50,
                    t: 70,
                    pad: 4,
                },
                height: 550,
            };
            if (plotData.for_report) {
                layout.height = 450;
            } else {
                layout.height = 400;
                layout.margin.b = 120;
                layout.margin.r = 80;
                layout.margin.l = 80;
                layout.showlegend = false;
            }
            traces = [
                {
                    values: plotData.values,
                    labels: plotData.labels,
                    type: plotType,
                    insidetextorientation: "radial",
                    hovertemplate:
                        "%{label}<br>%{value} [%{text}]<br>%{percent}<extra></extra>",
                    texttemplate: "%{label}<br>%{value} %{text}",
                    text: plotData.currency,
                    textposition: "outside",
                    automargin: true,
                },
            ];
            // A pie of nothing but zeros draws nothing, leaving a panel that
            // reads as a chart that failed. Say what it means instead.
            if (!(plotData.values ?? []).some((value) => value)) {
                layout.annotations = [
                    {
                        text: "No investment: every component in this system costs nothing.",
                        showarrow: false,
                        xref: "paper",
                        yref: "paper",
                        x: 0.5,
                        y: 0.5,
                        font: { size: 13, color: "rgba(255,255,255,.55)" },
                    },
                ];
            }
            break;
        case "efficiency":
            layout = {
                paper_bgcolor: "transparent",
                plot_bgcolor: "transparent",
                font: {
                    color: "#FFF",
                    family: "Poppins, sans-serif",
                },
                margin: {
                    r: 20,
                    l: 20,
                    b: 70,
                    t: 20,
                    pad: 30,
                },
                height: 350,
            };
            let x_positions = plotData.x;
            let y_positions = plotData.y;
            function makeLink(rawLink) {
                if (plotData.for_report) {
                    rawLink.color = "#abb3b2";
                }
                return rawLink;
            }
            traces = [
                {
                    type: "sankey",
                    arrangement: "snap",
                    domain: {
                        x: [0, 1],
                        y: [0, 1],
                    },
                    orientation: "h",
                    valueformat: ".0f",
                    valuesuffix: " [kWh]",
                    node: {
                        pad: 15,
                        thickness: 15,
                        line: {
                            color: "black",
                            width: 0.5,
                        },
                        label: plotData.label,
                        x: x_positions,
                        y: y_positions,
                        color: plotData.color,
                    },
                    link: makeLink(plotData.link),
                },
            ];
            // Solution-B: keep the Sankey within the blue family (no
            // complementary/contrast hues) — cycle node tones of blue and use a
            // single translucent blue for the links. The original UI and the
            // printable report keep the server-provided colours.
            if (isSolutionB && !plotData.for_report) {
                const sankeyLabels = plotData.label || [];
                traces[0].node.color = sankeyLabels.map(
                    (_, i) => SANKEY_BLUES[i % SANKEY_BLUES.length]
                );
                traces[0].link.color = "rgba(102,153,204,0.28)";
            }
            if (plotData.for_report) {
                layout.height = 525;
                layout.width = 1000;
            }
            break;
        case "heat1-generation":
            layout = makeScatterPlotLayout(plotData.y_label, false);
            if (plotData.for_report) {
                layout.height = 525;
                layout.width = 1000;
            }
            imageLayout = makeSelectorPlotLayout(layout);
            traces = applyDispatchColors(makeScatterPlotTraces(plotData));
            layout.xaxis.range = [plotData.x[startLeft], plotData.x[endRight]];
            includeSelector = true;
            break;
        case "heat2-generation":
            layout = makeScatterPlotLayout(plotData.y_label, false);
            if (plotData.for_report) {
                layout.height = 525;
                layout.width = 1000;
            }
            imageLayout = makeSelectorPlotLayout(layout);
            traces = applyDispatchColors(makeScatterPlotTraces(plotData));
            layout.xaxis.range = [plotData.x[startLeft], plotData.x[endRight]];
            includeSelector = true;
            break;
        case "electricity-generation":
            layout = makeScatterPlotLayout(plotData.y_label, false);
            if (plotData.for_report) {
                layout.height = 525;
                layout.width = 1000;
            }
            imageLayout = makeSelectorPlotLayout(layout);
            traces = applyDispatchColors(makeScatterPlotTraces(plotData));
            layout.xaxis.range = [plotData.x[startLeft], plotData.x[endRight]];
            includeSelector = true;
            break;
        case "revenues":
            // Was a template literal written with plain quotes, so it printed
            // "Market Revenues [plotData.currency]" and was then overwritten
            // with the bare currency — losing the words entirely. The label
            // comes from the data now, like every other axis title.
            layout = makeScatterPlotLayout(plotData.y_label, false);
            if (plotData.for_report) {
                layout.height = 525;
                layout.width = 1000;
            }
            // The centred x-axis title collides with the horizontal legend in
            // Solution-B (the date axis is self-explanatory), so omit it there.
            if (!isSolutionB) {
                let xaxisTitle: string;
                if (plotData.resolution == "hour") {
                    xaxisTitle = "Hourly intervals";
                } else {
                    xaxisTitle = "15-minute intervals";
                }
                layout.xaxis.title = {
                    text: xaxisTitle,
                };
            }
            imageLayout = makeSelectorPlotLayout(layout);
            traces = makeScatterPlotTraces(plotData);
            // Solution-B: recolour each stream with its canonical colour so the
            // line chart matches the revenue-composition bar below it and each
            // stream keeps its colour across projects (preserve the dashed total).
            if (isSolutionB) {
                traces.forEach((t, i) => {
                    const c = revenueStreamColor(
                        plotData.y[i] && plotData.y[i].name
                    );
                    if (c) t.line = { ...(t.line || {}), color: c };
                });
            }
            layout.xaxis.range = [plotData.x[startLeft], plotData.x[endRight]];
            includeSelector = true;
            break;
        case "additional-timeseries":
            layout = makeScatterPlotLayout("", false, plotData.x_label);
            if (plotData.for_report) {
                layout.height = 525;
                layout.width = 1000;
            }
            imageLayout = makeSelectorPlotLayout(layout);
            traces = makeScatterPlotTraces(plotData);
            if (isSolutionB && !plotData.for_report) {
                traces = setupTsMultiAxis(
                    traces,
                    layout,
                    imageLayout,
                    plotData
                );
            }
            layout.xaxis.range = [plotData.x[startLeft], plotData.x[endRight]];
            includeSelector = true;
            break;
        case "inputs-timeseries":
            layout = makeScatterPlotLayout(
                "Electricity generation [kW]",
                false,
                plotData.x_label
            );
            if (plotData.for_report) {
                layout.height = 525;
                layout.width = 1000;
            }
            if (plotPanel) {
                layout.width = plotPanel.clientWidth - 10;
            }

            imageLayout = makeSelectorPlotLayout(layout);
            traces = makeScatterPlotTraces(plotData);
            if (isSolutionB && !plotData.for_report) {
                traces = setupTsMultiAxis(
                    traces,
                    layout,
                    imageLayout,
                    plotData
                );
            }
            layout.xaxis.range = [plotData.x[startLeft], plotData.x[endRight]];
            includeSelector = true;
            break;
        case "modal-inputs-timeseries":
            layout = makeScatterPlotLayout(
                "Electricity generation [kW]",
                false,
                plotData.x_label
            );
            if (plotData.for_report) {
                layout.height = 525;
                layout.width = 1000;
            }
            if (plotPanel) {
                layout.width = plotPanel.clientWidth - 10;
            }

            imageLayout = makeSelectorPlotLayout(layout);
            traces = makeScatterPlotTraces(plotData);
            if (isSolutionB && !plotData.for_report) {
                traces = setupTsMultiAxis(
                    traces,
                    layout,
                    imageLayout,
                    plotData
                );
            }
            layout.xaxis.range = [plotData.x[startLeft], plotData.x[endRight]];
            includeSelector = true;
            break;
        case "load-profile":
            layout = makeBasicPlotLayout(plotData);
            if (plotPanel) {
                layout.width = plotPanel.clientWidth - 10;
            }
            imageLayout = makeSelectorPlotLayout(layout);
            traces = makeScatterPlotTraces(plotData);
            layout.title = null;
            layout.xaxis.range = [plotData.x[startLeft], plotData.x[endRight]];
            includeSelector = true;
            break;
        case "modal-load-profile":
            layout = makeBasicPlotLayout(plotData);
            if (plotPanel) {
                layout.width = plotPanel.clientWidth - 10;
            }
            imageLayout = makeSelectorPlotLayout(layout);
            traces = makeScatterPlotTraces(plotData);
            layout.title = null;
            layout.xaxis.range = [plotData.x[startLeft], plotData.x[endRight]];
            includeSelector = true;
            break;
        case "component-output":
            layout = makeBasicPlotLayout(plotData);
            traces = makeScatterPlotTraces(plotData);
            if (containerElement && containerElement.dataset.height) {
                layout.height = parseInt(containerElement.dataset.height, 10);
            }
            break;
        case "installed-capacity":
            layout = makeBasicPlotLayout(plotData);
            if (plotData.for_report) {
                layout.height = 530;
                layout.width = 1000;
                layout.margin.b = 120;
                layout.margin.r = 80;
            } else {
                layout.height = isSolutionB ? 430 : 350;
                layout.margin.b = 120;
                layout.margin.r = 80;
                layout.margin.l = 80;
            }
            layout.hovermode = "closest";
            layout.yaxis.range = plotData.y_range;
            if (plotData.y2_label) {
                layout.yaxis2 = {
                    tickformat: ",",
                    title: plotData.y2_label,
                    overlaying: "y",
                    side: "right",
                    range: plotData.y_range,
                    tickformat: ",",
                };
                if (plotData.for_report) {
                    layout.showlegend = true;
                    layout.legend = {
                        x: 1,
                        y: 1,
                        xanchor: "right",
                        text: "Legend",
                    };
                    layout.annotations = [
                        {
                            y: 1.05,
                            x: 1,
                            align: "right",
                            valign: "top",
                            yref: "paper",
                            xref: "paper",
                            text: "Legend",
                            showarrow: false,
                            xanchor: "right",
                            yanchor: "top",
                        },
                    ];
                } else {
                    layout.showlegend = false;
                    layout.legend = null;
                }
            }
            traces = plotData.traces;
            break;
        case "annual-cash-flows-per-component":
            layout = makeBasicPlotLayout(plotData);
            layout.margin.b = 120;
            layout.margin.r = 80;
            layout.hovermode = "x unified";
            layout.hoverlabel = { bgcolor: "#6c757d" };
            layout.yaxis.range = plotData.y_range;
            layout.yaxis.hoverformat = ",.0f";
            layout.barmode = "relative";
            layout.showlegend = true;
            layout.legend = {
                orientation: "h",
            };
            if (plotData.for_report) {
                layout.legend.y = -0.2;
            } else {
                layout.legend.y = -0.5;
            }

            if (plotData)
                if (plotData.y2_label) {
                    layout.yaxis2 = {
                        tickformat: ",",
                        title: plotData.y2_label,
                        overlaying: "y",
                        side: "right",
                        range: plotData.y_range,
                    };
                    layout.showlegend = true;
                    layout.legend = {
                        x: 1,
                        y: 1,
                        xanchor: "right",
                        text: "Legend",
                    };
                    layout.annotations = [
                        {
                            y: 1.05,
                            x: 1,
                            align: "right",
                            valign: "top",
                            yref: "paper",
                            xref: "paper",
                            text: "Legend",
                            showarrow: false,
                            xanchor: "right",
                            yanchor: "top",
                        },
                    ];
                }
            traces = plotData.traces;
            if (plotData.for_report) {
                layout.height = 525;
                layout.width = 1000;
            } else {
                layout.height = isSolutionB ? 430 : 350;
                layout.margin.b = 120;
                layout.margin.r = 80;
                layout.margin.l = 80;
            }
            break;
        case "peak-demand-vs-installed-capacity":
            let isResults =
                document.getElementById("component-schematic-column")?.dataset
                    .hasResults === "true";
            layout = makeBasicPlotLayout(plotData);
            // Per-trace colours (incl. the canonical battery/market/etc. hues and
            // the kW/kWh axis merge) are applied by styleResultPlotB below.
            layout.xaxis.title = null;
            layout.yaxis.title = "kW";
            layout.yaxis2 = {
                tickformat: "s",
                hoverformat: ",",
                title: "kWh",
                overlaying: "y",
                side: "right",
            };
            layout.hovermode = null;
            layout.margin.b = 120;
            layout.margin.r = 80;
            layout.showlegend = true;
            layout.legend = {
                x: 0,
                y: -0.4,
            };
            // Solution-B sits this plot in its own full-width row, so use a fixed
            // height. The original UI keeps its previous behaviour of matching the
            // height to the components/schematic column.
            if (isSolutionB) {
                layout.height = 420;
            } else {
                let componentSchematicColumn = document.getElementById(
                    "component-schematic-column"
                );
                if (componentSchematicColumn) {
                    let columnHeight =
                        componentSchematicColumn.offsetHeight - 135;
                    if (columnHeight < 350) {
                        columnHeight = 350;
                    }
                    layout.height = columnHeight;
                    if (columnHeight < 350) {
                        layout.legend = {
                            x: 0,
                            y: -0.9,
                        };
                    }
                }
            }
            layout.hovermode = "closest";
            layout.barmode = "stack";
            let xValues = ["Peak demand", "Producers", "Market", "Storage"];
            let kWValues = [];
            let kWhValues = [];
            let peakDemandValue = plotData.peak_demand_value
                ? plotData.peak_demand_value
                : 0;
            traces = [
                {
                    x: xValues,
                    y: [peakDemandValue, null, null, null],
                    name: "Peak demand",
                    type: "bar",
                    hovertemplate:
                        "%{fullData.name}<br>%{y:,.0f} [%{yaxis.title.text}]<br><extra></extra>",
                    opacity: 0.9,
                },
            ];
            let components;
            let onlySelected;
            components = Array.from(
                document.getElementsByClassName("asset-form-row")
            );
            if (components.length) {
                onlySelected = true;
            } else {
                components = Array.from(
                    document.getElementsByClassName("min-max-row")
                );
                onlySelected = false;
            }
            for (let _component of components) {
                let selected;
                if (onlySelected) {
                    let selectorEl = document.getElementById(
                        `selected-${_component.dataset.componentId}`
                    );
                    selected = selectorEl.checked;
                } else {
                    selected = true;
                }
                if (selected) {
                    let component = {
                        name: _component.dataset.componentName,
                        type: _component.dataset.componentType,
                        typeLabel: _component.dataset.componentTypeLabel,
                        unitCapacity: _component.dataset.componentUnitCapacity,
                        id: onlySelected
                            ? _component.dataset.componentId
                            : _component.dataset.asset_id,
                    };
                    let useOptimal = isResults && !detailAssetUserEdited;
                    let maxElId;
                    if (useOptimal) {
                        maxElId = `optimal-${component.id}`;
                    } else {
                        maxElId = onlySelected
                            ? `max-${component.id}`
                            : `asset-max-${component.id}`;
                    }
                    let numUnits;
                    if (onlySelected) {
                        numUnits =
                            parseFloat(
                                document.getElementById(maxElId)?.value
                            ) || 0;
                    } else if (useOptimal) {
                        numUnits =
                            parseFloat(
                                document.getElementById(maxElId)?.dataset.value
                            ) || 0;
                    } else {
                        numUnits =
                            parseFloat(
                                (
                                    document.getElementsByName(
                                        maxElId
                                    )[0] as HTMLInputElement
                                )?.value
                            ) || 0;
                    }
                    let unitCapacity;
                    // Check for unit-capacity input on the detail page first
                    let ucInputEl = document.getElementById(
                        `unit-capacity-${component.id}`
                    ) as HTMLInputElement;
                    if (ucInputEl) {
                        unitCapacity = parseFloat(ucInputEl.value) || 0;
                    } else {
                        // Calculate the unit capacity based on the component type and its general settings
                        switch (component.type) {
                            case "basicstorage":
                                let capacityEl = document.getElementById(
                                    `capacity-${component.id}`
                                );
                                if (capacityEl) {
                                    unitCapacity =
                                        parseFloat(capacityEl.value) || 0;
                                } else {
                                    unitCapacity = component.unitCapacity;
                                }
                                break;
                            default:
                                let maxEl = document.getElementById(
                                    `_max-${component.id}`
                                );
                                let maxMechEl = document.getElementById(
                                    `_max_mech-${component.id}`
                                );
                                if (maxMechEl) {
                                    unitCapacity =
                                        parseFloat(maxMechEl.value) || 0;
                                } else if (maxEl) {
                                    unitCapacity = parseFloat(maxEl.value) || 0;
                                } else {
                                    unitCapacity = component.unitCapacity;
                                }
                        }
                    }
                    component.maxCapacity = unitCapacity * numUnits;
                    let trace = {
                        x: xValues,
                        y: [],
                        name: component.typeLabel,
                        type: "bar",
                        hovertemplate:
                            "%{fullData.name}<br>%{y:,.0f} [%{yaxis.title.text}]<br><extra></extra>",
                        yaxis: "y1",
                        opacity: 0.9,
                    };
                    if (component.type === "basicstorage") {
                        kWhValues.push(component.maxCapacity);
                        trace.yaxis = "y2";
                        trace.y = [null, null, null, component.maxCapacity];
                    } else if (component.type === "market") {
                        trace.y = [null, null, component.maxCapacity, null];
                        kWValues.push(component.maxCapacity);
                    } else {
                        trace.y = [null, component.maxCapacity, null, null];
                        kWValues.push(component.maxCapacity);
                    }
                    traces.push(trace);
                }
            }
            let y_max = Math.max(
                peakDemandValue,
                sum(kWValues),
                sum(kWhValues)
            );
            console.log("y max", y_max);
            layout.yaxis.range = [0, y_max];
            if (layout.yaxis2) {
                layout.yaxis2.range = [0, y_max];
            }
            break;
        case "costs":
            layout = makeBasicPlotLayout(plotData);
            if (plotData.for_report) {
                layout.height = 500;
                layout.width = 450;
            } else {
                layout.height = isSolutionB ? 430 : 350;
                layout.margin.b = 120;
                layout.margin.r = 80;
                layout.margin.l = 80;
            }
            traces = makeBarPlotTraces(plotData);
            layout = fixAxisForAllZeroBars(layout, traces);
            break;
        case "production":
            layout = makeBasicPlotLayout(plotData);
            traces = makeBarPlotTraces(plotData);
            layout = fixAxisForAllZeroBars(layout, traces);
            if (plotData.for_report) {
                layout.height = 525;
                layout.width = 1000;
            } else {
                layout.height = isSolutionB ? 430 : 350;
                layout.margin.b = 120;
                layout.margin.r = 80;
                layout.margin.l = 80;
            }
            break;
        default:
            layout = makeBasicPlotLayout(plotData);
            traces = makeScatterPlotTraces(plotData);
    }
    layout.font.color = fontColor;
    if (plotData.for_report) {
        if (layout.yaxis) {
            layout.yaxis.gridcolor = fontColor;
        }
        if (layout.yaxis2) {
            layout.yaxis2.gridcolor = fontColor;
        }
    }
    // Solution-B polish for the non-timeseries result plots (not the printable report).
    if (
        isSolutionB &&
        !plotData.for_report &&
        NON_TS_RESULT_PLOTS.has(plotName)
    ) {
        ({ layout, traces } = styleResultPlotB(plotName, layout, traces));
    }
    return {
        id: plotId,
        div: div,
        traces: traces,
        layout: layout,
        imageLayout: imageLayout,
        prefix: plotId,
        plotData: plotData,
        selectorImageElement: selectorImageElement,
        containerWidth: containerWidth,
        selectorBox: { left: 0, right: 100 },
        slices: slices,
        days: days,
        timeRange: timeRange,
        slider: null,
        startLeft: startLeft,
        endRight: endRight,
        includeSelector: includeSelector,
        timeRangeSelect: timeRangeSelect,
        resolution: plotData.resolution,
        isMultiYear: plotData.is_multi_year,
        selectedRange: null,
        forReport: plotData.for_report,
    };
}

function setTriggers(plotName) {
    let form, field, useGeneratorButton, useInputFileButton;
    switch (plotName) {
        case "load-profile":
            form = document.getElementById(
                "load-profile-plot-form"
            ) as HTMLFormElement;
            field = document.getElementById(
                "id_demand_source"
            ) as HTMLInputElement;
            useGeneratorButton = document.getElementById("use-load-generator");
            useInputFileButton = document.getElementById("use-input-file");
            useGeneratorButton.onclick = function (e) {
                field.value = "load_generator";
            };
            useInputFileButton.onclick = function (e) {
                field.value = "user_input_file";
            };
            break;
        case "modal-load-profile":
            form = document.getElementById(
                "modal-load-profile-plot-form"
            ) as HTMLFormElement;
            field = document.getElementById(
                "id_demand_source"
            ) as HTMLInputElement;
            useGeneratorButton = document.getElementById("use-load-generator");
            useInputFileButton = document.getElementById("use-input-file");
            useGeneratorButton.onclick = function (e) {
                field.value = "load_generator";
            };
            useInputFileButton.onclick = function (e) {
                field.value = "user_input_file";
            };
            break;
        case "peak-demand-vs-installed-capacity":
            let components;
            let onlySelected;
            let isResults =
                document.getElementById("component-schematic-column")?.dataset
                    .hasResults === "true";
            components = Array.from(
                document.getElementsByClassName("asset-form-row")
            );
            if (components.length) {
                onlySelected = true;
            } else {
                components = Array.from(
                    document.getElementsByClassName("min-max-row")
                );
                onlySelected = false;
            }
            for (let component of components) {
                let selectorEl = document.getElementById(
                    `selected-${component.dataset.componentId}`
                );
                if (selectorEl) {
                    selectorEl.addEventListener("change", function (event) {
                        event.preventDefault();
                        let state = getPlotState(plotName);
                        update(state);
                    });
                }
                let maxElId;
                let maxValueEl;
                if (isResults) {
                    maxElId = `optimal-${component.dataset.componentId}`;
                    maxValueEl = document.getElementById(maxElId);
                } else {
                    maxElId = onlySelected
                        ? `max-${component.dataset.componentId}`
                        : `asset-max-${component.dataset.asset_id}`;
                    maxValueEl = onlySelected
                        ? document.getElementById(maxElId)
                        : document.getElementsByName(maxElId)[0];
                    maxValueEl.addEventListener("keyup", function (event) {
                        event.preventDefault();
                        if (
                            (onlySelected &&
                                document.getElementById(
                                    `${event.target.id.replace(
                                        "max-",
                                        "selected-"
                                    )}`
                                ).checked) ||
                            !onlySelected
                        ) {
                            let state = getPlotState(plotName);
                            update(state);
                        }
                    });
                }
                switch (component.dataset.componentType) {
                    case "basicstorage":
                        let capacityEl = document.getElementById(
                            `capacity-${component.dataset.componentId}`
                        );
                        if (capacityEl) {
                            capacityEl.addEventListener(
                                "keyup",
                                function (event) {
                                    event.preventDefault();
                                    if (
                                        document.getElementById(
                                            `${event.target.id.replace(
                                                "capacity-",
                                                "selected-"
                                            )}`
                                        ).checked
                                    ) {
                                        let state = getPlotState(plotName);
                                        update(state);
                                    }
                                }
                            );
                        }
                        break;
                    default:
                        let maxEl = document.getElementById(
                            `_max-${component.dataset.componentId}`
                        );
                        let maxMechEl = document.getElementById(
                            `_max_mech-${component.dataset.componentId}`
                        );
                        if (maxMechEl) {
                            maxMechEl.addEventListener(
                                "keyup",
                                function (event) {
                                    event.preventDefault();
                                    if (
                                        document.getElementById(
                                            `${event.target.id.replace(
                                                "_max_mech-",
                                                "selected-"
                                            )}`
                                        ).checked
                                    ) {
                                        let state = getPlotState(plotName);
                                        update(state);
                                    }
                                }
                            );
                        } else if (maxEl) {
                            maxEl.addEventListener("keyup", function (event) {
                                event.preventDefault();
                                if (
                                    document.getElementById(
                                        `${event.target.id.replace(
                                            "_max-",
                                            "selected-"
                                        )}`
                                    ).checked
                                ) {
                                    let state = getPlotState(plotName);
                                    update(state);
                                }
                            });
                        }
                }
            }
        default:
        // No action required now
    }
}

const updatePlot = (state) =>
    new Promise(function (resolve, reject) {
        renderPlot(state);
        if (state.includeSelector) {
            setUpRangeSelector(state);
        }
        resolve(state);
    });

function update(state) {
    Promise.resolve(state).then(updatePlot);
}

async function makePlotNew(plotName) {
    console.log(`Making plot ${plotName}`);
    let state = await getPlotState(plotName);
    if (state === null) {
        return;
    }
    if (
        state.includeSelector ||
        plotName === "peak-demand-vs-installed-capacity"
    ) {
        setTriggers(plotName);
    }
    update(state);
    if (state.plotData.comment) {
        let commentEl = document.getElementById(`${state.id}-comment`);
        commentEl.innerHTML = `<span class="font-italic">Note </span>— ${state.plotData.comment}`;
        commentEl.classList.remove("d-none");
    }
    enablePlotDownloads(state);
    renderSectionKpis(plotName, state);
}

/* The display name of a plot series (mapped name, falling back to the raw one). */
function seriesDisplayName(s: any, siblings: string[] = []): string {
    return seriesNameIn(s.name, siblings);
}

/* Small abbreviation matching the result tiles (e.g. 50000 -> "50,000"). */
function formatKpiValue(n: number): string {
    return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/*
 * Section KPI tiles derived from a plot's series (Solution-B results tabs):
 *  - revenues: % composition per market stream — a ratio (Σstream / Σtotal), so
 *    the time resolution and multi-year horizon cancel out (safe to compute).
 *  - electricity-generation: peak demand = max of the "Demand" series (a max, so
 *    no summation pitfalls). Matched by series name, not position.
 * Only existing series are used; nothing is summed into an absolute total.
 */
function renderSectionKpis(plotName: string, state: any) {
    if (
        !state ||
        !state.plotData ||
        !Array.isArray(state.plotData.y) ||
        !document.body.classList.contains("solution-b")
    ) {
        return;
    }
    const series = state.plotData.y;
    const palette = [
        "#08acde",
        "#00d29a",
        "#f5a623",
        "#9b59f6",
        "#ff6b6b",
        "#e0c341",
    ];

    if (plotName === "revenues") {
        const el = document.getElementById("economics-revenue-composition");
        if (!el) return;
        const parts = series
            .filter((s: any) => seriesDisplayName(s).toLowerCase() !== "total")
            .map((s: any, i: number) => ({
                name: seriesDisplayName(s),
                // Same canonical colour as the revenues line chart above, so the
                // two always agree; fall back to the categorical palette for any
                // unrecognised stream.
                color:
                    revenueStreamColor(s.name) || palette[i % palette.length],
                sum: (s.values || []).reduce(
                    (a: number, v: any) =>
                        a + (typeof v === "number" && v > 0 ? v : 0),
                    0
                ),
            }))
            .filter((p: any) => p.sum > 0)
            .sort((a: any, b: any) => b.sum - a.sum);
        const total = parts.reduce((a: number, p: any) => a + p.sum, 0);
        if (!total || !parts.length) return;
        const seg = parts
            .map(
                (p: any) =>
                    `<span class="rc-seg" style="width:${
                        (p.sum / total) * 100
                    }%;background:${p.color}" title="${p.name}: ${(
                        (p.sum / total) *
                        100
                    ).toFixed(1)}%"></span>`
            )
            .join("");
        const legend = parts
            .map(
                (p: any) =>
                    `<span class="rc-item"><span class="rc-dot" style="background:${
                        p.color
                    }"></span>${p.name} <b>${((p.sum / total) * 100).toFixed(
                        1
                    )}%</b></span>`
            )
            .join("");
        el.innerHTML = `<div class="rc-title">Revenue composition</div><div class="rc-bar">${seg}</div><div class="rc-legend">${legend}</div>`;
        el.classList.remove("d-none");
    } else if (plotName === "electricity-generation") {
        const strip = document.getElementById("technical-dispatch-kpis");
        if (!strip || strip.querySelector(".skpi-peak-demand")) return;
        const demand = series.find(
            (s: any) => seriesDisplayName(s).toLowerCase() === "demand"
        );
        const vals = (demand && demand.values) || [];
        // Reduce, not Math.max(...vals): the series can hold 100k+ points and a
        // spread of that many args overflows the call stack.
        let peak = -Infinity;
        for (const v of vals) {
            if (typeof v === "number" && v > peak) peak = v;
        }
        if (!isFinite(peak)) return;
        const tile = document.createElement("div");
        tile.className = "skpi skpi-peak-demand";
        tile.innerHTML = `<div class="skpi-lbl">Peak demand</div><div class="skpi-val">${formatKpiValue(
            peak
        )}<span class="skpi-unit">kW</span></div>`;
        strip.appendChild(tile);
    }
}

let initializedPlots = new Set<string>();
// Last container width each plot was laid out at, so we can skip a costly
// Plotly resize on tab switches where the width hasn't actually changed.
let plotRenderedWidths = new Map<string, number>();
// Result charts on not-yet-visible tabs, queued for background rendering
// (Solution-B). They render one-at-a-time after the page is interactive, each
// behind a spinner, so switching to a heavy tab shows a clear loading state
// instead of a surprise freeze. `renderingPlotNow` keeps it strictly sequential
// (Plotly renders synchronously on the main thread).
let pendingPlots = new Set<string>();
let renderingPlotNow = false;
// Progress for the results-loading overlay: total result charts to render and
// how many are done, so the overlay can show a progress bar and reveal the
// page only once every chart is ready.
let plotsTotal = 0;
let plotsDone = 0;

/* Update the results-loading overlay's progress bar; hide it once every result
 * chart has rendered. No-op when the overlay isn't present (no results / not
 * Solution-B). */
function updateResultsLoadingProgress() {
    const overlay = document.getElementById("results-loading-overlay");
    if (!overlay) return;
    const pct =
        plotsTotal > 0 ? Math.round((plotsDone / plotsTotal) * 100) : 100;
    const fill = overlay.querySelector(".rlo-fill") as HTMLElement;
    const count = overlay.querySelector(".rlo-count") as HTMLElement;
    if (fill) fill.style.width = pct + "%";
    if (count) count.textContent = `${plotsDone} / ${plotsTotal} charts`;
    if (plotsDone >= plotsTotal) overlay.classList.add("rlo-hidden");
}

/* Mark one result chart rendered and refresh the overlay. */
function markPlotRendered() {
    plotsDone += 1;
    updateResultsLoadingProgress();
}

async function makePlots() {
    let basicPlots = [
        // Results charts
        "peak-demand-vs-installed-capacity",
        "installed-capacity",
        "initial-investment",
        "costs",
        "annual-cash-flows-per-component",
        "production",
        "revenues",
        "efficiency",
        // Non-results charts
        "component-output",
        "load-profile",
        "modal-load-profile",
        "inputs-timeseries",
        "modal-inputs-timeseries",
    ];
    let timeseriesPlots = [
        "electricity-generation",
        "heat1-generation",
        "heat2-generation",
        "stacked-annual-generation",
        "additional-timeseries",
    ];
    // Get the names of all plots on the page
    let plotDivs = Array.from(document.querySelectorAll("[id$='-plot']"));
    let plotNames = plotDivs.map((plotDiv) => plotDiv.id.replace("-plot", ""));

    // Build ordered list of plots to render, preserving existing ordering constraints
    let plotNamesToRender: string[] = [];
    for (let plotName of plotNames) {
        if (basicPlots.includes(plotName)) {
            plotNamesToRender.push(plotName);
        }
    }
    // The time-series charts are rendered together with the multi-series one on
    // the results page. The report page carries neither that chart nor the tab
    // it lives in, so it says outright that its time series are to be drawn --
    // without it the representative week is a chart container nothing ever
    // draws into.
    if (
        plotNames.includes("additional-timeseries") ||
        document.body.classList.contains("report-b")
    ) {
        for (let plotName of plotNames) {
            if (timeseriesPlots.includes(plotName)) {
                plotNamesToRender.push(plotName);
            }
        }
    }

    // Count the result charts up front so the loading overlay's progress bar
    // has a stable denominator, and start it at 0.
    const overlay = document.getElementById("results-loading-overlay");
    if (overlay) {
        plotsTotal = plotNamesToRender.filter((n) =>
            document.getElementById(`${n}-plot-container`)
        ).length;
        plotsDone = 0;
        updateResultsLoadingProgress();
        // Safety net: never leave the overlay stuck if a render hangs/errors.
        setTimeout(() => overlay.classList.add("rlo-hidden"), 30000);
    }

    // Phase 1: Render visible plots immediately
    for (let plotName of plotNamesToRender) {
        let container = document.getElementById(`${plotName}-plot-container`);
        if (container && container.offsetParent !== null) {
            // One chart that fails must not take the rest of the page with it.
            // Without this, a single failed request threw out of the loop:
            // every chart after it stayed empty and the loading overlay sat
            // there counting a total it could never reach, which read as the
            // charts having been removed.
            try {
                await makePlotNew(plotName);
                initializedPlots.add(plotName);
                let el = document.getElementById(`${plotName}-plot`);
                if (el) plotRenderedWidths.set(plotName, el.clientWidth);
            } catch (err) {
                console.log("Plot render failed", plotName, err);
            }
            markPlotRendered();
        }
    }

    // Phase 2 (Solution-B): queue the remaining (hidden-tab) result charts for
    // background rendering, so heavy tabs are ready by the time the overlay
    // lifts (and switching is instant afterwards).
    if (document.body.classList.contains("solution-b")) {
        for (let plotName of plotNamesToRender) {
            if (initializedPlots.has(plotName)) continue;
            if (!document.getElementById(`${plotName}-plot-container`))
                continue;
            pendingPlots.add(plotName);
            setPlotLoading(plotName, true);
        }
        if (pendingPlots.size > 0) {
            // Let Phase 1 paint first, then start the background worker.
            setTimeout(renderNextPendingPlot, 200);
        } else {
            updateResultsLoadingProgress(); // nothing pending → lift overlay
        }
    }
}

/* Toggle the loading spinner on a plot's container. */
function setPlotLoading(plotName: string, on: boolean) {
    const container = document.getElementById(`${plotName}-plot-container`);
    if (container) container.classList.toggle("plot-loading", on);
}

/* Next plot to background-render: prefer one on the currently visible tab so
 * switching to a heavy tab renders its charts next. */
function pickNextPendingPlot(): string | null {
    let anyName: string | null = null;
    for (const name of pendingPlots) {
        const container = document.getElementById(`${name}-plot-container`);
        if (!container) continue;
        if (anyName === null) anyName = name;
        if (container.offsetParent !== null) return name; // visible → first
    }
    return anyName;
}

/* Background render worker: renders one pending plot, then schedules the next.
 * Strictly sequential, with a beat between renders so tab clicks and repaints
 * are handled between the (synchronous, main-thread) Plotly renders. */
async function renderNextPendingPlot() {
    if (renderingPlotNow) return;
    const name = pickNextPendingPlot();
    if (!name) return;
    renderingPlotNow = true;
    pendingPlots.delete(name);
    try {
        if (!initializedPlots.has(name)) {
            await makePlotNew(name);
            initializedPlots.add(name);
            // The plot is hidden, so its element width is 0; record the width it
            // was actually laid out at (the results content width) so the first
            // tab-show finds the width unchanged and skips a costly resize of a
            // full-resolution chart.
            const el = document.getElementById(`${name}-plot`);
            if (el) {
                let w = el.clientWidth;
                if (w === 0) {
                    const rc = document.querySelector(
                        "#project-detail-card .tab-content"
                    ) as HTMLElement | null;
                    if (rc && rc.clientWidth > 0) w = rc.clientWidth;
                }
                plotRenderedWidths.set(name, w);
            }
        }
    } catch (err) {
        console.log("Background plot render failed", name, err);
    }
    setPlotLoading(name, false);
    markPlotRendered();
    renderingPlotNow = false;
    if (pendingPlots.size > 0) {
        setTimeout(renderNextPendingPlot, 60);
    }
}

async function loadGeneratorPlot() {
    let state: State;

    async function getInitialState() {
        // Get the initial state
        let plotId = "load-generator-plot";
        let div = getPlotDiv(plotId);
        let scalingSettings = JSON.parse(
            document.getElementById("load-generator-scaling-data").textContent
        );

        var scaling = scalingSettings;
        var projectId = JSON.parse(
            document.getElementById("project_id").textContent
        );

        var initialState = {
            id: plotId,
            div: div,
            traces: [],
            layout: null,
            isScaled: scaling.scaled,
            profileIndex: null,
            profileName: scaling.profile_name,
            validationFields: [
                "min",
                "max",
                "mean",
                "daily_variation",
                "hourly_variation",
            ],
            errors: {},
            load_profile: scaling.load_profile,
            min: scaling.min || 100,
            max: scaling.max || 2000,
            mean: scaling.mean || 1000,
            daily_variation: scaling.daily_variation || 10,
            hourly_variation: scaling.hourly_variation || 5,
            download_solar: scaling.download_solar || false,
            download_wind: scaling.download_wind || false,
            projectId: projectId,
            imageLayout: null,
            selectorBox: { left: 0, right: 100 },
            plotData: null,
            slices: null,
            days: null,
            timeRange: null,
            selectorImageElement: document.getElementById(
                `${plotId}-selector-image`
            ),
            containerWidth: document.getElementById(`${plotId}-container`)
                .clientWidth,
            slider: null,
            startLeft: null,
            endRight: null,
            prefix: plotId,
            resolution: null,
            isMultiYear: null,
            selectedRange: null,
            includeSelector: true,
        };

        // Use getTrace to fetch the initial plot data
        let stateWithTrace = await getTrace(initialState);

        let plotData = stateWithTrace.plotData;
        let layout = makeBasicPlotLayout(plotData);
        let plotPanel = document.getElementById("plot-panel");
        if (plotPanel) {
            layout.width = plotPanel.clientWidth - 10;
        }
        let imageLayout = makeSelectorPlotLayout(layout);
        let slices = plotData.x.length;
        let days = slices / 24;
        let timeRange = 7;
        if (plotData.resolution === "fifteen_minutes") {
            timeRange = timeRange * 4;
        }
        var startLeft = slices - timeRange * 24;
        startLeft = startLeft - (startLeft % 24);
        let endRight = slices - 1;
        layout.xaxis.range = [plotData.x[startLeft], plotData.x[endRight]];
        layout.title = null;

        return {
            ...stateWithTrace,
            layout: layout,
            imageLayout: imageLayout,
            slices: slices,
            days: days,
            timeRange: timeRange,
            startLeft: startLeft,
            endRight: endRight,
            resolution: plotData.resolution,
            isMultiYear: plotData.is_multi_year,
        };
    }

    function setupSliders(state) {
        // Hourly variation slider //
        /////////////////////////////
        var hourlyVariationInput = document.getElementById(
            "id_hourly_variation"
        ) as HTMLInputElement;

        var hourlyVariationSliderValue = document.getElementById(
            "hourly-variation-slider-value"
        ) as HTMLInputElement;
        hourlyVariationSliderValue.innerHTML = hourlyVariationSliderValue.value;

        var hourlyVariationSlider = document.getElementById(
            "hourly-variation-slider"
        ) as noUiSlider.Instance;
        noUiSlider.create(hourlyVariationSlider, {
            start: [parseFloat(hourlyVariationInput.value)],
            range: {
                min: 0,
                max: 100,
            },
            connect: [true, false],
            step: 1,
            tooltips: true,
            format: {
                // 'to' the formatted value. Receives a number and returns a string.
                to: function (value) {
                    return value + "%";
                },
                // 'from' the formatted value.
                // Receives a string and returns a number.
                from: function (value) {
                    return parseInt(value);
                },
            },
        });

        var hourlyVariationSliderValue = document.getElementById(
            "hourly-variation-slider-value"
        ) as HTMLInputElement;

        hourlyVariationSlider.noUiSlider.on(
            "update",
            function (values, handle) {
                hourlyVariationSliderValue.innerHTML = values[handle];
            }
        );

        var hourlyVariationInput = document.getElementById(
            "id_hourly_variation"
        ) as HTMLInputElement;

        hourlyVariationSlider.noUiSlider.on(
            "update",
            function (values, handle) {
                hourlyVariationInput.value = values[handle].slice(0, -1);
                triggerEvent(hourlyVariationInput, "keyup");
            }
        );

        // Daily variation slider //
        ////////////////////////////

        var dailyVariationInput = document.getElementById(
            "id_daily_variation"
        ) as HTMLInputElement;

        var dailyVariationSliderValue = document.getElementById(
            "daily-variation-slider-value"
        ) as HTMLInputElement;
        dailyVariationSliderValue.innerHTML = dailyVariationSliderValue.value;

        var dailyVariationSlider = document.getElementById(
            "daily-variation-slider"
        ) as noUiSlider.Instance;
        noUiSlider.create(dailyVariationSlider, {
            start: [parseFloat(dailyVariationInput.value)],
            range: {
                min: 0,
                max: 100,
            },
            connect: [true, false],
            step: 1,
            tooltips: true,
            format: {
                // 'to' the formatted value. Receives a number and returns a string.
                to: function (value) {
                    return value + "%";
                },
                // 'from' the formatted value.
                // Receives a string and returns a number.
                from: function (value) {
                    return parseInt(value);
                },
            },
        });

        dailyVariationSlider.noUiSlider.on("update", function (values, handle) {
            dailyVariationSliderValue.innerHTML = values[handle];
        });

        var dailyVariationInput = document.getElementById(
            "id_daily_variation"
        ) as HTMLInputElement;

        dailyVariationSlider.noUiSlider.on("update", function (values, handle) {
            dailyVariationInput.value = values[handle].slice(0, -1);
            triggerEvent(dailyVariationInput, "keyup");
        });

        state.hourlyVariationSlider = hourlyVariationSlider;
        state.dailyVariationSlider = dailyVariationSlider;

        if (!state.isScaled) {
            state.dailyVariationSlider.setAttribute("disabled", true);
            state.hourlyVariationSlider.setAttribute("disabled", true);
            Array.from(document.getElementsByClassName("noUi-base")).forEach(
                (sliderLine) => {
                    sliderLine.classList.add("slider-line-disabled");
                }
            );
            Array.from(document.getElementsByClassName("noUi-connect")).forEach(
                (sliderLine) => {
                    sliderLine.classList.add("slider-line-disabled");
                }
            );
        }
    }

    async function toggleUI(state, loadProfileSelect) {
        state.profileIndex = loadProfileSelect.selectedIndex;
        state.profileName =
            loadProfileSelect.options[
                loadProfileSelect.selectedIndex
            ].innerHTML;
        state.load_profile = loadProfileSelect.value;
        if (!state.isScaled) {
            [
                "id_min",
                "id_max",
                "id_mean",
                "id_daily_variation",
                "id_hourly_variation",
            ].forEach((fieldId) => {
                let el = document.getElementById(fieldId) as HTMLInputElement;
                el.disabled = true;
            });
            state.dailyVariationSlider.setAttribute("disabled", true);
            state.hourlyVariationSlider.setAttribute("disabled", true);
            Array.from(document.getElementsByClassName("noUi-base")).forEach(
                (sliderLine) => {
                    sliderLine.classList.add("slider-line-disabled");
                }
            );
            Array.from(document.getElementsByClassName("noUi-connect")).forEach(
                (sliderLine) => {
                    sliderLine.classList.add("slider-line-disabled");
                }
            );
        }
        // }
        await update(state);
    }

    function setupTriggers(state) {
        const debouncedUpdate = debounce(() => update(state), 300);

        document.getElementById("id_load_profile").onchange =
            async function () {
                let loadProfileSelect = this as HTMLSelectElement;
                await toggleUI(state, loadProfileSelect);
            };

        document.getElementById("reset-button").onclick = async function (e) {
            console.log("Reset button clicked");
            e.preventDefault();
            state = await reset(state);
        };

        document.getElementById("id_scaled").onchange = function () {
            let el = this as HTMLInputElement;
            state.isScaled = el.checked;
            if (state.isScaled) {
                [
                    "id_min",
                    "id_max",
                    "id_mean",
                    "id_daily_variation",
                    "id_hourly_variation",
                ].forEach((fieldId) => {
                    let el = document.getElementById(
                        fieldId
                    ) as HTMLInputElement;
                    el.disabled = false;
                });
                state.dailyVariationSlider.removeAttribute("disabled");
                state.hourlyVariationSlider.removeAttribute("disabled");
                Array.from(
                    document.getElementsByClassName("noUi-base")
                ).forEach((sliderLine) => {
                    sliderLine.classList.remove("slider-line-disabled");
                });
                Array.from(
                    document.getElementsByClassName("noUi-connect")
                ).forEach((sliderLine) => {
                    sliderLine.classList.remove("slider-line-disabled");
                });
            } else {
                [
                    "id_min",
                    "id_max",
                    "id_mean",
                    "id_daily_variation",
                    "id_hourly_variation",
                ].forEach((fieldId) => {
                    let el = document.getElementById(
                        fieldId
                    ) as HTMLInputElement;
                    el.disabled = true;
                });
                state.dailyVariationSlider.setAttribute("disabled", true);
                state.hourlyVariationSlider.setAttribute("disabled", true);
                Array.from(
                    document.getElementsByClassName("noUi-base")
                ).forEach((sliderLine) => {
                    sliderLine.classList.add("slider-line-disabled");
                });
                Array.from(
                    document.getElementsByClassName("noUi-connect")
                ).forEach((sliderLine) => {
                    sliderLine.classList.add("slider-line-disabled");
                });
            }
            debouncedUpdate();
        };

        [
            "id_min",
            "id_max",
            "id_mean",
            "id_daily_variation",
            "id_hourly_variation",
        ].forEach((fieldId) => {
            let el = document.getElementById(fieldId) as HTMLInputElement;
            el.onkeyup = function () {
                let fieldName = fieldId.slice(3);
                state[fieldName] = parseFloat(el.value);
                debouncedUpdate();
            };
        });
    }

    async function init() {
        state = await getInitialState();
        renderPlot(state);
        setupSliders(state);
        setupTriggers(state);
        setUpRangeSelector(state);
        enablePlotDownloads(state);

        // Some housekeeping...
        Array.from(document.getElementsByClassName("errorlist")).forEach(
            (el) => {
                el.remove();
            }
        );

        return state;
    }

    async function reset(state) {
        console.log("Resetting parameters to original values...");

        // Preserve slider references before merging, as getInitialState does not set them
        let dailyVariationSlider = state.dailyVariationSlider;
        let hourlyVariationSlider = state.hourlyVariationSlider;

        // Retain elements that have been added to the state while restoring the
        // original values.
        let initialState = await getInitialState();
        state = Object.assign(state, initialState);

        // Restore slider references after merge
        state.dailyVariationSlider = dailyVariationSlider;
        state.hourlyVariationSlider = hourlyVariationSlider;

        var selectedProfile = document.getElementById(
            "id_load_profile"
        ) as HTMLSelectElement;
        selectedProfile.value = state.load_profile;
        [
            "id_min",
            "id_max",
            "id_mean",
            "id_daily_variation",
            "id_hourly_variation",
        ].forEach((fieldId) => {
            var el = document.getElementById(fieldId) as HTMLInputElement;
            var fieldName = fieldId.slice(3);
            el.value = state[fieldName];
        });

        ["id_download_solar", "id_download_wind"].forEach((fieldId) => {
            var el = document.getElementById(fieldId) as HTMLInputElement;
            var fieldName = fieldId.slice(3);
            el.checked = state[fieldName];
        });

        // Reset slider positions to match the restored state values
        state.dailyVariationSlider.noUiSlider.set(state.daily_variation);
        state.hourlyVariationSlider.noUiSlider.set(state.hourly_variation);

        toggleUI(state, selectedProfile);
        return state;
    }

    async function update(state) {
        state = await validateFields(state);
        state = await displayErrors(state);
        state = await getTrace(state);
        state = await updatePlot(state);
    }

    // Update functions //
    //////////////////////

    async function validateFields(state) {
        interface Errors {
            [x: string]: any;
        }
        var errors: Errors = {};
        if (state.isScaled) {
            state.validationFields.forEach((field) => {
                if (
                    ["min", "max", "mean"].includes(field) &&
                    !(state[field] > 0)
                ) {
                    var error_msg = "Must be a positive number";
                    errors[field] = error_msg;
                }
                if (
                    ["daily_variation", "hourly_variation"].includes(field) &&
                    !(state[field] >= 0)
                ) {
                    var error_msg = "Must be a greater than or equal to zero";
                    errors[field] = error_msg;
                }
                if (field === "mean" && state.min >= state.mean) {
                    var error_msg =
                        "Average load must be greater than base load and less than peak load";
                    errors.mean = error_msg;
                }
                if (field == "max") {
                    if (state.min >= state.max) {
                        var error_msg =
                            "Peak load must be greater than base and average loads";
                        errors.max = error_msg;
                    }
                    if (state.mean >= state.max) {
                        var error_msg =
                            "Peak load must be greater than base and average loads";
                        errors.max = error_msg;
                    }
                }
            });
        }
        state.errors = errors;
        return state;
    }

    async function displayErrors(state) {
        state.validationFields.forEach((field) => {
            var el = document.getElementById("id_" + field);
            var invalid_feedback = document.getElementById(field + "-errors");
            if (invalid_feedback) {
                invalid_feedback.remove();
            }
            var error_msg = state.errors[field];
            if (typeof error_msg === "string") {
                invalid_feedback = document.createElement("DIV");
                invalid_feedback.id = field + "-errors";
                invalid_feedback.classList.add("invalid-feedback");
                invalid_feedback.innerHTML = error_msg;
                el.classList.add("is-invalid");
                el.parentElement.appendChild(invalid_feedback);
            } else {
                el.classList.remove("is-invalid");
            }
        });
        return state;
    }

    async function getTrace(state) {
        if (Object.entries(state.errors).length) {
            return state;
        }

        try {
            let baseUrl = "/api/v1/inputs";
            let parameters = new URLSearchParams({
                project_id: state.projectId,
                load_profile_id: state.load_profile,
                scaled: state.isScaled,
                load_min: state.min,
                load_max: state.max,
                load_mean: state.mean,
                daily_variation: state.daily_variation,
                hourly_variation: state.hourly_variation,
                profile_name: state.profileName,
            });
            let url = `${baseUrl}?${parameters}`;
            let response = await fetchWithNoCache(url);
            let result = await response.json();
            state.plotData = result;
            state.traces = makeScatterPlotTraces(result);
            return state;
        } catch (error) {
            console.log("Unable to get updated chart data");
            console.log(error);
            return state;
        }
    }

    async function updatePlot(state) {
        renderPlot(state);
        if (state.includeSelector) {
            setUpRangeSelector(state);
        }
        return state;
    }

    // Helpers //
    /////////////

    function resetParams(state) {
        [
            "id_min",
            "id_max",
            "id_mean",
            "id_daily_variation",
            "id_hourly_variation",
        ].forEach((fieldId) => {
            // var el = document.getElementById(fieldId) as HTMLInputElement;
            // var fieldName = fieldId.slice(3);
            // el.value = "";
            // state[fieldName] = parseFloat(el.value);
        });
    }

    init();
}

/*
 * Automatically disable any upload button if a file has been de-selected.
 */
function disableUploadButtonsIfNoFileIsSelected() {
    $(".fileinput")
        .closest("form")
        .find(":file")
        .on("change", function () {
            let fileInput = $(this);
            let submitButton = fileInput.closest("form").find(":submit");
            if (fileInput.attr("name")) {
                submitButton.prop("disabled", false);
            } else {
                submitButton.prop("disabled", true);
            }
        });
}

/**
 * Toggles visibility of field groups.
 *
 * Plots that were intially rendered in hidden divs must be resized; otherwise, they will be too small.
 */
function enableToggleGroups() {
    $(".group-collapse").on("show.bs.collapse", function () {
        $(this).siblings(".group-heading").addClass("active");
        $(this)
            .find("*[id$='-plot']")
            .each(function (i, el) {
                Plotly.Plots.resize(el);
            });
        $(this)
            .find("*[id$='-map']")
            .each(function (i, el) {
                setTimeout(function () {
                    state.maps[el.id].invalidateSize();
                }, 10);
            });
    });

    $(".group-collapse").on("hide.bs.collapse", function () {
        $(this).siblings(".group-heading").removeClass("active");
    });
}

/**
 * Applies a "count up" animation to all p elements whose id's end with "countup-value".
 */
function enableCountUp() {
    const abbreviate = document.body.classList.contains("solution-b");
    $("span[id$='countup-value']").each(function (i, el) {
        let options: CountUpOptions = {
            separator: ",",
            decimal: ".",
            decimalPlaces: 0,
        };
        let _value = el.dataset.value;
        if (_value && _value !== "n/a") {
            let parts = _value.split(".");
            let value =
                parts.length === 2 ? parseFloat(_value) : parseInt(_value);
            options.decimalPlaces = parts.length === 2 ? parts[1].length : 0;
            // Solution B abbreviates large headline figures (e.g. "-327.3 M");
            // the exact value stays in the tile's title tooltip. Mirrors the
            // Django abbreviate_number filter — keep both in sync.
            if (abbreviate && Math.abs(value) >= 1000) {
                options.formattingFn = abbreviateNumber;
            }
            new CountUp(el.id, value, options).start();
        }
    });
}

// Holds the Leaflet location map so its size can be recomputed when its tab
// becomes visible. Leaflet renders blank/partial if initialised while its
// container is hidden (the project Settings tab is display:none at page load),
// so we invalidateSize() it on shown.bs.tab. Shared by both UIs on purpose —
// the original app has the same bug.
let projectLocationMap: any = null;

/**
 * Add a map showing the project's location; if the map is part of a form,
 * the coordinates are derived from the location fields and the map reacts
 * to changes in those fields.
 */
function makeProjectLocationMap() {
    let mapDiv = document.getElementById("project-location-map");
    if (!mapDiv) {
        return;
    }
    let isReactive = mapDiv.classList.contains("reactive-map");
    let isReportMap = mapDiv.classList.contains("report-map");
    let coordinates: Array<number>;
    function parseFloatOrZero(coord) {
        let elementId = `id_${coord}`;
        let element = document.getElementById(elementId);
        if (!element) return 0;
        let value = element.value;
        let parsedFloat = parseFloat(value);
        let isValid = true;
        if (Number.isNaN(parsedFloat)) {
            isValid = false;
        } else if (coord == "latitude") {
            if (parsedFloat > 90 || parsedFloat < -90) {
                isValid = false;
            }
        } else if (coord == "longitude") {
            if (parsedFloat > 180 || parsedFloat < -180) {
                isValid = false;
            }
        } else {
            isValid = false;
        }
        if (isValid) {
            element.parentElement.classList.remove("has-danger");
        } else {
            element.parentElement.classList.add("has-danger");
        }
        return parsedFloat;
    }
    if (isReactive) {
        if (
            !document.getElementById("id_latitude") ||
            !document.getElementById("id_longitude")
        ) {
            return;
        }
        let latitude = parseFloatOrZero("latitude");
        let longitude = parseFloatOrZero("longitude");
        coordinates = [latitude, longitude];
        for (let field of ["latitude", "longitude"]) {
            const el = document.getElementById(`id_${field}`);
            if (el) {
                el.onkeyup = function () {
                    updateMap();
                };
                el.onchange = function () {
                    updateMap();
                };
            }
        }
    } else {
        try {
            coordinates = JSON.parse(
                document.getElementById("project-location-coordinates")
                    .textContent
            );
        } catch {
            coordinates = [52.520008, 13.404954];
        }
    }
    let map = L.map(mapDiv, { zoomControl: !isReportMap }).setView(
        coordinates,
        13
    );
    projectLocationMap = map;
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "© OpenStreetMap",
        // OpenStreetMap's servers answer 403 "Referer is required by the usage
        // policy" when a tile request carries no Referer. Django's
        // SECURE_REFERRER_POLICY defaults to "same-origin", which strips it on
        // every cross-origin request, so every map in the application went
        // blank. Set the policy on the tile images alone rather than loosening
        // the site-wide header: the browser then sends our origin — and only
        // the origin, never the path — to the tile server.
        referrerPolicy: "strict-origin-when-cross-origin",
    }).addTo(map);
    // The picker is Solution-B only, and only where the map already follows the
    // form; the read-only maps on the detail and report pages stay read-only.
    let isPickable =
        isReactive && document.body.classList.contains("solution-b");
    let marker = L.marker(coordinates, { draggable: isPickable }).addTo(map);
    function updateMap() {
        let isValid = true;
        let latitude = parseFloatOrZero("latitude");
        let longitude = parseFloatOrZero("longitude");
        if (Math.abs(latitude) > 90) {
            isValid = false;
        }
        if (Math.abs(longitude) > 180) {
            isValid = false;
        }
        if (isValid) {
            coordinates = [latitude, longitude];
            try {
                map.panTo(coordinates);
                marker.setLatLng(coordinates);
            } catch {
                console.log("invalid coordinates");
            }
        }
    }
    if (isReactive) {
        // Undo on the create pages is a <button type="reset">, and a native
        // form reset restores the coordinate fields without firing input or
        // change — the events everything above listens for. Without this the
        // fields would go back while the map kept showing the discarded
        // location, which reads as Undo having done nothing. The reset itself
        // happens after this event, hence the deferral. Nothing changes for the
        // original interface: its forms have no reset button at all.
        const locationForm = document.getElementById("id_latitude")?.form;
        if (locationForm) {
            locationForm.addEventListener("reset", function () {
                setTimeout(updateMap, 0);
            });
        }
    }
    if (isPickable) {
        map.on("click", function (event: any) {
            setProjectLocation(event.latlng.lat, event.latlng.lng);
        });
        marker.on("dragend", function () {
            const position = marker.getLatLng();
            setProjectLocation(position.lat, position.lng);
        });
    }
}

/**
 * Write a coordinate into one of the Location fields as if it had been typed.
 *
 * Three mechanisms listen for these events: the map recentring above, the
 * settings form's dirty detection that enables Save and Undo, and the
 * unsaved-changes guard on Calculate. Assigning `.value` alone would leave a
 * changed location that the form does not consider changed, which can be lost
 * or saved without the user realising the resource data and the results are
 * now stale.
 *
 * The field is briefly highlighted, because a value the user also edits by hand
 * must not appear to change on its own.
 */
function setLocationFieldValue(fieldId: string, value: number | string) {
    const field = document.getElementById(fieldId) as HTMLInputElement | null;
    if (!field) return;
    const text = String(value);
    if (field.value === text) return;
    field.value = text;
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
    field.classList.remove("field-just-changed");
    void field.offsetWidth; // restart the animation if it is already running
    field.classList.add("field-just-changed");
    field.addEventListener(
        "animationend",
        function () {
            field.classList.remove("field-just-changed");
        },
        { once: true }
    );
}

/**
 * Set the project's coordinates from the map or the address search, and with
 * them the time zone offset.
 *
 * The offset is a suggestion the user may overrule: it is only rewritten when
 * the location itself changes — a deliberate action, and a visible one — and it
 * is left alone when the lookup cannot determine a zone. Pass `timeZoneOffset`
 * when it is already known, as it is for a search result, to avoid asking twice.
 */
function setProjectLocation(
    latitude: number,
    longitude: number,
    timeZoneOffset?: number | null
) {
    const roundedLatitude = Math.round(latitude * 1e6) / 1e6;
    const roundedLongitude = Math.round(longitude * 1e6) / 1e6;
    if (Math.abs(roundedLatitude) > 90 || Math.abs(roundedLongitude) > 180) {
        return;
    }
    setLocationFieldValue("id_latitude", roundedLatitude);
    setLocationFieldValue("id_longitude", roundedLongitude);

    // A whole-hour offset is written as "1.0" rather than "1", matching how the
    // field is rendered from the stored value.
    function setOffset(offset: number) {
        const field = document.getElementById(
            "id_time_zone_offset"
        ) as HTMLInputElement | null;
        if (field && Number.isInteger(offset)) {
            setLocationFieldValue("id_time_zone_offset", offset.toFixed(1));
        } else {
            setLocationFieldValue("id_time_zone_offset", offset);
        }
    }

    if (timeZoneOffset !== undefined) {
        if (timeZoneOffset !== null) {
            setOffset(timeZoneOffset);
        }
        return;
    }
    fetch(
        `/api/v1/timezone-offset?lat=${roundedLatitude}&lon=${roundedLongitude}`
    )
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
            if (data && data.time_zone_offset !== null) {
                setOffset(data.time_zone_offset);
            }
        })
        .catch(() => {
            // The offset stays as it is; the coordinates are already set.
        });
}

/**
 * The address search over the project map: a place name in, coordinates out.
 *
 * Searching happens on Enter or on the button, never per keystroke — the free
 * geocoding service forbids being queried at autocomplete rate. A single
 * unambiguous result is applied directly; several are offered as a list.
 */
function initAddressFinder() {
    if (!document.body.classList.contains("solution-b")) return;
    const input = document.getElementById(
        "address-search-input"
    ) as HTMLInputElement | null;
    const button = document.getElementById("address-search-button");
    const results = document.getElementById("address-search-results");
    const message = document.getElementById("address-search-message");
    if (!input || !button || !results || !message) return;

    function showMessage(text: string) {
        message.textContent = text;
        message.hidden = !text;
    }

    function clearResults() {
        results.textContent = "";
        results.hidden = true;
    }

    function applyCandidate(candidate: any) {
        setProjectLocation(
            candidate.latitude,
            candidate.longitude,
            candidate.time_zone_offset
        );
        if (projectLocationMap) {
            projectLocationMap.setView(
                [candidate.latitude, candidate.longitude],
                13
            );
        }
        clearResults();
        showMessage("");
    }

    function search() {
        const query = input.value.trim();
        clearResults();
        if (!query) {
            showMessage("");
            return;
        }
        showMessage("Searching…");
        fetch(`/api/v1/geocode?q=${encodeURIComponent(query)}`)
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                if (!data || data.error) {
                    showMessage(
                        "The address search is unavailable. The coordinates can still be entered by hand."
                    );
                    return;
                }
                const candidates = data.results ?? [];
                if (!candidates.length) {
                    showMessage("No place found for this search.");
                    return;
                }
                showMessage("");
                if (candidates.length === 1) {
                    applyCandidate(candidates[0]);
                    return;
                }
                candidates.forEach(function (candidate: any) {
                    const entry = document.createElement("button");
                    entry.type = "button";
                    entry.className = "address-search-result";
                    entry.textContent = candidate.label;
                    entry.onclick = function () {
                        applyCandidate(candidate);
                    };
                    results.appendChild(entry);
                });
                results.hidden = false;
            })
            .catch(function () {
                showMessage(
                    "The address search is unavailable. The coordinates can still be entered by hand."
                );
            });
    }

    button.onclick = search;
    input.onkeydown = function (event: KeyboardEvent) {
        if (event.key === "Enter") {
            // The search field sits inside the settings form; Enter must search
            // rather than submit the whole project.
            event.preventDefault();
            search();
        } else if (event.key === "Escape") {
            clearResults();
            showMessage("");
        }
    };
}

// Results "scroll" layout (Economics / Technical): the Tables/Charts selector
// scrolls to a section — both live on one page — and highlights the section in
// view, instead of switching Bootstrap tab panes. Solution-B only.
// Energy-bus layout (Summary tab): the bus node sits in the FIRST row (the
// vertical centre of the topmost cards), so the top-left component and the
// market connect straight into it; the line runs from the node centre down to
// the centre of the bottommost card, with the end dot there.
function positionEnergyBus() {
    if (!document.body.classList.contains("solution-b")) return;
    document
        .querySelectorAll<HTMLElement>(".component-bus")
        .forEach(function (bus) {
            const mid = bus.querySelector(".bus-mid") as HTMLElement;
            const line = bus.querySelector(".bus-line") as HTMLElement;
            const node = bus.querySelector(".bus-node") as HTMLElement;
            if (!mid || !line) return;
            const cards = Array.from(
                bus.querySelectorAll(".comp-card")
            ) as HTMLElement[];
            const midRect = mid.getBoundingClientRect();
            if (!cards.length || !midRect.height) {
                line.style.display = "none";
                return;
            }
            line.style.display = "";
            let topC = Infinity;
            let botC = -Infinity;
            cards.forEach(function (c) {
                const r = c.getBoundingClientRect();
                const center = r.top + r.height / 2;
                if (center < topC) topC = center;
                if (center > botC) botC = center;
            });
            const topInset = Math.max(0, topC - midRect.top);
            const botInset = Math.max(0, midRect.bottom - botC);
            line.style.top = topInset + "px";
            line.style.bottom = botInset + "px";
            line.style.opacity = "1"; // reveal once correctly placed
            if (node) {
                node.style.top = topInset + "px"; // node in the first row
                node.style.opacity = "1";
            }
        });
}

// Sticky section selector for the one-page Economics/Technical results tabs:
// clicking a pill smooth-scrolls to its section and a scroll-spy highlights the
// section under the selector. Solution-B only.
function initResultsScrollNav() {
    if (!document.body.classList.contains("solution-b")) return;
    // Each nav contributes its updateActive(); a single shown.bs.tab handler
    // (bound once, below) resyncs them all on a top-level tab switch, instead of
    // binding one document listener per nav.
    const navUpdaters: Array<() => void> = [];
    Array.from(document.querySelectorAll(".results-scroll-nav")).forEach(
        function (nav) {
            const links = Array.from(
                nav.querySelectorAll("a[href^='#']")
            ) as HTMLAnchorElement[];
            const sections = links
                .map(function (a) {
                    const href = a.getAttribute("href");
                    return href ? document.querySelector(href) : null;
                })
                .filter(Boolean) as HTMLElement[];
            if (!sections.length) return;
            // Click a pill → smooth-scroll to its section.
            links.forEach(function (a) {
                a.addEventListener("click", function (e) {
                    e.preventDefault();
                    const href = a.getAttribute("href");
                    const target = href ? document.querySelector(href) : null;
                    if (target) {
                        (target as HTMLElement).scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                    }
                });
            });
            // Scroll-spy: the active pill is the section currently under the
            // selector — the LAST section whose top has passed the trigger line
            // (just below the sticky selector). Picking "last past the line"
            // (rather than the first one touching a band) avoids highlighting
            // the section just above the one you scrolled to.
            const wrapper = nav.closest(".results-scroll-nav-wrapper");
            function setActive(id) {
                links.forEach(function (a) {
                    a.classList.toggle(
                        "active",
                        a.getAttribute("href") === "#" + id
                    );
                });
            }
            // This nav lives in a top-level tab pane; while that pane is hidden
            // its sections measure as 0×0, which would wrongly resolve to the
            // last section. Skip those — and resync on tab show (below).
            function navVisible() {
                return sections[0].offsetParent !== null;
            }
            function updateActive() {
                if (!navVisible()) return;
                const triggerY =
                    (wrapper ? wrapper.getBoundingClientRect().bottom : 0) + 16;
                let id = sections[0].id;
                sections.forEach(function (s) {
                    if (s.getBoundingClientRect().top - 4 <= triggerY) {
                        id = s.id;
                    }
                });
                // A short last section (e.g. "Tables") may never reach the
                // trigger line because the page can't scroll far enough; once
                // we're at the bottom it IS the active one.
                const scroller =
                    document.scrollingElement || document.documentElement;
                if (
                    scroller.scrollHeight -
                        (scroller.scrollTop + scroller.clientHeight) <=
                    4
                ) {
                    id = sections[sections.length - 1].id;
                }
                setActive(id);
            }
            let ticking = false;
            function onScroll() {
                if (ticking) return;
                ticking = true;
                requestAnimationFrame(function () {
                    updateActive();
                    ticking = false;
                });
            }
            // Capture phase so scrolls on any ancestor container are caught.
            window.addEventListener("scroll", onScroll, true);
            window.addEventListener("resize", onScroll);
            navUpdaters.push(updateActive);
            updateActive();
        }
    );
    // One delegated handler resyncs every nav's indicator to the current scroll
    // position when a top-level tab is (re)shown, so it can't stay stuck on a
    // stale section from before the switch.
    $(document).on("shown.bs.tab", function () {
        navUpdaters.forEach(function (fn) {
            requestAnimationFrame(fn);
        });
    });
}

function resizeLocationFields() {
    let mapElement = document.getElementById("project-location-map");
    if (!mapElement) {
        return;
    }
    function resize() {
        let element = document.getElementById("location-row");
        // Solution B lays this row out as a grid whose gutter has to match the
        // 24px between the section columns above it; an inline gap here would
        // win over the stylesheet and leave the two columns 9px out of step
        // with every section. The original design keeps its inline value.
        const styledBySheet = document.body.classList.contains("solution-b");
        let labels = Array.from(
            document.getElementsByClassName("location-label")
        );
        let inputs = Array.from(
            document.getElementsByClassName("location-input")
        );
        if (window.innerWidth < 900) {
            element.classList.remove("row");
            if (!styledBySheet) {
                element.style.columnGap = "0px";
            }
            for (let label of labels) {
                label.classList.add("col-md-3");
                label.classList.remove("col-md-6");
            }
            for (let input of inputs) {
                input.classList.add("col-md-9");
                input.classList.remove("col-md-6");
            }
            mapElement.style.marginBottom = "20px";
        } else {
            element.classList.add("row");
            if (!styledBySheet) {
                element.style.columnGap = "15px";
            }
            for (let label of labels) {
                label.classList.add("col-md-6");
                label.classList.remove("col-md-3");
            }
            for (let input of inputs) {
                input.classList.add("col-md-6");
                input.classList.remove("col-md-9");
            }
            mapElement.style.marginBottom = "10px";
        }
    }
    resize();
    window.addEventListener("resize", resize);
}

async function checkResDownloadStatus(elementId: string = null) {
    if (!elementId) {
        elementId = "res-status-config";
    }
    let configElement = document.getElementById(elementId) as HTMLScriptElement;
    if (!configElement) {
        return;
    }
    let config = JSON.parse(configElement.textContent);
    if (!config.wait) {
        console.log("RES download status check not required.");
        return;
    }
    let url = `${apiUrl}/projects/${config.project_id}/inputs/res_download_status`;
    let status;
    while (true) {
        console.log("Checking RES download status...");
        let response = await get(url);
        let result = await response.json();
        status = result.status;
        if (status !== "in_progress") {
            break;
        }
        await sleep(1500);
    }
    if (config.close_modal) {
        window.location.href = config.success_url;
        // document.dispatchEvent(new Event("closeUploadModal"));
        // document.dispatchEvent(new Event("reloadInputsTab"));
    } else {
        window.location.href = config.success_url;
    }
}

/*
 * Add functionality for adding or removing formset rows for "matrix" formsets within project
 * or component forms by clicking an add or subtract button.
 */
function enhanceMatrixFields(root: Element | Document = document) {
    let chartDivs = Array.from(
        root.querySelectorAll("[id$='-component-chart']")
    );
    for (let chartDiv of chartDivs) {
        let chartName = chartDiv.id.replace("-component-chart", "");
        const formsetContainer: HTMLElement = (chartDiv.closest<HTMLElement>(
            ".component-formset-section"
        ) ?? chartDiv.parentElement) as HTMLElement;
        let seriesLabels = [];
        let formsetHeaderDiv = Array.from(
            formsetContainer.querySelectorAll("[class$='formset-row-header']")
        )[0];
        for (let child of Array.from(formsetHeaderDiv.children)) {
            seriesLabels.push(
                child.innerText.replace(/(^\s+)|(\s+$)|(\r\n|\n|\r)/gm, "")
            );
        }
        let yAxisLabels = [];
        for (let seriesLabel of seriesLabels.slice(1)) {
            let unit = seriesLabel.match(/\[(.*)\]/);
            if (unit) {
                unit = unit[0];
            } else {
                unit = "quantity";
            }
            if (!yAxisLabels.includes(unit)) {
                yAxisLabels.push(unit);
            }
        }
        let layout = makeTimeSeriesLayout(
            "",
            false,
            seriesLabels[0],
            yAxisLabels
        );

        function getTraces(chartDiv, seriesLabels) {
            let series = [];
            for (let i = 0; i < seriesLabels.length; i++) {
                series.push([]);
            }
            let rowFields = Array.from(
                formsetContainer.querySelectorAll(
                    "[class$='formset-row-fields']"
                )
            );
            for (let rowField of rowFields) {
                let cols = Array.from(
                    rowField.querySelectorAll("[class^='col-']")
                );
                for (let [i, col] of cols.entries()) {
                    let value = col.querySelector("input").value;
                    series[i].push(parseFloat(value));
                }
            }
            let xValues = series[0];
            let traces = [];
            for (let [i, seriex] of series.slice(1).entries()) {
                let trace = {
                    x: xValues,
                    y: seriex,
                    mode: "lines",
                    name: seriesLabels[i + 1],
                    opacity: 0.9,
                };
                traces.push(trace);
            }
            return traces;
        }

        function updateChart() {
            let traces = getTraces(chartDiv, seriesLabels);
            // On re-renders, preserve the chart's actual current height rather than
            // letting Plotly.react reset it to the layout default (600px). Plotly
            // updates _fullLayout.height whenever Plots.resize() is called, so this
            // keeps the height stable across data updates.
            let gd = chartDiv as any;
            let currentHeight = gd._fullLayout ? gd._fullLayout.height : null;
            let reactLayout = currentHeight
                ? { ...layout, height: currentHeight }
                : layout;
            Plotly.react(chartDiv.id, traces, reactLayout, {
                displayModeBar: false,
                displaylogo: false,
                responsive: true,
            });
        }

        updateChart();

        let rowFields = Array.from(
            formsetContainer.querySelectorAll("[class$='formset-row-fields']")
        );
        for (let rowField of rowFields) {
            let cols = Array.from(rowField.querySelectorAll("[class^='col-']"));
            for (let [i, col] of cols.entries()) {
                let input = col.querySelector("input");
                input.addEventListener("keyup", function () {
                    updateChart();
                });
            }
        }

        let formsetRows = formsetContainer.querySelector(".formset-rows");
        let addButton = document.createElement("button");
        addButton.innerHTML = '<i class="tim-icons icon-simple-add"></i>';
        addButton.classList.add("btn", "btn-icon", "btn-round", "btn-success");
        addButton.style.marginLeft = "1px";
        let removeButton = document.createElement("button");
        removeButton.innerHTML = '<i class="tim-icons icon-simple-delete"></i>';
        removeButton.classList.add(
            "btn",
            "btn-icon",
            "btn-round",
            "btn-danger"
        );
        let totalFormsEl = formsetRows.firstElementChild;
        addButton.addEventListener("click", function (event) {
            event.preventDefault();
            let prevDiv = $(addButton).parent().prev();
            let copyDiv = $(prevDiv).clone();
            $(copyDiv)
                .children()
                .children()
                .each(function () {
                    let parts = this.name.split("-");
                    parts[1] = `${parseInt(parts[1]) + 1}`;
                    let name = parts.join("-");
                    this.name = name;
                    this.id = `id_${name}`;
                    this.value = "";
                });
            totalFormsEl.value = `${parseInt(totalFormsEl.value) + 1}`;
            $(copyDiv).insertAfter(prevDiv);
            let lastRow = Array.from(
                formsetContainer.querySelectorAll(
                    "[class$='formset-row-fields']"
                )
            ).slice(-1)[0];
            let cols = Array.from(lastRow.querySelectorAll("[class^='col-']"));
            for (let [i, col] of cols.entries()) {
                let input = col.querySelector("input");
                input.addEventListener("keyup", function () {
                    updateChart();
                });
            }
        });
        removeButton.addEventListener("click", function (event) {
            event.preventDefault();
            let prevDiv = $(addButton).parent().prev();
            if (prevDiv.prev().hasClass("formset-row-fields")) {
                prevDiv.remove();
                totalFormsEl.value = `${parseInt(totalFormsEl.value) - 1}`;
            }
            updateChart();
        });
        let div = document.createElement("div");
        div.style.textAlign = "center";
        div.append(addButton);
        div.append(removeButton);
        formsetRows.append(div);
    }
}

/*
 * Add functionality for adding or removing formset rows for "matrix" formsets within project
 * or component forms by clicking an add or subtract button.
 */
function enhanceWindPowerCurveFields(root: Element | Document = document) {
    let chartDiv = root.querySelector("#wind-power-curve-chart");
    if (chartDiv) {
        let layout = makeTimeSeriesLayout("", false, "Windspeed [m/s]", [
            "Output [kW]",
        ]);

        function getTraces(chartDiv) {
            let xValues = [];
            let yValues = [];
            let inputs = Array.from(
                document.querySelectorAll<HTMLInputElement>("[id^='id_pc']")
            );
            for (let [i, input] of inputs.entries()) {
                let value = parseFloat(input.value);
                xValues.push(i + 1);
                yValues.push(value);
            }
            let trace = {
                x: xValues,
                y: yValues,
                mode: "lines",
                name: "Output [kW]",
                opacity: 0.9,
            };

            return [trace];
        }

        function updateChart() {
            let traces = getTraces(chartDiv);
            Plotly.react(chartDiv.id, traces, layout, {
                displayModeBar: false,
                displaylogo: false,
                responsive: true,
            });
        }

        updateChart();

        let inputs = Array.from(
            document.querySelectorAll<HTMLInputElement>("[id^='id_pc']")
        );
        for (let input of inputs) {
            input.addEventListener("keyup", function () {
                updateChart();
            });
        }
    }
}

let componentPreviewDebounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Format a derived figure the way the server rendered it on page load, so the
 * value does not visibly shift the moment the user types. Currency figures are
 * grouped with no decimals (Django's floatformat:"0g"), the rest carry up to
 * three decimals and no grouping (floatformat:"-3").
 */
function formatDerivedFigure(figure: any): string {
    const value = figure.value;
    if (value === null || value === undefined || Number.isNaN(value)) {
        return "–";
    }
    if (figure.currency) {
        return Number(value).toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }
    // Up to three decimals, and none where there are none to show. A fixed
    // single decimal hides small differences entirely: at half a kilowatt of
    // rated power, availability could move between 95 and 100 per cent without
    // the figure ever changing.
    return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
        useGrouping: false,
    });
}

/**
 * Keep the component form's Summary figures and Quick look chart in step with
 * the values currently in the form, rather than the ones last saved.
 *
 * Both come from a single request: the form is posted to the preview endpoint,
 * which builds an unsaved component from it and asks the simulation class for
 * the plot and the figures. Nothing is recomputed here — a figure on screen is
 * the number the economics will use, not a copy of the formula.
 */
function updateComponentPreview(root: Element | Document = document) {
    const plotEl = root.querySelector<HTMLElement>("#component-output-plot");
    const hasFigures = !!document.querySelector("[data-derived]");
    // A component may have a chart, figures, or both; only leave if it has neither.
    if (!plotEl && !hasFigures) return;

    const formEl = document.querySelector<HTMLFormElement>("#component-form");
    if (!formEl) return;

    const container = document.querySelector<HTMLElement>(
        "#component-output-plot-container"
    );
    // The form carries the type for every component; the plot container only
    // exists for those that draw a chart.
    const componentType =
        formEl.dataset.componentType ?? container?.dataset.componentType;
    if (!componentType) return;

    function fetchAndUpdate() {
        const params = new URLSearchParams();
        new FormData(formEl).forEach((value, key) => {
            params.append(key, value as string);
        });
        const csrfToken =
            document.querySelector<HTMLInputElement>(
                "[name=csrfmiddlewaretoken]"
            )?.value ?? "";
        fetch(`/api/v1/components/${componentType}/preview`, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "X-CSRFToken": csrfToken,
            },
            body: params.toString(),
        })
            .then((r) => r.json())
            .then((data) => {
                // An invalid form leaves the last good values in place rather
                // than blanking the panel while the user is mid-edit.
                if (data.error) return;
                (data.derived_figures ?? []).forEach((figure: any) => {
                    const cell = document.querySelector<HTMLElement>(
                        `[data-derived="${figure.key}"]`
                    );
                    if (cell) {
                        cell.textContent = formatDerivedFigure(figure);
                    }
                });
                if (!plotEl || !data.plot || !container) return;
                const traces = makeScatterPlotTraces(data.plot);
                const layout = makeBasicPlotLayout(data.plot);
                if (container.dataset.height) {
                    layout.height = parseInt(container.dataset.height, 10);
                }
                Plotly.react(plotEl.id, traces, layout, {
                    displayModeBar: false,
                    displaylogo: false,
                    responsive: true,
                });
            })
            .catch(() => {
                // Silently ignore fetch errors (e.g. network issues)
            });
    }

    // Fire immediately so both render from the current form state
    fetchAndUpdate();

    // Debounced listener on all form input events (covers every tab)
    const existingHandler = (formEl as any)._componentPreviewHandler;
    if (existingHandler) {
        formEl.removeEventListener("input", existingHandler);
    }
    function handler() {
        if (componentPreviewDebounceTimer !== null) {
            clearTimeout(componentPreviewDebounceTimer);
        }
        componentPreviewDebounceTimer = setTimeout(fetchAndUpdate, 400);
    }
    (formEl as any)._componentPreviewHandler = handler;
    formEl.addEventListener("input", handler);
}

function validateMinMaxBase() {
    let isAssetFormRow = false;
    let minMaxRows = document.getElementsByClassName("min-max-row");
    if (!minMaxRows.length) {
        minMaxRows = document.getElementsByClassName("asset-form-row");
        isAssetFormRow = true;
    }
    if (!minMaxRows) {
        return;
    }
    let updateAssetValuesButton = document.getElementById(
        "update-asset-values-button"
    );
    for (let minMaxRow of minMaxRows) {
        let assetId = isAssetFormRow
            ? minMaxRow.dataset.componentId
            : minMaxRow.dataset.asset_id;
        let banner = document.getElementById("min-max-feedback-banner");
        if (isAssetFormRow) {
            banner =
                minMaxRow.parentElement.parentElement.parentElement
                    .parentElement.firstElementChild;
        }
        let minEl = document.getElementById(`min-${assetId}`);
        let maxEl = document.getElementById(`max-${assetId}`);
        minEl.onkeyup = function () {
            let minVal = parseInt(this.value);
            let maxVal = parseInt(
                document.getElementById(`max-${assetId}`).value
            );
            if (minVal > maxVal) {
                updateAssetValuesButton.disabled = true;
                minEl.classList.add("input-field-error");
                banner.innerHTML = "Min cannot be greater than max";
                banner.classList.remove("d-none");
            } else {
                minEl.classList.remove("input-field-error");
                maxEl.classList.remove("input-field-error");
                banner.classList.add("d-none");
                checkComponentFormDirty();
            }
        };
        maxEl.onkeyup = function () {
            let maxVal = parseInt(this.value);
            let minVal = parseInt(
                document.getElementById(`min-${assetId}`).value
            );
            if (maxVal < minVal) {
                updateAssetValuesButton.disabled = true;
                maxEl.classList.add("input-field-error");
                banner.innerHTML = "Max cannot be less than min";
                banner.classList.remove("d-none");
            } else {
                maxEl.classList.remove("input-field-error");
                minEl.classList.remove("input-field-error");
                banner.classList.add("d-none");
                checkComponentFormDirty();
            }
        };
    }
}

function warnResultsDownload() {
    let resultsButton = document.getElementById("results-btn");
    if (!resultsButton) {
        return;
    }
    resultsButton.onclick = function (e) {
        Swal.fire({
            position: "center",
            title: "Downloading results file!",
            text: "This may take a few moments...",
            showConfirmButton: false,
            timer: 2000,
            icon: "success",
        });
    };
}

/**
 * Say that a chart's spreadsheet is being prepared.
 *
 * The same notice the results file gives, for the same reason: the workbook is
 * built when it is asked for, and a full-resolution series takes a few seconds
 * during which the browser shows nothing at all.
 */
function warnChartDataDownload() {
    document
        .querySelectorAll<HTMLElement>(".ts-data-download")
        .forEach(function (link) {
            link.addEventListener("click", function () {
                Swal.fire({
                    position: "center",
                    title: "Preparing the data file!",
                    text: "This may take a few moments...",
                    showConfirmButton: false,
                    timer: 2000,
                    icon: "success",
                });
            });
        });
}

/**
 * The settings sections that fold away, and the sensitivity fields' greying.
 *
 * A section opens when something in it is in error, so a complaint is never
 * hidden inside a folded section.
 */
function initSettingsFoldaways() {
    document
        .querySelectorAll<HTMLElement>("[data-settings-fold]")
        .forEach(function (section) {
            const head = section.querySelector<HTMLElement>(
                "[data-settings-fold-toggle]"
            );
            if (!head) return;
            const hasError = !!section.querySelector(".has-danger");
            section.dataset.open = hasError ? "true" : "false";
            head.addEventListener("click", function () {
                section.dataset.open =
                    section.dataset.open === "true" ? "false" : "true";
            });
        });

    const fields = document.querySelector<HTMLElement>(
        "[data-sensitivity-fields]"
    );
    if (!fields) return;
    const toggle = fields.querySelector<HTMLInputElement>(
        "input[name='calculate_sensitivity']"
    );
    if (!toggle) return;

    // The switch keeps its contrast while the values under it dim. It is found
    // rather than assumed to be first: the form renders fields in the order the
    // model declares them, which puts this one last.
    const switchRow = toggle.closest(".settings-field-row");
    if (switchRow) {
        switchRow.classList.add("settings-switch-row");
    }

    // Greyed, not disabled. A disabled input is not posted, so switching the
    // analysis off and saving would erase the values it was switched off from —
    // and they have to be there when it is switched back on.
    const refresh = () => {
        fields.classList.toggle("settings-fields-inactive", !toggle.checked);
    };
    refresh();
    toggle.addEventListener("change", refresh);
}

function filterAssetFormOnSubmit() {
    let selectedInputs = document.querySelectorAll('input[id^="selected"]');
    for (let selectedInput of selectedInputs) {
        let assetId = selectedInput.id.replace("selected-", "");
        let relatedInputs = document.querySelectorAll(
            `input[id="min-${assetId}"], input[id="max-${assetId}"], input[id="base-${assetId}"], input[id="asset-${assetId}"], input[id="asset-type-${assetId}"], input[id="component-${assetId}"], input[id="asset-component-${assetId}"]`
        );
        selectedInput.addEventListener("change", function () {
            if (selectedInput.checked) {
                for (let relatedInput of relatedInputs) {
                    relatedInput.removeAttribute("disabled");
                }
            } else {
                for (let relatedInput of relatedInputs) {
                    relatedInput.setAttribute("disabled", "");
                }
            }
        });
    }
}

window["manageJobs"] = manageJobs;
window["cancelJob"] = cancelJob;
window["startJob"] = startJob;
window["loadGeneratorPlot"] = loadGeneratorPlot;
window["checkResDownloadStatus"] = checkResDownloadStatus;

/*
 * Detail page: live capacity figures and chart updates.
 * Uses document-level delegation so it survives htmx re-renders of #components.
 */
function updateCapacityFigures(assetId: string) {
    const ucInput = document.getElementById(
        `unit-capacity-${assetId}`
    ) as HTMLInputElement;
    const unitCapacity = ucInput ? parseFloat(ucInput.value) || 0 : 0;
    const unitLabel = ucInput
        ? (ucInput.nextElementSibling as HTMLElement)?.textContent?.trim() || ""
        : "";
    const pairs: [string, string][] = [
        [`min-cap-${assetId}`, `asset-min-${assetId}`],
        [`max-cap-${assetId}`, `asset-max-${assetId}`],
        [`base-cap-${assetId}`, `asset-base-${assetId}`],
    ];
    for (const [capId, inputName] of pairs) {
        const capEl = document.getElementById(capId);
        const inputEl = document.getElementsByName(
            inputName
        )[0] as HTMLInputElement;
        if (capEl && inputEl) {
            const val = (parseFloat(inputEl.value) || 0) * unitCapacity;
            capEl.textContent =
                val.toLocaleString("en-US") +
                (unitLabel ? " " + unitLabel : "");
        }
    }
}

/*
 * Show a number input without a trailing ".0" when its value is a whole number
 * (e.g. a unit capacity of 1000.0 renders as 1000). Number-of-units fields are
 * integers, and the misleading ".0" — which a float-backed value or an Undo can
 * reintroduce — is dropped. Fractional values (e.g. 2.5) are left untouched.
 */
function trimIntegralZero(el: HTMLInputElement | null) {
    if (!el || el.value === "") return;
    const n = parseFloat(el.value);
    if (!isNaN(n) && Number.isInteger(n)) el.value = String(n);
}

/* Normalize every dimensioning input of one asset (min/base/max/unit-capacity). */
function normalizeAssetInputs(assetId: string) {
    for (const fieldId of [
        `min-${assetId}`,
        `base-${assetId}`,
        `max-${assetId}`,
        `unit-capacity-${assetId}`,
    ]) {
        trimIntegralZero(document.getElementById(fieldId) as HTMLInputElement);
    }
}

function initDetailPageCapacityFigures() {
    const rows = Array.from(document.getElementsByClassName("min-max-row"));
    for (const row of rows) {
        const tr = row as HTMLElement;
        const assetId = tr.dataset.asset_id;
        if (!assetId) continue;
        normalizeAssetInputs(assetId);
        updateCapacityFigures(assetId);
        const optCapEl = document.getElementById(`opt-cap-${assetId}`);
        const optTd = document.getElementById(
            `optimal-${assetId}`
        ) as HTMLElement;
        if (optCapEl && optTd && !optCapEl.classList.contains("d-none")) {
            const optVal = parseFloat(optTd.dataset.value);
            if (!isNaN(optVal) && optTd.dataset.value !== "--") {
                const origUC =
                    parseFloat(tr.dataset.componentUnitCapacity || "0") || 0;
                const ucInput = document.getElementById(
                    `unit-capacity-${assetId}`
                ) as HTMLInputElement;
                const unitLabel =
                    (
                        ucInput?.nextElementSibling as HTMLElement
                    )?.textContent?.trim() || "";
                optCapEl.textContent =
                    (optVal * origUC).toLocaleString("en-US") +
                    (unitLabel ? " " + unitLabel : "");
            }
        }
    }
}

function isInputDirty(input: HTMLInputElement): boolean {
    const currentNum = input.valueAsNumber;
    const originalNum = parseFloat(input.defaultValue);
    if (isNaN(currentNum) && isNaN(originalNum)) return false;
    return currentNum !== originalNum;
}

/*
 * True if any dimensioning input (min/max/base/unit-capacity) for this asset
 * differs from its saved value. Disabled unit-capacity inputs (markets) are
 * ignored.
 */
function isAssetDimensioningDirty(assetId: string): boolean {
    for (const fieldId of [
        `min-${assetId}`,
        `max-${assetId}`,
        `base-${assetId}`,
    ]) {
        const input = document.getElementById(fieldId) as HTMLInputElement;
        if (input && isInputDirty(input)) return true;
    }
    const ucInput = document.getElementById(
        `unit-capacity-${assetId}`
    ) as HTMLInputElement;
    if (ucInput && !ucInput.disabled && isInputDirty(ucInput)) return true;
    return false;
}

/* True if any component on the detail page has unsaved dimensioning edits. */
function areComponentsDirty(): boolean {
    let dirty = false;
    document.querySelectorAll<HTMLElement>(".min-max-row").forEach((row) => {
        if (dirty) return;
        const assetId = row.dataset.asset_id;
        if (assetId && isAssetDimensioningDirty(assetId)) dirty = true;
    });
    return dirty;
}

/* True if any dimensioning row currently has min > max (an invalid state that
 * must block saving). Independent of the onkeyup CSS-class bookkeeping so it's
 * reliable after an Undo / programmatic change. */
function hasDimensioningError(): boolean {
    let invalid = false;
    document
        .querySelectorAll<HTMLElement>(".min-max-row, .asset-form-row")
        .forEach((row) => {
            if (invalid) return;
            const id = row.dataset.asset_id || row.dataset.componentId;
            const minEl = document.getElementById(
                `min-${id}`
            ) as HTMLInputElement | null;
            const maxEl = document.getElementById(
                `max-${id}`
            ) as HTMLInputElement | null;
            if (minEl && maxEl && parseInt(minEl.value) > parseInt(maxEl.value))
                invalid = true;
        });
    return invalid;
}

function checkComponentFormDirty() {
    const btn = document.getElementById(
        "update-asset-values-button"
    ) as HTMLButtonElement;
    if (!btn || btn.dataset.projectLocked === "true") return;
    const dirty = areComponentsDirty();
    // The creation "sizing" step's Save is the wizard proceed/finish action, so
    // it stays available regardless of dirty (otherwise Undo, which reverts to a
    // clean state, would disable it and strand the user) — only a min>max error
    // disables it. The detail page keeps the dirty-gated behaviour.
    if (btn.dataset.alwaysEnabled === "true") {
        btn.disabled = hasDimensioningError();
    } else {
        btn.disabled = !dirty;
    }
    // The Undo button is enabled together with Save: there is something to
    // discard exactly when there is something to save.
    const undoBtn = document.getElementById(
        "undo-asset-values-button"
    ) as HTMLButtonElement;
    if (undoBtn && undoBtn.dataset.projectLocked !== "true") {
        undoBtn.disabled = !dirty;
    }
}

/* Discard unsaved component edits: restore every dimensioning input to its last
 * saved value, then refresh the capacity figures, optimal-stale state and the
 * Save/Undo button state. */
function revertComponentEdits() {
    document.querySelectorAll<HTMLElement>(".min-max-row").forEach((row) => {
        const assetId = row.dataset.asset_id;
        if (!assetId) return;
        for (const fieldId of [
            `min-${assetId}`,
            `base-${assetId}`,
            `max-${assetId}`,
            `unit-capacity-${assetId}`,
        ]) {
            const el = document.getElementById(fieldId) as HTMLInputElement;
            if (el) el.value = el.defaultValue;
        }
        normalizeAssetInputs(assetId);
        updateCapacityFigures(assetId);
        updateAssetOptimalStale(assetId);
    });
    checkComponentFormDirty();
}

/*
 * True if the project has calculation results (and therefore an optimal design
 * to invalidate). Solution-B marks the components column with data-has-results;
 * the original UI only renders the peak-demand plot div when results exist.
 */
function detailPageHasResults(): boolean {
    if (document.body.classList.contains("solution-b")) {
        const col = document.getElementById("component-schematic-column");
        return !!(col && col.dataset.hasResults === "true");
    }
    return !!document.getElementById("peak-demand-vs-installed-capacity-plot");
}

/*
 * The optimal number of units (and the optimal design) is only valid for the
 * exact dimensioning it was computed with. When the user edits any dimensioning
 * input the displayed optimal becomes stale, so hide it and show a dash; when
 * every edit is reverted to the saved values, restore it. No-op when there are
 * no results to invalidate.
 */
function updateAssetOptimalStale(assetId: string) {
    const optEl = document.getElementById(`optimal-${assetId}`);
    if (!optEl || !detailPageHasResults()) return;
    const stale = isAssetDimensioningDirty(assetId);

    // Solution-B card layout: toggle the figures block against the dash.
    const ccOpt = optEl.closest(".cc-opt");
    if (ccOpt) {
        const figs = ccOpt.querySelector(".cc-opt-figs");
        const staleEl = ccOpt.querySelector(".cc-opt-stale");
        if (figs && staleEl) {
            figs.classList.toggle("d-none", stale);
            staleEl.classList.toggle("d-none", !stale);
        }
        return;
    }

    // Original UI table cell: replace the count with a dash, preserving the
    // original markup (and data-value) so it can be restored on revert.
    if (stale) {
        if (optEl.dataset.origHtml === undefined) {
            optEl.dataset.origHtml = optEl.innerHTML;
        }
        optEl.innerHTML = "&mdash;";
    } else if (optEl.dataset.origHtml !== undefined) {
        optEl.innerHTML = optEl.dataset.origHtml;
        delete optEl.dataset.origHtml;
    }
}

/*
 * Unsaved-changes guard for the Calculate button.
 *
 * Calculation always runs against the project state stored on the server, so
 * any edits the user has made on the detail page but not yet saved are silently
 * excluded. Before calculating we therefore check for unsaved component and
 * settings changes and, if any exist, prompt the user instead of starting the
 * job. Components save via htmx, so they can be saved and the job started in one
 * step; the settings form is a full-page POST, so we can only warn about it.
 */

/* The option index a <select> reverts to on form.reset(): the first option
 * marked selected in the markup, or 0 when none is. */
function selectResetIndex(sel: HTMLSelectElement): number {
    for (let i = 0; i < sel.options.length; i++) {
        if (sel.options[i].defaultSelected) return i;
    }
    return sel.options.length ? 0 : -1;
}

/* True if a single form control differs from the value it would reset to.
 * Mirrors form.reset() exactly so dirty-detection and undo stay consistent. */
function isControlDirty(
    el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
): boolean {
    if (el.disabled || (el as HTMLInputElement).type === "hidden") return false;
    if (el.tagName === "SELECT") {
        const sel = el as HTMLSelectElement;
        return sel.selectedIndex !== selectResetIndex(sel);
    }
    const input = el as HTMLInputElement;
    if (input.type === "checkbox" || input.type === "radio") {
        return input.checked !== input.defaultChecked;
    }
    return input.value !== input.defaultValue;
}

/* True if any control in the form has unsaved edits. */
function isFormDirty(form: HTMLFormElement): boolean {
    const controls = form.querySelectorAll<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >("input, select, textarea");
    for (const el of Array.from(controls)) {
        if (isControlDirty(el)) return true;
    }
    return false;
}

/* True if the inline settings form has unsaved edits. Always false when there
 * is no settings form (e.g. the original UI edits settings on another page). */
function isSettingsFormDirty(): boolean {
    const form = document.getElementById("settings-form") as HTMLFormElement;
    return form ? isFormDirty(form) : false;
}

/* Open the existing "Review settings before calculating" confirmation modal. */
function openConfirmSettingsModal() {
    ($("#confirmSettingsModal") as any).modal("show");
}

/* Configure and show the unsaved-changes prompt. "Save & calculate" is only
 * offered when the unsaved changes are component edits (saved via htmx) and the
 * settings are clean, since the settings form cannot be saved without a reload. */
function showUnsavedChangesModal(compDirty: boolean, setDirty: boolean) {
    const modal = document.getElementById("unsavedChangesModal");
    if (!modal) {
        // No prompt available: fall back to the normal confirmation flow.
        openConfirmSettingsModal();
        return;
    }
    const list = modal.querySelector(".unsaved-list");
    if (list) {
        const items: string[] = [];
        if (compDirty) items.push("Component dimensioning (min / base / max)");
        if (setDirty) items.push("Project settings");
        list.innerHTML = items.map((i) => `<li>${i}</li>`).join("");
    }
    const saveBtn = document.getElementById(
        "update-asset-values-button"
    ) as HTMLButtonElement;
    // "Save & calculate" only works when the unsaved changes are component edits
    // (saved via htmx); the settings form is a full-page POST and cannot be
    // saved in place, so it is warned about only.
    const canSaveCalc =
        compDirty &&
        !setDirty &&
        !!saveBtn &&
        !saveBtn.disabled &&
        saveBtn.hasAttribute("hx-post");
    const saveCalcBtn = document.getElementById("unsaved-save-calc-btn");
    if (saveCalcBtn) saveCalcBtn.classList.toggle("d-none", !canSaveCalc);
    const explainer = modal.querySelector(".unsaved-explainer");
    if (explainer) {
        explainer.innerHTML = canSaveCalc
            ? "<strong>Save &amp; calculate</strong> saves your changes first, then calculates with them. <strong>Calculate with saved values</strong> ignores them and uses the last saved values."
            : "Save your changes first if you want them included, or <strong>Calculate with saved values</strong> to use the last saved values.";
    }
    ($("#unsavedChangesModal") as any).modal("show");
}

function initCalculateGuard() {
    const calcBtn = document.getElementById("calculate-btn");
    if (!calcBtn) return;
    calcBtn.addEventListener("click", function (e: Event) {
        e.preventDefault();
        if (
            calcBtn.hasAttribute("disabled") ||
            calcBtn.classList.contains("disabled")
        ) {
            return;
        }
        const compDirty = areComponentsDirty();
        const setDirty = isSettingsFormDirty();
        if (compDirty || setDirty) {
            showUnsavedChangesModal(compDirty, setDirty);
        } else {
            openConfirmSettingsModal();
        }
    });

    // "Calculate without saving": proceed with the saved values via the normal
    // review modal. Chain on hidden.bs.modal so the two modals never overlap.
    const runAnywayBtn = document.getElementById("unsaved-run-anyway-btn");
    if (runAnywayBtn) {
        runAnywayBtn.addEventListener("click", function () {
            ($("#unsavedChangesModal") as any)
                .one("hidden.bs.modal", openConfirmSettingsModal)
                .modal("hide");
        });
    }

    // "Save & calculate": save the component form via htmx, then start the job
    // once the swap settles (see the #components htmx:afterSettle handler).
    const saveCalcBtn = document.getElementById("unsaved-save-calc-btn");
    if (saveCalcBtn) {
        saveCalcBtn.addEventListener("click", function () {
            const saveBtn = document.getElementById(
                "update-asset-values-button"
            ) as HTMLButtonElement;
            if (!saveBtn || !saveBtn.hasAttribute("hx-post")) return;
            pendingCalcAfterSave = true;
            ($("#unsavedChangesModal") as any).modal("hide");
            saveBtn.click();
        });
    }
}

/*
 * Charts of one value per calendar period — today the revenues totalled per day,
 * per week and per year, beside the quarter-hourly revenues chart.
 *
 * These have no zoom windows: at a day, a week or a year per bar the whole
 * operating horizon is on the screen already. The server aggregates the totals
 * on request out of the stored result series; the browser only picks which of
 * the three to draw.
 */
/**
 * The x label of a period: the year alone for yearly buckets, the start date
 * for the others.
 */
function periodLabels(labels: string[], period: string): string[] {
    return period === "year"
        ? labels.map((label) => label.slice(0, 4))
        : labels.slice();
}

/**
 * Annualise a period's figures and express them per kW of rated power.
 *
 * A bar covers as many days as its period holds, so it is scaled to a year
 * before being divided — a week of revenue and a year of it then read on the
 * same axis. Returns the values untouched when there is nothing to divide by:
 * a system with no battery has no rated power, and dividing by it would be a
 * division by zero wearing a unit.
 */
function perRatedPower(data: any, block: any, values: number[]): number[] {
    const power = Number(data.rated_power_kw);
    if (!power || !Array.isArray(block.days)) {
        return values;
    }
    return values.map(function (value: number, i: number) {
        const days = Number(block.days[i]);
        if (!days) return 0;
        return (value * (365 / days)) / power;
    });
}

function periodPlotTraces(data: any, period: string, perUnit = false): any[] {
    const block = data[period];
    const x = periodLabels(block.x ?? [], period);
    const colours: Record<string, string> = {};
    (data.streams ?? []).forEach(function (stream: any) {
        colours[stream.name] = stream.color;
    });
    const currency = data.currency ?? "";
    const unit = perUnit ? `${currency}/kW/a` : currency;
    const scale = (values: number[]) =>
        perUnit ? perRatedPower(data, block, values) : values;
    const present = (block.y ?? []).map((series: any) => series.name);
    const shortName = (name: string) =>
        seriesNameIn(name, present) !== name
            ? seriesNameIn(name, present)
            : name.replace(" market revenues", "");

    const traces: any[] = (block.y ?? []).map(function (series: any) {
        return {
            type: "bar",
            x: x,
            y: scale(series.values),
            name: shortName(series.name),
            marker: { color: colours[series.name] },
            hovertemplate: `%{y:,.1f} ${unit}<extra>%{fullData.name}</extra>`,
        };
    });

    // The stack does not show the net: streams can be negative — a final
    // settlement regularly is — and those stack downwards, so the top of the
    // stack is the positive part alone. The total is drawn as its own marker.
    if (block.total) {
        // One marker per bar is legible over a year of days; over seventeen it
        // is a solid block that hides the bars underneath, so past a couple of
        // hundred periods the total becomes a thin line instead.
        const dense = x.length > 120;
        const totalColour = colours[data.total_name] ?? "#ff5b5b";
        traces.push({
            type: "scatter",
            mode: dense ? "lines" : "markers",
            x: x,
            y: scale(block.total),
            name: "Net total",
            line: { color: totalColour, width: 1 },
            marker: {
                color: totalColour,
                symbol: "diamond",
                size: 6,
                line: { color: "rgba(255,255,255,.6)", width: 1 },
            },
            hovertemplate: `%{y:,.1f} ${unit}<extra>Net total</extra>`,
        });
    }
    return traces;
}

function periodPlotLayout(data: any, period: string, perUnit = false): any {
    const perPeriod = { day: "day", week: "week", year: "year" }[period];
    return {
        // "relative" rather than "stack": it puts negative streams below the
        // axis instead of folding them into the positive stack.
        barmode: "relative",
        // A bar takes its share of one category slot, so a run of a single year
        // would otherwise be one bar across the whole plot. Widen the gap when
        // there are only a few periods.
        bargap: (data[period].x ?? []).length <= 4 ? 0.6 : 0.15,
        // A category axis, not a date axis: every bar is one period whatever
        // its length, a single yearly bar keeps a sensible width instead of
        // filling the plot, and the ticks stay readable from one bar to
        // several thousand. nticks thins the labels; Plotly picks which.
        xaxis: {
            ...solutionBAxis(),
            type: "category",
            nticks: 12,
            automargin: true,
        },
        yaxis: {
            ...solutionBAxis(),
            title: perUnit
                ? `Annualized average revenues [${data.currency ?? ""}/kW/a]`
                : `${data.currency ?? ""} per ${perPeriod}`,
            tickformat: "s",
            hoverformat: ",",
            zeroline: true,
            zerolinecolor: "rgba(255,255,255,.35)",
        },
        autosize: true,
        showlegend: true,
        // Above the plot, not below it. Below, the legend is placed in paper
        // coordinates and the date tick labels are not — long labels, or ones
        // Plotly decides to rotate, grow into the same band and the two print
        // on top of each other. There is nothing above the plot to collide with.
        legend: {
            orientation: "h",
            x: 0,
            xanchor: "left",
            y: 1.08,
            yanchor: "bottom",
            font: { size: 11 },
        },
        paper_bgcolor: "transparent",
        plot_bgcolor: "transparent",
        font: { color: "rgba(255,255,255, .8)", family: "Poppins" },
        margin: { l: 70, r: 20, b: 60, t: 40, pad: 4 },
        hovermode: "closest",
        height: 460,
    };
}

/*
 * Battery cycles per calendar period, split by the market that caused the
 * cycling. The values are full-cycle equivalents against each battery's own
 * round trip, computed from the engine's own per-step throughput — the same
 * quantity the daily cap constrains — so a bar above the allowance line is a
 * finding about the dispatch, not an artefact of the chart.
 */
function batteryCyclesTraces(
    data: any,
    period: string,
    perDay: boolean
): any[] {
    const block = data[period];
    const x = periodLabels(block.x ?? [], period);
    const days: number[] = block.days ?? [];
    const scale = (values: number[]) =>
        perDay ? values.map((v, i) => (days[i] ? v / days[i] : 0)) : values;

    const colours: Record<string, string> = {};
    (data.sources ?? []).forEach(function (source: any) {
        colours[source.name] = source.color;
    });
    const batteries: any[] = data.batteries ?? [];
    const several = batteries.length > 1;
    // Sources that are shares of one another cannot be stacked; they sit side
    // by side and the total is one of the bars.
    const stacked = data.stacked !== false;

    const traces: any[] = (block.series ?? []).map(function (series: any) {
        return {
            type: "bar",
            x: x,
            y: scale(series.values),
            name: several
                ? `${series.battery} — ${series.source}`
                : series.source,
            marker: { color: colours[series.source] },
            // One stack per battery: cycles of different batteries are not
            // added together, they are compared side by side.
            offsetgroup: series.battery,
            // The contained share is drawn narrower so it sits visibly within
            // the total rather than replacing it.
            width: stacked
                ? undefined
                : series.source.startsWith("of which")
                ? 0.35
                : 0.7,
            legendgroup: series.battery,
            hovertemplate: "%{y:,.2f} cycles<extra>%{fullData.name}</extra>",
        };
    });

    // Intraday goes below the axis wherever it bought day-ahead volume back, so
    // the top of the stack is the positive part alone rather than the total.
    (block.totals ?? []).forEach(function (total: any) {
        const dense = x.length > 120;
        traces.push({
            type: "scatter",
            mode: dense ? "lines" : "markers",
            x: x,
            y: scale(total.values),
            name: several ? `${total.battery} — total` : "Total",
            legendgroup: total.battery,
            line: { color: "#ff5b5b", width: 1 },
            marker: {
                color: "#ff5b5b",
                symbol: "diamond",
                size: 6,
                line: { color: "rgba(255,255,255,.6)", width: 1 },
            },
            hovertemplate: "%{y:,.2f} cycles<extra>%{fullData.name}</extra>",
        });
    });
    return traces;
}

function batteryCyclesLayout(data: any, period: string, perDay: boolean): any {
    const layout = periodPlotLayout(data, period);
    // A share drawn inside its total, not stacked on top of it and not beside
    // it: overlaid, with the narrower bar in front.
    layout.barmode = data.stacked === false ? "overlay" : "relative";
    layout.yaxis = {
        ...layout.yaxis,
        title:
            perDay || period === "day"
                ? "full cycles per day"
                : `full cycles per ${period}`,
        tickformat: ",.2f",
        hoverformat: ",.2f",
    };
    // The allowance is a per-day figure, so it is only comparable where the
    // bars are per-day too. On a weekly or yearly total it would be a line at
    // two against bars in the tens, which reads as a limit that is always
    // breached.
    const comparable = perDay || period === "day";
    const allowances = (data.batteries ?? [])
        .map((battery: any) => battery.allowed_cycles_per_day)
        .filter((value: any) => typeof value === "number" && value > 0);
    if (comparable && allowances.length) {
        layout.shapes = allowances.map((allowed: number) => ({
            type: "line",
            xref: "paper",
            x0: 0,
            x1: 1,
            yref: "y",
            y0: allowed,
            y1: allowed,
            line: { color: "#ff5b5b", width: 1, dash: "dash" },
        }));
        layout.annotations = [
            {
                xref: "paper",
                yref: "y",
                x: 1,
                xanchor: "right",
                y: Math.max(...allowances),
                yanchor: "bottom",
                text: data.soft_cap
                    ? "allowed cycles per day (soft)"
                    : "allowed cycles per day",
                showarrow: false,
                font: { size: 10, color: "rgba(255,255,255,.7)" },
            },
        ];
    }
    return layout;
}

/*
 * Volumes, prices and state of charge stacked in three panels on one shared
 * time axis, driven by the same range buttons as the other time-series charts.
 */
/* The panels that have something in them. A payload can carry a panel with no
 * series at all -- a state of charge for a system with no battery -- and an
 * empty panel with an auto-scaled axis reads as a chart that failed rather
 * than one there was nothing to draw. */
function overviewPanels(data: any): any[] {
    return (data?.panels ?? []).filter((panel: any) =>
        (panel.series ?? []).some((series: any) => (series.values ?? []).length)
    );
}

function overviewTraces(data: any): any[] {
    const traces: any[] = [];
    overviewPanels(data).forEach(function (panel: any, index: number) {
        const axis = index === 0 ? "" : String(index + 1);
        // Plotly 1.x has one legend and no group titles, so each panel opens
        // with an empty entry that acts as its heading.
        traces.push({
            type: "scatter",
            mode: "lines",
            x: [],
            y: [],
            name: `── ${panel.title} ──`,
            xaxis: `x${axis}`,
            yaxis: `y${axis}`,
            legendgroup: panel.title,
            hoverinfo: "skip",
            marker: { color: "rgba(0,0,0,0)" },
            line: { color: "rgba(0,0,0,0)" },
        });
        (panel.series ?? []).forEach(function (series: any) {
            traces.push({
                type: "scatter",
                mode: "lines",
                x: data.x,
                y: series.values,
                name: `\u2007${series.name}`,
                xaxis: `x${axis}`,
                yaxis: `y${axis}`,
                legendgroup: panel.title,
                line: { width: 1 },
                hovertemplate: `%{y:,.3f} ${panel.unit ?? ""}<extra>${
                    series.name
                }</extra>`,
            });
        });
    });
    return traces;
}

function overviewLayout(data: any, range: [string, string] | null): any {
    const panels: any[] = overviewPanels(data);
    const layout: any = {
        grid: {
            rows: panels.length,
            columns: 1,
            pattern: "independent",
            roworder: "top to bottom",
        },
        autosize: true,
        showlegend: true,
        // Pan by default: a year of quarter-hourly data is unusable otherwise.
        dragmode: "pan",
        legend: {
            orientation: "v",
            x: 1.01,
            xanchor: "left",
            y: 1,
            yanchor: "top",
            font: { size: 10 },
            tracegroupgap: 18,
        },
        paper_bgcolor: "transparent",
        plot_bgcolor: "transparent",
        font: { color: "rgba(255,255,255, .8)", family: "Poppins" },
        // Without this the unified hover box is white on white and unreadable.
        hoverlabel: {
            bgcolor: "rgba(20,22,38,.95)",
            bordercolor: "rgba(255,255,255,.25)",
            font: {
                color: "rgba(255,255,255,.9)",
                family: "Poppins",
                size: 11,
            },
            namelength: -1,
        },
        margin: { l: 80, r: 220, b: 50, t: 30, pad: 4 },
        hovermode: "x unified",
        height: 300 * panels.length,
    };
    panels.forEach(function (panel: any, index: number) {
        const axis = index === 0 ? "" : String(index + 1);
        layout[`xaxis${axis}`] = {
            ...solutionBAxis(),
            type: "date",
            // Only the bottom panel carries tick labels, and every panel follows
            // the first one's range so a pan or zoom moves all three together.
            showticklabels: index === panels.length - 1,
            ...(index === 0 ? {} : { matches: "x" }),
            ...(range ? { range: range } : {}),
        };
        // Same title form, tick format and margin handling on every panel, so
        // one axis does not read differently from the others.
        layout[`yaxis${axis}`] = {
            ...solutionBAxis(),
            title: `${panel.title} [${panel.unit ?? ""}]`,
            tickformat: "s",
            automargin: true,
            zeroline: true,
            zerolinecolor: "rgba(255,255,255,.35)",
            // The wheel and the drag belong to time alone. The range is still
            // set from here, it just cannot be moved by hand.
            fixedrange: true,
        };
    });
    return layout;
}

/*
 * One chart per varied factor: net present value against that factor's own
 * values, as points joined by a line, with the original run drawn across.
 */
function initSensitivityPlot() {
    if (!document.body.classList.contains("solution-b")) return;
    const divs = Array.from(
        document.querySelectorAll<HTMLElement>("[data-sensitivity-index]")
    );
    if (!divs.length) return;
    const projectId = divs[0].dataset.project_id;
    if (!projectId) return;

    fetch(`/api/v1/projects/${projectId}/charts/sensitivity`)
        .then((response) => (response.ok ? response.json() : null))
        .then(function (data) {
            if (!data || !data.parameters) return;
            const currency = data.currency ?? "";
            divs.forEach(function (div) {
                const index = Number(div.dataset.sensitivityIndex);
                const parameter = data.parameters[index];
                const container = div.closest<HTMLElement>(".plotly-plot-div");
                if (!parameter || !parameter.points.length) {
                    container?.classList.add("d-none");
                    return;
                }
                // Numeric where the factor's values allow it, so the spacing
                // between them means something.
                const raw = parameter.points.map((point: any) => point.value);
                const numeric = raw.every(
                    (value: string) => value !== "" && !isNaN(Number(value))
                );
                const x = numeric ? raw.map(Number) : raw;
                const order = x
                    .map((value: any, at: number) => at)
                    .sort((a: number, b: number) =>
                        numeric
                            ? x[a] - x[b]
                            : String(x[a]).localeCompare(String(x[b]))
                    );
                const trace = {
                    type: "scatter",
                    mode: "lines+markers",
                    x: order.map((at: number) => x[at]),
                    y: order.map((at: number) => parameter.points[at].npv),
                    name: parameter.name,
                    line: { color: "#4e9cf5", width: 2 },
                    marker: { color: "#4e9cf5", size: 8 },
                    hovertemplate: `%{y:,.0f} ${currency}<extra>%{x}</extra>`,
                };
                const layout: any = {
                    autosize: true,
                    showlegend: false,
                    xaxis: {
                        ...solutionBAxis(),
                        title: parameter.name,
                        type: numeric ? "linear" : "category",
                        automargin: true,
                    },
                    yaxis: {
                        ...solutionBAxis(),
                        title: `NPV [${currency}]`,
                        tickformat: "s",
                        automargin: true,
                        zeroline: true,
                        zerolinecolor: "rgba(255,255,255,.35)",
                    },
                    paper_bgcolor: "transparent",
                    plot_bgcolor: "transparent",
                    font: { color: "rgba(255,255,255, .8)", family: "Poppins" },
                    hoverlabel: {
                        bgcolor: "rgba(20,22,38,.95)",
                        bordercolor: "rgba(255,255,255,.25)",
                        font: { color: "rgba(255,255,255,.9)", size: 11 },
                        namelength: -1,
                    },
                    margin: { l: 80, r: 30, b: 55, t: 20, pad: 4 },
                    height: 320,
                };
                if (typeof data.baseline === "number") {
                    layout.shapes = [
                        {
                            type: "line",
                            xref: "paper",
                            x0: 0,
                            x1: 1,
                            yref: "y",
                            y0: data.baseline,
                            y1: data.baseline,
                            line: { color: "#ff5b5b", width: 1, dash: "dash" },
                        },
                    ];
                    layout.annotations = [
                        {
                            xref: "paper",
                            yref: "y",
                            x: 1,
                            xanchor: "right",
                            y: data.baseline,
                            yanchor: "bottom",
                            text: "original system",
                            showarrow: false,
                            font: { size: 10, color: "rgba(255,255,255,.7)" },
                        },
                    ];
                }
                Plotly.react(div.id, [trace], layout, {
                    displayModeBar: false,
                    displaylogo: false,
                    responsive: true,
                });
                if (typeof ResizeObserver !== "undefined" && container) {
                    let lastWidth = 0;
                    new ResizeObserver(function () {
                        const width = container.clientWidth;
                        if (!width || width === lastWidth) return;
                        lastWidth = width;
                        Plotly.Plots.resize(div);
                    }).observe(container);
                }
            });
        })
        .catch(function () {
            /* leave the tables in place */
        });
}

function initOverviewPlot() {
    if (!document.body.classList.contains("solution-b")) return;
    document
        .querySelectorAll<HTMLElement>(".overview-plot-div")
        .forEach(function (container) {
            const div = container.querySelector<HTMLElement>("[data-chart]");
            if (!div) return;
            const projectId = div.dataset.project_id;
            const chart = div.dataset.chart;
            if (!projectId || !chart) return;

            function hideSection() {
                const section = container.closest("section");
                if (!section) return;
                section.classList.add("d-none");
                const link = document.querySelector(
                    `[data-results-scroll][href="#${section.id}"]`
                );
                link?.closest("li")?.classList.add("d-none");
            }

            fetch(`/api/v1/projects/${projectId}/charts/${chart}`)
                .then((response) => (response.ok ? response.json() : null))
                .then(function (data) {
                    if (
                        !data ||
                        !overviewPanels(data).length ||
                        !data.x?.length
                    ) {
                        hideSection();
                        return;
                    }
                    const days: Record<string, number> = {
                        "1-day": 1,
                        "7-days": 7,
                        "1-month": 30,
                        "6-months": 182,
                        "1-year": 365,
                    };
                    function windowFor(value: string): [string, string] | null {
                        const span = days[value];
                        if (!span) return null; // "all-years": let Plotly fit it
                        const start = new Date(data.x[0]);
                        const end = new Date(start);
                        end.setDate(end.getDate() + span);
                        return [start.toISOString(), end.toISOString()];
                    }
                    const radios = document.querySelectorAll<HTMLInputElement>(
                        `input[type=radio][name="${div.id}-timerange"]`
                    );
                    let selected =
                        Array.from(radios).find((r) => r.checked)?.value ??
                        "7-days";
                    const traces = overviewTraces(data);
                    // Set while the code moves the axes, so the handler below
                    // can tell our own relayout from the user's.
                    let applying = false;
                    function clearPreset() {
                        selected = "";
                        clearRangePreset(`${div.id}-timerange`);
                    }
                    function draw() {
                        applying = true;
                        const window_ = windowFor(selected);
                        Plotly.react(
                            div.id,
                            traces,
                            overviewLayout(data, window_),
                            {
                                // The modebar carries the pan and zoom tools;
                                // scroll zoom works on the shared axis too.
                                displayModeBar: true,
                                displaylogo: false,
                                scrollZoom: true,
                                responsive: true,
                                modeBarButtonsToRemove: [
                                    "select2d",
                                    "lasso2d",
                                    "toggleSpikelines",
                                ],
                            }
                        ).then(function () {
                            const window_ = windowFor(selected);
                            rescaleAxes(
                                window_
                                    ? new Date(window_[0]).getTime()
                                    : undefined,
                                window_
                                    ? new Date(window_[1]).getTime()
                                    : undefined
                            );
                            applying = false;
                            watchRelayout();
                        });
                    }
                    // Every panel rescales to what is on screen. Series within
                    // one panel differ by orders of magnitude — a capacity
                    // price beside a day-ahead one, a committed volume beside
                    // an activated one — and a fixed range leaves most of them
                    // pressed flat against the axis.
                    const stamps: number[] = (data.x ?? []).map(
                        (value: string) => new Date(value).getTime()
                    );
                    let rescaling = false;
                    function rescaleAxes(from?: number, to?: number) {
                        if (rescaling) return;
                        const start = from ?? stamps[0];
                        const end = to ?? stamps[stamps.length - 1];
                        const update: Record<string, number[]> = {};
                        overviewPanels(data).forEach(function (
                            panel: any,
                            index: number
                        ) {
                            const series = panel.series ?? [];
                            if (!series.length) return;
                            let low = Infinity;
                            let high = -Infinity;
                            for (let i = 0; i < stamps.length; i++) {
                                if (stamps[i] < start || stamps[i] > end)
                                    continue;
                                for (const one of series) {
                                    const value = one.values[i];
                                    if (
                                        typeof value !== "number" ||
                                        !isFinite(value)
                                    )
                                        continue;
                                    if (value < low) low = value;
                                    if (value > high) high = value;
                                }
                            }
                            if (!isFinite(low) || !isFinite(high)) return;
                            const pad =
                                (high - low || Math.abs(high) || 1) * 0.08;
                            const axis =
                                index === 0 ? "yaxis" : `yaxis${index + 1}`;
                            update[`${axis}.range`] = [low - pad, high + pad];
                        });
                        if (!Object.keys(update).length) return;
                        rescaling = true;
                        Plotly.relayout(div, update).then(function () {
                            rescaling = false;
                        });
                    }

                    radios.forEach(function (radio) {
                        radio.onchange = function () {
                            selected = radio.value;
                            draw();
                        };
                    });
                    // Attached only once the plot exists: Plotly gives the
                    // div its event emitter during the first draw, and calling
                    // .on before that throws — which used to land in the catch
                    // below and hide the whole section.
                    let listening = false;
                    function watchRelayout() {
                        if (listening || typeof (div as any).on !== "function")
                            return;
                        listening = true;
                        (div as any).on(
                            "plotly_relayout",
                            function (event: any) {
                                // The three x axes are linked, and a zoom reports
                                // whichever one the pointer was over — xaxis2 and
                                // xaxis3 as readily as xaxis.
                                const lower = Object.keys(event).find((key) =>
                                    /^xaxis\d*\.range\[0\]$/.test(key)
                                );
                                const upper = Object.keys(event).find((key) =>
                                    /^xaxis\d*\.range\[1\]$/.test(key)
                                );
                                const autoranged = Object.keys(event).some(
                                    (key) => /^xaxis\d*\.autorange$/.test(key)
                                );
                                const movedByHand = !applying && !rescaling;
                                if (!lower && !upper) {
                                    if (autoranged) {
                                        if (movedByHand) clearPreset();
                                        rescaleAxes();
                                    }
                                    return;
                                }
                                // A zoom or a pan leaves the range buttons describing a
                                // window that is no longer on screen. Clear them, so the
                                // one just left can be picked again — clicking a radio
                                // that is still checked fires nothing.
                                if (movedByHand) clearPreset();
                                rescaleAxes(
                                    new Date(event[lower as string]).getTime(),
                                    new Date(event[upper as string]).getTime()
                                );
                            }
                        );
                    }
                    // The Economics tab is hidden at load, so the first draw
                    // has no box to measure and comes out empty. Resizing is
                    // not enough for a grid layout; it has to be drawn again
                    // once the container has a real width.
                    if (typeof ResizeObserver !== "undefined") {
                        let lastWidth = 0;
                        new ResizeObserver(function () {
                            const width = container.clientWidth;
                            if (!width || width === lastWidth) return;
                            lastWidth = width;
                            draw();
                        }).observe(container);
                    }
                })
                .catch(hideSection);
        });
}

function initBatteryCyclesPlot() {
    if (!document.body.classList.contains("solution-b")) return;
    document
        .querySelectorAll<HTMLElement>(".battery-cycles-plot-div")
        .forEach(function (container) {
            const div = container.querySelector<HTMLElement>("[data-chart]");
            if (!div) return;
            const projectId = div.dataset.project_id;
            const chart = div.dataset.chart;
            if (!projectId || !chart) return;

            // Results calculated before this chart existed have no data file,
            // and a project can hold a battery that never ran. Either way, hide
            // the section rather than leave an empty frame.
            function hideSection() {
                const section = container.closest("section");
                if (!section) return;
                section.classList.add("d-none");
                const link = document.querySelector(
                    `[data-results-scroll][href="#${section.id}"]`
                );
                link?.closest("li")?.classList.add("d-none");
            }

            fetch(`/api/v1/projects/${projectId}/charts/${chart}`)
                .then((response) => (response.ok ? response.json() : null))
                .then(function (data) {
                    if (!data || !data.batteries || !data.batteries.length) {
                        hideSection();
                        return;
                    }
                    const modeRadios =
                        document.querySelectorAll<HTMLInputElement>(
                            `input[type=radio][name="${div.id}-mode"]`
                        );
                    const periodRadios =
                        document.querySelectorAll<HTMLInputElement>(
                            `input[type=radio][name="${div.id}-period"]`
                        );
                    // Read the defaults off whatever the markup has checked, so
                    // they live in one place rather than two.
                    let period =
                        Array.from(periodRadios).find((r) => r.checked)
                            ?.value ?? "week";
                    let perDay =
                        Array.from(modeRadios).find((r) => r.checked)?.value !==
                        "total";
                    function draw() {
                        if (!data[period]) return;
                        // A day per bar is already a per-day figure, so the
                        // switch has nothing to do: disable it rather than
                        // offer two buttons that draw the same chart.
                        const daily = period === "day";
                        modeRadios.forEach(function (radio) {
                            radio.disabled = daily;
                            radio
                                .closest("label")
                                ?.classList.toggle("disabled", daily);
                        });
                        Plotly.react(
                            div.id,
                            batteryCyclesTraces(data, period, perDay && !daily),
                            batteryCyclesLayout(data, period, perDay && !daily),
                            {
                                displayModeBar: false,
                                displaylogo: false,
                                responsive: true,
                            }
                        );
                    }
                    // Assigned rather than added: Bootstrap's button group
                    // announces the change with jQuery's .trigger("change"),
                    // which reaches the onchange property only.
                    periodRadios.forEach(function (radio) {
                        radio.onchange = function () {
                            period = radio.value;
                            draw();
                        };
                    });
                    modeRadios.forEach(function (radio) {
                        radio.onchange = function () {
                            perDay = radio.value === "per-day";
                            draw();
                        };
                    });
                    draw();

                    // The tab is hidden at load, so the first draw has no
                    // layout to measure; redraw once the container has a real
                    // width. See initPeriodPlots for the full reasoning.
                    if (typeof ResizeObserver !== "undefined") {
                        let lastWidth = 0;
                        new ResizeObserver(function () {
                            const width = container.clientWidth;
                            if (!width || width === lastWidth) return;
                            lastWidth = width;
                            Plotly.Plots.resize(div);
                        }).observe(container);
                    }
                })
                .catch(hideSection);
        });
}

/* ===================== Generated PDF report ============================
 *
 * The report's charts are Plotly figures, and Plotly draws in a browser and
 * nowhere else. Rather than put a headless browser on the server, the browser
 * already showing the report exports each figure and posts the images; the
 * server assembles the document around them. That keeps every chart defined
 * once, here, for both the screen and the page.
 *
 * Solution-B only: the button is not rendered for the original design.
 */

/** Milliseconds to wait for the report's charts before giving up on one. */
const REPORT_CHART_TIMEOUT_MS = 60000;

/**
 * Resolve once Plotly has drawn into ``div``, or after the timeout.
 *
 * Plotly draws asynchronously and offers no general "finished" event, and the
 * report's figures arrive down several different code paths. Watching the
 * element for the ``data`` Plotly attaches to it covers all of them without
 * touching the shared render pipeline -- and a chart that never arrives is
 * simply left out of the document rather than blocking it.
 */
function whenPlotDrawn(div: HTMLElement): Promise<boolean> {
    return new Promise(function (resolve) {
        const started = Date.now();
        (function poll() {
            const traces = (div as any).data;
            if (traces && traces.length) {
                resolve(true);
                return;
            }
            if (Date.now() - started > REPORT_CHART_TIMEOUT_MS) {
                resolve(false);
                return;
            }
            window.setTimeout(poll, 150);
        })();
    });
}

/**
 * Export one drawn chart as a PNG blob at print resolution.
 *
 * What is on the page is what the document gets: the time range, the period
 * buttons, the series the reader switched off. Nothing is re-ranged here — the
 * representative week is set on the chart when the page loads, so a reader who
 * then picks a different week gets the week they picked.
 *
 * The chart is cloned offscreen first and its type enlarged, the same trick the
 * per-chart download uses: type sized for a screen is unreadable once the figure
 * is placed at a fixed width on a page.
 */
async function reportChartAsPng(
    div: HTMLElement
): Promise<{ blob: Blob; range: string[] | null } | null> {
    const source = div as any;
    let clone: HTMLElement | null = null;
    try {
        clone = document.createElement("div");
        clone.style.position = "absolute";
        clone.style.left = "-5000px";
        document.body.appendChild(clone);
        await Plotly.newPlot(clone, source.data, source.layout);

        const layout: any = {
            paper_bgcolor: "#fff",
            plot_bgcolor: "#fff",
            font: { color: "#212529", family: "Poppins, sans-serif", size: 15 },
            margin: { t: 60, b: 90, l: 80, r: 40 },
            showlegend: true,
        };
        // A range slider is a control, not information; it wastes a fifth of the
        // figure on a page nobody can drag.
        ["xaxis", "xaxis2", "yaxis", "yaxis2"].forEach(function (axis) {
            const original = source.layout && source.layout[axis];
            if (!original) return;
            layout[axis] = { ...original, rangeslider: null };
            layout[axis].title = {
                ...(original.title || {}),
                font: { size: 16 },
            };
            layout[axis].tickfont = { size: 14 };
        });
        if (source.layout && source.layout.legend) {
            layout.legend = { ...source.layout.legend, font: { size: 14 } };
        }
        await Plotly.relayout(clone, layout);

        // 1000x525 at scale 2 is 2000px across, which is 299 dpi over the
        // 170 mm an A4 page leaves inside its margins.
        const dataUrl = await Plotly.toImage(clone, {
            format: "png",
            width: 1000,
            height: 525,
            scale: 2,
        });
        const response = await fetch(dataUrl);
        // The span actually exported, so the document can caption the figure
        // with what it shows rather than with what it was going to show.
        const axis =
            (clone as any)._fullLayout && (clone as any)._fullLayout.xaxis;
        const shown =
            axis && axis.range && axis.type === "date"
                ? axis.range.map((value: any) => String(value))
                : null;
        return { blob: await response.blob(), range: shown };
    } catch (err) {
        console.warn("Could not export chart", div.id, err);
        return null;
    } finally {
        if (clone) {
            try {
                Plotly.purge(clone);
            } catch (err) {
                /* the clone is going away regardless */
            }
            clone.remove();
        }
    }
}

/**
 * Narrow a chart to the span the report chose for it, on screen.
 *
 * The document shows the representative week; the page has to show the same
 * week, or the two disagree about what the reader is looking at. The exporter
 * applies the same range again at export time, which costs nothing and covers
 * a chart the user has since panned.
 */
function applyInitialChartRanges() {
    document
        .querySelectorAll<HTMLElement>("[data-initial-range-for]")
        .forEach(function (holder) {
            const id = holder.dataset.initialRangeFor;
            const span = (holder.dataset.initialRange || "").split("|");
            if (!id || span.length !== 2) {
                return;
            }
            const div = document.getElementById(id);
            if (!div) {
                return;
            }
            whenPlotDrawn(div).then(function (drawn) {
                if (!drawn) {
                    return;
                }
                Plotly.relayout(div, { "xaxis.range": span }).catch(function (
                    err
                ) {
                    console.warn("Could not set the report range on", id, err);
                });
            });
        });
}

/* ============ The report page as its own editor ============
 *
 * Everything a user can change is stored as an override, never as a copy of the
 * standard text: a section they left alone keeps the wording the software
 * ships, and improving that later improves every report that never overrode it.
 *
 * Nothing is written until Save is pressed, so an edit can be abandoned by
 * leaving the page — and the page says so, rather than saving silently.
 */

/** Read the report's edits out of the page, as the server stores them. */
function collectReportSettings(): Record<string, any> {
    const settings: Record<string, any> = { hidden: [] };

    document
        .querySelectorAll<HTMLElement>("[data-report-edit]")
        .forEach(function (field) {
            const path = field.dataset.reportEdit || "";
            const text = (field.innerText || "").trim();
            if (!path || !text) {
                return;
            }
            const [group, key] = path.split(".");
            if (key) {
                settings[group] = settings[group] || {};
                settings[group][key] = text;
            } else {
                settings[group] = text;
            }
        });

    document
        .querySelectorAll<HTMLElement>("[data-figure-hidden]")
        .forEach(function (marker) {
            const id = marker.dataset.figureHidden;
            if (id) settings.hidden.push(id);
        });
    document
        .querySelectorAll<HTMLElement>(".report-b__restore[data-figure-id]")
        .forEach(function (button) {
            const id = button.dataset.figureId;
            if (id && settings.hidden.indexOf(id) === -1)
                settings.hidden.push(id);
        });

    return settings;
}

function markReportDirty() {
    document.body.classList.add("report-b__dirty");
    const flag = document.getElementById("report-unsaved");
    if (flag) flag.hidden = false;
}

/**
 * Remove a figure from the report, or put one back.
 *
 * Both happen in the page and are only written on save. A removed figure keeps
 * its place in the document's order, so putting it back returns it where it
 * belongs rather than to the end.
 */
function initReportFigureControls() {
    const content = document.querySelector(".report-b__content");
    if (!content) return;

    content.addEventListener("click", function (event) {
        const target = event.target as HTMLElement;

        const remove = target.closest<HTMLElement>(".report-b__remove");
        if (remove) {
            const figure = remove.closest<HTMLElement>(".report-b__figure");
            const id = remove.dataset.figureId;
            if (!figure || !id) return;
            figure.hidden = true;
            figure.dataset.figureHidden = id;
            figure.setAttribute("data-figure-hidden", id);
            addRestoreButton(figure, id);
            markReportDirty();
            return;
        }

        const restore = target.closest<HTMLElement>(".report-b__restore");
        if (restore) {
            const id = restore.dataset.figureId;
            const figure = document.querySelector<HTMLElement>(
                `.report-b__figure[data-figure-id="${id}"]`
            );
            if (figure) {
                figure.hidden = false;
                figure.removeAttribute("data-figure-hidden");
                delete figure.dataset.figureHidden;
            }
            restore.remove();
            markReportDirty();
        }
    });
}

/** Offer a removed figure back, in its own section. */
function addRestoreButton(figure: HTMLElement, id: string) {
    const section = figure.closest("section");
    if (!section) return;
    let tray = section.querySelector<HTMLElement>(".report-b__removed");
    if (!tray) {
        tray = document.createElement("div");
        tray.className = "report-b__removed";
        tray.innerHTML =
            '<span class="report-b__removed-label">Add figure:</span>';
        section.appendChild(tray);
    }
    const title =
        figure
            .querySelector(".stacked-area-chart-header")
            ?.textContent?.trim() || "Figure";
    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn btn-secondary btn-sm report-b__restore";
    button.dataset.figureId = id;
    button.textContent = `+ ${title}`;
    tray.appendChild(button);
}

/** Placeholders for the editable fields, which are not form inputs. */
function initReportTextEditing() {
    const fields = Array.from(
        document.querySelectorAll<HTMLElement>("[data-report-edit]")
    );
    if (!fields.length) return;

    const refresh = (field: HTMLElement) => {
        field.dataset.empty = (field.innerText || "").trim() ? "false" : "true";
    };
    fields.forEach(function (field) {
        refresh(field);
        field.addEventListener("input", function () {
            refresh(field);
            markReportDirty();
        });
        // Paste as text: pasted markup would be stored and then rendered into a
        // document, and the document has its own type.
        field.addEventListener("paste", function (event: ClipboardEvent) {
            event.preventDefault();
            const text = event.clipboardData?.getData("text/plain") || "";
            document.execCommand("insertText", false, text);
        });
    });
}

function initReportLogo() {
    const input = document.getElementById(
        "report-logo-input"
    ) as HTMLInputElement;
    const image = document.getElementById("report-logo") as HTMLImageElement;
    const remove = document.getElementById("report-logo-remove");
    if (!input || !image) return;

    input.addEventListener("change", function () {
        const file = input.files && input.files[0];
        if (!file) return;
        image.src = URL.createObjectURL(file);
        image.hidden = false;
        if (remove) remove.hidden = false;
        delete image.dataset.removed;
        markReportDirty();
    });

    if (remove) {
        remove.addEventListener("click", function () {
            input.value = "";
            image.hidden = true;
            image.dataset.removed = "true";
            remove.hidden = true;
            markReportDirty();
        });
    }
}

function initReportSave() {
    const toolbar = document.querySelector<HTMLElement>(".report-b__toolbar");
    const button = document.getElementById("save-report");
    const status = document.getElementById("generate-report-pdf-status");
    if (!toolbar || !button) return;

    const setStatus = (text: string) => {
        if (status) status.textContent = text;
    };

    button.addEventListener("click", async function () {
        const input = document.getElementById(
            "report-logo-input"
        ) as HTMLInputElement | null;
        const image = document.getElementById(
            "report-logo"
        ) as HTMLImageElement;

        const form = new FormData();
        form.append("settings", JSON.stringify(collectReportSettings()));
        if (image && image.dataset.removed === "true") {
            form.append("remove_logo", "true");
        } else if (input && input.files && input.files[0]) {
            form.append("logo", input.files[0], input.files[0].name);
        }

        (button as HTMLButtonElement).disabled = true;
        setStatus("Saving…");
        try {
            const csrfToken = (
                document.querySelector(
                    "[name=csrfmiddlewaretoken]"
                ) as HTMLInputElement | null
            )?.value;
            const response = await fetch(toolbar.dataset.saveUrl as string, {
                method: "POST",
                headers: { "X-CSRFToken": csrfToken || "" },
                body: form,
            });
            if (!response.ok) {
                throw new Error(await response.text());
            }
            document.body.classList.remove("report-b__dirty");
            const flag = document.getElementById("report-unsaved");
            if (flag) flag.hidden = true;
            setStatus("Saved.");
            window.setTimeout(() => setStatus(""), 4000);
        } catch (err) {
            console.error("Could not save the report", err);
            setStatus("The report could not be saved.");
            Sentry.captureException(err);
        } finally {
            (button as HTMLButtonElement).disabled = false;
        }
    });

    // Leaving with unsaved edits loses them, so say so rather than let it happen
    // quietly.
    window.addEventListener("beforeunload", function (event) {
        if (!document.body.classList.contains("report-b__dirty")) return;
        event.preventDefault();
        event.returnValue = "";
    });
}

function initReportPdf() {
    const button = document.getElementById("generate-report-pdf");
    if (!button) {
        return;
    }
    const status = document.getElementById("generate-report-pdf-status");
    const setStatus = function (text: string) {
        if (status) {
            status.textContent = text;
        }
    };

    button.addEventListener("click", async function () {
        // What the report asks for, in its own order, from the definition the
        // server built. Falls back to every report chart on the page, which is
        // what the page carried before the definition existed.
        let wanted: {
            key: string;
            chart_id: string;
            kind?: string;
            x_range?: [string, string];
        }[];
        try {
            wanted = JSON.parse(
                document.getElementById("report-figures")?.textContent || "[]"
            );
        } catch (err) {
            wanted = [];
        }
        if (!wanted.length) {
            wanted = Array.from(
                document.querySelectorAll<HTMLElement>(
                    '[data-for_report="true"]'
                )
            ).map((div) => ({ key: div.id, chart_id: div.id }));
        }

        (button as HTMLButtonElement).disabled = true;
        button.classList.add("is-working");
        const originalLabel = button.dataset.label || button.textContent || "";
        button.dataset.label = originalLabel;
        button.textContent = "Generating…";

        try {
            const divs = wanted.map((figure) =>
                document.getElementById(figure.chart_id)
            );
            setStatus(`Waiting for ${divs.length} charts to finish drawing…`);
            const drawn = await Promise.all(
                divs.map((div) =>
                    div ? whenPlotDrawn(div) : Promise.resolve(false)
                )
            );

            const form = new FormData();
            const ranges: Record<string, string[]> = {};
            let count = 0;
            for (let i = 0; i < wanted.length; i++) {
                const div = divs[i];
                if (!div || !drawn[i]) {
                    continue;
                }
                setStatus(
                    `Exporting ${i + 1} of ${wanted.length}: ${wanted[
                        i
                    ].chart_id
                        .replace(/-plot$/, "")
                        .replace(/-/g, " ")}…`
                );
                const exported = await reportChartAsPng(div);
                if (!exported) {
                    continue;
                }
                // Posted under the key the document looks the image up by,
                // not the chart id: a chart can appear twice with different
                // ranges, because the representative week reuses the dispatch
                // chart.
                const key = wanted[i].key || wanted[i].chart_id;
                form.append(key, exported.blob, `${key}.png`);
                if (exported.range) {
                    ranges[key] = exported.range;
                }
                count += 1;
            }

            form.append("ranges", JSON.stringify(ranges));
            setStatus(`Building the document from ${count} charts…`);
            const csrfToken = (
                document.querySelector(
                    "[name=csrfmiddlewaretoken]"
                ) as HTMLInputElement | null
            )?.value;
            const response = await fetch(button.dataset.url as string, {
                method: "POST",
                headers: { "X-CSRFToken": csrfToken || "" },
                body: form,
            });
            if (!response.ok) {
                throw new Error(`The server returned ${response.status}`);
            }

            const pdf = await response.blob();
            const url = URL.createObjectURL(pdf);
            const link = document.createElement("a");
            link.href = url;
            link.download = button.dataset.filename || "report.pdf";
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            setStatus("The report has been downloaded.");
            window.setTimeout(() => setStatus(""), 6000);
        } catch (err) {
            console.error("Could not generate the report", err);
            setStatus("The report could not be generated. Please try again.");
            Sentry.captureException(err);
        } finally {
            (button as HTMLButtonElement).disabled = false;
            button.classList.remove("is-working");
            button.textContent = originalLabel;
        }
    });
}

function initPeriodPlots() {
    if (!document.body.classList.contains("solution-b")) return;
    document
        .querySelectorAll<HTMLElement>(".period-plot-div")
        .forEach(function (container) {
            const div = container.querySelector<HTMLElement>("[data-chart]");
            if (!div) return;
            const projectId = div.dataset.project_id;
            const chart = div.dataset.chart;
            if (!projectId || !chart) return;

            // Results calculated before this chart existed have no data file.
            // Hide the section and its scroll-nav entry rather than leave an
            // empty frame behind; recalculating the project produces the file.
            function hideSection() {
                const section = container.closest("section");
                if (!section) return;
                section.classList.add("d-none");
                const link = document.querySelector(
                    `[data-results-scroll][href="#${section.id}"]`
                );
                link?.closest("li")?.classList.add("d-none");
            }

            fetch(`/api/v1/projects/${projectId}/charts/${chart}`)
                .then((response) => (response.ok ? response.json() : null))
                .then(function (data) {
                    if (!data || !data.streams || !data.streams.length) {
                        hideSection();
                        return;
                    }
                    let currentPeriod = "week";
                    let perUnit = false;
                    // Only offered when there is something to divide by: a
                    // system with no battery has no rated power.
                    const basisToggle = container.querySelector<HTMLElement>(
                        "[data-per-unit-toggle]"
                    );
                    if (basisToggle && data.rated_power_kw) {
                        basisToggle.hidden = false;
                    }
                    function draw(period: string) {
                        if (!data[period]) return;
                        currentPeriod = period;
                        Plotly.react(
                            div.id,
                            periodPlotTraces(data, period, perUnit),
                            periodPlotLayout(data, period, perUnit),
                            {
                                displayModeBar: false,
                                displaylogo: false,
                                responsive: true,
                            }
                        );
                    }
                    const radios = document.querySelectorAll<HTMLInputElement>(
                        `input[type=radio][name="${div.id}-period"]`
                    );
                    radios.forEach(function (radio) {
                        // Assigned, not addEventListener: Bootstrap's button
                        // group checks the radio itself and announces it with
                        // jQuery's .trigger("change"), which calls the onchange
                        // property but never reaches addEventListener listeners.
                        // The zoom buttons are wired the same way.
                        radio.onchange = function () {
                            draw(radio.value);
                        };
                    });
                    const basisRadios =
                        document.querySelectorAll<HTMLInputElement>(
                            `input[type=radio][name="${div.id}-basis"]`
                        );
                    basisRadios.forEach(function (radio) {
                        radio.onchange = function () {
                            perUnit = radio.value === "per-kw";
                            draw(currentPeriod);
                        };
                    });
                    const checked = Array.from(radios).find((r) => r.checked);
                    draw(checked ? checked.value : "week");

                    // The Economics tab is hidden at load, so the first draw has
                    // no layout to work against: Plotly falls back to 700px and
                    // places the legend where the tick labels end up, so the two
                    // overlap once the tab is opened. Draw again the first time
                    // the container has a real width, and on any later reflow.
                    // Plotly.react alone would not help: it keeps the width it
                    // measured on the first draw. Plots.resize is what re-reads
                    // the container.
                    if (typeof ResizeObserver !== "undefined") {
                        let lastWidth = 0;
                        new ResizeObserver(function () {
                            const width = container.clientWidth;
                            if (!width || width === lastWidth) return;
                            lastWidth = width;
                            Plotly.Plots.resize(div);
                        }).observe(container);
                    }
                })
                .catch(hideSection);
        });
}

/*
 * Undo buttons that discard unsaved edits and restore the last saved values.
 * One sits next to the component Save button, one next to the settings Save
 * button; each is enabled only while its form has unsaved changes.
 */
function initUndoButtons() {
    // Component Undo: delegated so it survives htmx re-renders of #components.
    document.addEventListener("click", function (e: Event) {
        const target = e.target as HTMLElement;
        const undoBtn =
            target &&
            (target.closest("#undo-asset-values-button") as HTMLButtonElement);
        if (!undoBtn || undoBtn.disabled) return;
        e.preventDefault();
        revertComponentEdits();
    });

    // Settings Undo: the settings form is not swapped, so bind directly. Track
    // dirtiness live to toggle the button, and reset the form on click.
    const settingsForm = document.getElementById(
        "settings-form"
    ) as HTMLFormElement;
    const settingsUndo = document.getElementById(
        "settings-undo-button"
    ) as HTMLButtonElement;
    if (settingsForm && settingsUndo) {
        const refresh = () => {
            if (settingsUndo.dataset.projectLocked === "true") return;
            settingsUndo.disabled = !isFormDirty(settingsForm);
        };
        settingsForm.addEventListener("input", refresh);
        settingsForm.addEventListener("change", refresh);
        settingsUndo.addEventListener("click", function () {
            settingsForm.reset();
            // reset() doesn't fire input/change, so notify dependent widgets
            // (toggle groups, unit displays) and our own dirty tracking.
            settingsForm
                .querySelectorAll("input, select, textarea")
                .forEach((el) => {
                    el.dispatchEvent(new Event("change", { bubbles: true }));
                    el.dispatchEvent(new Event("input", { bubbles: true }));
                });
            refresh();
        });
        refresh();
    }

    // Component edit form Undo (in the edit modal and on the standalone
    // component page). The form is loaded/replaced via htmx, so everything is
    // delegated. The button starts disabled and the form is clean on load, so
    // it enables only once a field is edited.
    const refreshComponentFormUndo = () => {
        const form = document.getElementById(
            "component-form"
        ) as HTMLFormElement;
        const undo = document.getElementById(
            "component-undo-button"
        ) as HTMLButtonElement;
        if (form && undo) undo.disabled = !isFormDirty(form);
    };
    const onComponentFormEdit = (e: Event) => {
        const t = e.target as HTMLElement;
        if (t && t.closest && t.closest("#component-form")) {
            refreshComponentFormUndo();
        }
    };
    document.addEventListener("input", onComponentFormEdit);
    document.addEventListener("change", onComponentFormEdit);
    // Enter in a text field submits the form it is in, which here closes the
    // component and reloads the page from under the person typing. A component
    // is saved with its Save button, so Enter has nothing to do: it leaves the
    // field instead, which commits the value to any listener watching for a
    // change without throwing the page away.
    document.addEventListener("keydown", function (e: KeyboardEvent) {
        if (e.key !== "Enter" || e.shiftKey) return;
        const target = e.target as HTMLElement | null;
        if (!target?.closest?.("#component-form")) return;
        // A textarea takes its newline, and a button or a link is being used.
        if (target.matches("textarea, button, a, [type=submit]")) return;
        e.preventDefault();
        (target as HTMLInputElement).blur?.();
    });
    document.addEventListener("click", function (e: Event) {
        const t = e.target as HTMLElement;
        const undo =
            t && (t.closest("#component-undo-button") as HTMLButtonElement);
        if (!undo || undo.disabled) return;
        e.preventDefault();
        const form = document.getElementById(
            "component-form"
        ) as HTMLFormElement;
        if (!form) return;
        form.reset();
        // reset() doesn't fire input/change; notify dependent widgets (output
        // plot, matrix fields, toggles) and our own dirty tracking.
        form.querySelectorAll("input, select, textarea").forEach((el) => {
            el.dispatchEvent(new Event("change", { bubbles: true }));
            el.dispatchEvent(new Event("input", { bubbles: true }));
        });
        refreshComponentFormUndo();
    });
    // Re-sync after htmx swaps the form in (modal open) or re-renders it
    // (validation errors).
    document.addEventListener("htmx:afterSettle", refreshComponentFormUndo);
}

function initDetailPageAssetListeners() {
    document.addEventListener("input", function (e: Event) {
        const target = e.target as HTMLElement;
        if (!target) return;
        const name = target.getAttribute("name") || "";
        const id = target.id || "";
        const isMaxInput = name.startsWith("asset-max-");
        const isMinInput = name.startsWith("asset-min-");
        const isBaseInput = name.startsWith("asset-base-");
        const isUcInput = id.startsWith("unit-capacity-");
        if (!isMaxInput && !isMinInput && !isBaseInput && !isUcInput) return;

        let assetId: string;
        if (isUcInput) {
            assetId = id.replace("unit-capacity-", "");
        } else {
            // names are "asset-min-{uuid}", "asset-max-{uuid}", "asset-base-{uuid}"
            assetId = name.split("-").slice(2).join("-");
        }

        // Update capacity figure spans
        updateCapacityFigures(assetId);

        // Any dimensioning edit (min/max/base/unit-capacity) invalidates this
        // component's optimal design: hide the optimal figures and show a dash
        // until the project is recalculated. Reverting all edits restores it.
        updateAssetOptimalStale(assetId);

        // For max or unit capacity changes: re-render the peak demand chart
        if (isMaxInput || isUcInput) {
            detailAssetUserEdited = true;
            // Update chart title to reflect we're now showing max installed capacity
            const titleEl = document.getElementById("peak-demand-chart-title");
            if (titleEl) {
                titleEl.textContent = "Peak demand vs. max installed capacity";
            }
            getPlotState("peak-demand-vs-installed-capacity").then((state) => {
                if (state) update(state);
            });
        }

        checkComponentFormDirty();
    });
}

/*
 * Initialize the module.
 */

/**
 * The simulated period: the dates decide, and the project lifetime steps aside.
 *
 * Filling both dates in is what makes them govern — there is nothing else to
 * set. While they do, the lifetime input is greyed out and read-only, because
 * it no longer decides anything; clear either date and it applies again. A period
 * shorter than a year is not one, so the lifetime keeps governing until the
 * dates make sense.
 *
 * A page can carry the dates twice, since the wizard's quick strip mirrors the
 * settings tab, so one edit is reflected in every copy: setting a value from
 * script fires no event of its own.
 *
 * The note under the dates says what the period still needs — one date filled
 * in without the other, or less than a year between them — and, once it is
 * valid, what happens where it falls outside the uploaded data. Both ends can
 * be outside at once.
 *
 * Solution B only. The original interface renders none of these controls.
 */
function initSimulationPeriod() {
    if (!document.body.classList.contains("solution-b")) {
        return;
    }
    const blocks = Array.from(
        document.querySelectorAll<HTMLElement>("[data-horizon-block]")
    );
    if (blocks.length === 0) {
        return;
    }

    const A_YEAR_IN_DAYS = 365;

    const datesOf = (block: HTMLElement) => ({
        start: block.querySelector<HTMLInputElement>(
            'input[name$="start_date"], input[id$="simulation-start"]'
        ),
        end: block.querySelector<HTMLInputElement>(
            'input[name$="end_date"], input[id$="simulation-end"]'
        ),
    });

    /** Both dates given, and far enough apart to be a period at all. */
    const governs = (block: HTMLElement): boolean => {
        const { start, end } = datesOf(block);
        if (!start?.value || !end?.value) {
            return false;
        }
        const days =
            (Date.parse(end.value) - Date.parse(start.value)) / 86400000;
        return days >= A_YEAR_IN_DAYS;
    };

    /**
     * What the period needs, or what it will do — whichever applies.
     *
     * The only writer of the note, which is what keeps it honest: everything it
     * says is derived from the fields as they stand, so emptying them says
     * nothing rather than leaving the last complaint on screen.
     *
     * A half-entered date is the common trap: a date input whose day is missing
     * has an empty value and reports ``badInput``, so the page can say so while
     * the user is still looking at it rather than leaving it to the save.
     */
    const describePeriod = (block: HTMLElement) => {
        const note = block.querySelector<HTMLElement>("[data-horizon-note]");
        if (!note) {
            return;
        }
        const { start, end } = datesOf(block);
        // Only while the field is being edited. A date input can go on
        // reporting a bad entry after it looks empty, and a complaint about a
        // field nobody is in is a complaint about nothing the user can see.
        const incomplete = [start, end].some(
            (input) =>
                input?.validity.badInput && document.activeElement === input
        );
        const half =
            Boolean(start?.value) !== Boolean(end?.value) &&
            !start?.validity.badInput &&
            !end?.validity.badInput;

        const problems: string[] = [];
        if (incomplete) {
            problems.push(
                "One of the dates is incomplete — a date needs a day, a month and a year."
            );
        } else if (half) {
            problems.push(
                "Give a start and an end date, or leave both empty to use the project lifetime."
            );
        } else if (start?.value && end?.value) {
            const days =
                (Date.parse(end.value) - Date.parse(start.value)) / 86400000;
            if (days < A_YEAR_IN_DAYS) {
                problems.push(
                    "The simulated period is at least one year long."
                );
            }
        }
        if (problems.length > 0) {
            note.textContent = problems.join(" ");
            note.classList.add("is-problem");
            note.hidden = false;
            return;
        }

        // Only a multi-year input has a period to fall outside of; a single year
        // is reproduced over the horizon however long it is, so nothing is
        // outside anything.
        const from = block.dataset.dataStart;
        const to = block.dataset.dataEnd;
        const messages: string[] = [];
        if (from && to) {
            if (start?.value && start.value < from) {
                messages.push(
                    `The period starts before the uploaded data, which begins on ${from}. ` +
                        `The simulation starts there instead and runs for the length you asked for.`
                );
            }
            if (end?.value && end.value > to) {
                messages.push(
                    `The period reaches past the uploaded data, which ends on ${to}. ` +
                        `The last year of it is repeated to fill the rest.`
                );
            }
        }
        note.textContent = messages.join(" ");
        note.classList.remove("is-problem");
        note.hidden = messages.length === 0;
    };

    /**
     * A date to open an empty picker on, so a day is never left unset.
     *
     * The native control lets a user pick a month and a year and walk away
     * without a day. What comes back is not a partial date but no date at all —
     * an empty value and ``badInput`` — which reads from the page as though the
     * field was never touched. Starting from a real date means every later
     * choice in the picker keeps a complete one.
     */
    const openingDate = (
        block: HTMLElement,
        which: "start" | "end"
    ): string => {
        const { start } = datesOf(block);
        const from =
            block.dataset.dataStart ?? new Date().toISOString().slice(0, 10);
        if (which === "start") {
            return from;
        }
        const lifetime = (
            block.parentElement ?? document
        ).querySelector<HTMLInputElement>("[data-horizon-lifetime]");
        const years = Number(lifetime?.value) || 10;
        const begins = new Date(start?.value || from);
        begins.setFullYear(begins.getFullYear() + years);
        return begins.toISOString().slice(0, 10);
    };

    /**
     * Empty a date field so that the control agrees it is empty.
     *
     * Assigning an empty value is not enough: a date input holding a
     * half-entered date keeps reporting ``badInput`` afterwards, so the page
     * goes on describing an incomplete date while the box looks blank. Only
     * rebuilding the control clears that state — which is why an Undo, which
     * resets the whole form, was the one thing that made the message go away.
     */
    const clearDate = (input: HTMLInputElement) => {
        input.type = "text";
        input.value = "";
        input.type = "date";
    };

    const apply = () => {
        const onDates = blocks.some(governs);
        blocks.forEach((block) => {
            // The lifetime input is a sibling of the block, not a child: it is
            // an ordinary settings row rendered above it.
            const lifetime = (
                block.parentElement ?? document
            ).querySelector<HTMLInputElement>("[data-horizon-lifetime]");
            if (lifetime) {
                // Read-only rather than disabled. A disabled input is not
                // submitted, so the lifetime went missing from the POST and the
                // form rejected it as required -- which is what a user saw on
                // saving a project that had dates and a lifetime both set.
                lifetime.readOnly = onDates;
                lifetime
                    .closest(".settings-field-row, .css-field")
                    ?.classList.toggle("is-inactive", onDates);
            }
            describePeriod(block);
        });
    };

    blocks.forEach((block) => {
        const { start, end } = datesOf(block);
        (
            [
                [start, "start"],
                [end, "end"],
            ] as Array<[HTMLInputElement | null, "start" | "end"]>
        ).forEach(([input, which]) => {
            if (!input) {
                return;
            }
            input.addEventListener("change", apply);
            input.addEventListener("input", () => {
                block.dataset.periodEdited = "yes";
                apply();
            });
            // Opening an empty picker starts it on a real date, so leaving it
            // can only ever produce a complete one — but only until the user
            // has edited the dates. Otherwise emptying a field would be undone
            // by the very click needed to do it, and going back to the project
            // lifetime would be impossible.
            input.addEventListener("click", () => {
                const edited = block.dataset.periodEdited === "yes";
                if (!edited && !input.value && !input.validity.badInput) {
                    input.value = openingDate(block, which);
                    apply();
                }
            });
            // And an entry left half-made does not survive the field: an empty
            // box is a state the page can explain, a half-typed one is not. The
            // field emptying is the feedback — a message saying it happened
            // would still be on screen after the fields were put right, because
            // it describes the past rather than the state.
            input.addEventListener("blur", () => {
                if (input.validity.badInput) {
                    clearDate(input);
                    apply();
                }
            });
        });
    });

    apply();

    // Delegated, so the block keeps working if the page re-renders it.
    ["input", "change", "focusout"].forEach((event) => {
        document.addEventListener(event, (e) => {
            const target = e.target as HTMLElement | null;
            if (target?.matches?.('[data-horizon-block] input[type="date"]')) {
                apply();
            }
        });
    });
}

function initDocumentationSearch() {
    if (!document.body.classList.contains("solution-b")) return;
    const box = document.querySelector(
        ".docs__search-input"
    ) as HTMLInputElement | null;
    const panel = document.getElementById("docs-search-results");
    if (!box || !panel) return;

    // Which result the arrow keys are on. Reset whenever HTMX swaps a new set
    // of results in, since the old position means nothing against a new list.
    let at = -1;
    const results = () =>
        Array.from(panel.querySelectorAll(".docs__result a")) as HTMLElement[];

    function mark(next: number) {
        const links = results();
        links.forEach((link, index) =>
            link.classList.toggle("is-selected", index === next)
        );
        at = next;
        if (links[next]) links[next].scrollIntoView({ block: "nearest" });
    }

    panel.addEventListener("htmx:afterSwap", () => mark(-1));

    box.addEventListener("keydown", function (event) {
        const links = results();
        if (event.key === "ArrowDown" && links.length) {
            event.preventDefault();
            mark((at + 1) % links.length);
        } else if (event.key === "ArrowUp" && links.length) {
            event.preventDefault();
            mark(at <= 0 ? links.length - 1 : at - 1);
        } else if (event.key === "Enter") {
            // Nothing picked yet means the first one: a reader who types and
            // presses return wants the best answer, not a reload of the page.
            const going = links[at < 0 ? 0 : at];
            if (going) {
                event.preventDefault();
                going.click();
            }
        } else if (event.key === "Escape") {
            box.value = "";
            panel.innerHTML = "";
            box.blur();
        }
    });

    // Slash focuses the box, the way it does in a manual anyone has read
    // before -- but not while something else is being typed into.
    document.addEventListener("keydown", function (event) {
        const target = event.target as HTMLElement | null;
        const typing =
            target &&
            (target.tagName === "INPUT" ||
                target.tagName === "TEXTAREA" ||
                target.isContentEditable);
        if (event.key === "/" && !typing) {
            event.preventDefault();
            box.focus();
            box.select();
        }
    });
}

// ---------------------------------------------------------------------------
// The demand-charge editor (Market, Solution B only).
//
// A period list (name / colour / price) and two twelve-by-twenty-four schedules
// -- weekdays and weekends -- saying which period each hour of each month falls
// in. The schedules are painted with a brush: pick a period, then click or drag
// across the grid, or click a month row or an hour column to paint the whole
// line. State is mirrored into three hidden inputs the save path reads, so the
// grid is the only thing that writes these fields. Gated on body.solution-b.
// ---------------------------------------------------------------------------

type DemandPeriod = { name: string; colour: string; price: number };

const DEMAND_MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

// Fallback colours for periods that arrive without one, and for newly added
// periods. Distinct hues so adjacent periods read apart on the grid.
const DEMAND_COLOURS = [
    "#4b5563",
    "#2563eb",
    "#dc2626",
    "#16a34a",
    "#d97706",
    "#7c3aed",
    "#0891b2",
    "#db2777",
];

function readJsonScript(id: string, fallback: any): any {
    const el = document.getElementById(id);
    if (!el || !el.textContent) return fallback;
    try {
        return JSON.parse(el.textContent);
    } catch (e) {
        return fallback;
    }
}

function initDemandChargeEditor(root?: HTMLElement) {
    if (!document.body.classList.contains("solution-b")) return;
    const scope: ParentNode = root || document;
    const editor = scope.querySelector(
        "#demand-charge-editor"
    ) as HTMLElement | null;
    if (!editor) return;

    const periodsBody = document.getElementById(
        "demand-periods-body"
    ) as HTMLElement | null;
    const palette = document.getElementById(
        "demand-palette"
    ) as HTMLElement | null;
    const gridWeekday = document.getElementById(
        "demand-grid-weekday"
    ) as HTMLElement | null;
    const gridWeekend = document.getElementById(
        "demand-grid-weekend"
    ) as HTMLElement | null;
    const periodsInput = document.getElementById(
        "demand-periods-input"
    ) as HTMLInputElement | null;
    const weekdayInput = document.getElementById(
        "demand-schedule-weekday-input"
    ) as HTMLInputElement | null;
    const weekendInput = document.getElementById(
        "demand-schedule-weekend-input"
    ) as HTMLInputElement | null;
    const enabledBox = document.querySelector(
        'input[name="demand_charges_enabled"]'
    ) as HTMLInputElement | null;
    if (
        !periodsBody ||
        !palette ||
        !gridWeekday ||
        !gridWeekend ||
        !periodsInput ||
        !weekdayInput ||
        !weekendInput
    ) {
        return;
    }

    // --- State, from the embedded json_script blocks ---
    let periods: DemandPeriod[] = (
        readJsonScript("demand-periods-data", []) as any[]
    ).map((p, i) => ({
        name: String(p.name ?? `Period ${i + 1}`),
        colour: String(p.colour || DEMAND_COLOURS[i % DEMAND_COLOURS.length]),
        price: Number(p.price) || 0,
    }));
    if (periods.length === 0) {
        periods = [{ name: "Off-peak", colour: DEMAND_COLOURS[0], price: 0 }];
    }
    const schedules: { [k: string]: number[][] } = {
        weekday: normaliseGrid(
            readJsonScript("demand-schedule-weekday-data", [])
        ),
        weekend: normaliseGrid(
            readJsonScript("demand-schedule-weekend-data", [])
        ),
    };
    let activePeriod = 0;

    // The undo stack: a snapshot of both schedules taken before each change
    // (one drag, one row, one column, one copy). Undo pops the last one back.
    const history: { weekday: number[][]; weekend: number[][] }[] = [];
    const undoBtn = document.getElementById(
        "demand-undo"
    ) as HTMLButtonElement | null;

    function snapshotGrids() {
        return {
            weekday: schedules.weekday.map((row) => row.slice()),
            weekend: schedules.weekend.map((row) => row.slice()),
        };
    }

    function pushHistory() {
        history.push(snapshotGrids());
        if (history.length > 200) history.shift();
        updateUndo();
    }

    function updateUndo() {
        if (undoBtn) undoBtn.disabled = history.length === 0;
    }

    function undo() {
        const prev = history.pop();
        if (!prev) return;
        schedules.weekday = prev.weekday;
        schedules.weekend = prev.weekend;
        paintAllCells();
        serialise();
        updateUndo();
    }

    function normaliseGrid(raw: any): number[][] {
        const grid: number[][] = [];
        for (let m = 0; m < 12; m++) {
            const row: number[] = [];
            const src =
                Array.isArray(raw) && Array.isArray(raw[m]) ? raw[m] : [];
            for (let h = 0; h < 24; h++) {
                const v = parseInt(src[h], 10);
                row.push(Number.isFinite(v) ? v : 0);
            }
            grid.push(row);
        }
        return grid;
    }

    function colourOf(period: number): string {
        const p = periods[period];
        return p ? p.colour : "#e5e7eb";
    }

    function serialise() {
        periodsInput.value = JSON.stringify(periods);
        weekdayInput.value = JSON.stringify(schedules.weekday);
        weekendInput.value = JSON.stringify(schedules.weekend);
    }

    // --- The period list ---
    function renderPeriods() {
        periodsBody.innerHTML = "";
        periods.forEach((period, index) => {
            const row = document.createElement("tr");

            const colourCell = document.createElement("td");
            colourCell.className = "demand-col-colour";
            const colour = document.createElement("input");
            colour.type = "color";
            colour.value = period.colour;
            colour.className = "demand-colour-input";
            colour.addEventListener("input", () => {
                period.colour = colour.value;
                paintAllCells();
                renderPalette();
                serialise();
            });
            colourCell.appendChild(colour);

            const nameCell = document.createElement("td");
            nameCell.className = "demand-col-name";
            const name = document.createElement("input");
            name.type = "text";
            name.className = "form-control demand-name-input";
            name.value = period.name;
            name.addEventListener("input", () => {
                period.name = name.value;
                renderPalette();
                serialise();
            });
            nameCell.appendChild(name);

            const priceCell = document.createElement("td");
            priceCell.className = "demand-col-price";
            const price = document.createElement("input");
            price.type = "number";
            price.step = "any";
            price.min = "0";
            price.className = "form-control demand-price-input";
            price.value = String(period.price);
            price.addEventListener("input", () => {
                period.price = Number(price.value) || 0;
                serialise();
            });
            priceCell.appendChild(price);

            const removeCell = document.createElement("td");
            removeCell.className = "demand-col-remove";
            const remove = document.createElement("button");
            remove.type = "button";
            remove.className = "btn btn-sm btn-link demand-period-remove";
            remove.innerHTML = '<i class="tim-icons icon-simple-remove"></i>';
            remove.title = "Remove period";
            // The first period is the schedule's fallback (every cell that
            // referenced a removed period falls back to it), so it always stays.
            remove.disabled = periods.length <= 1 || index === 0;
            remove.addEventListener("click", () => removePeriod(index));
            removeCell.appendChild(remove);

            row.appendChild(colourCell);
            row.appendChild(nameCell);
            row.appendChild(priceCell);
            row.appendChild(removeCell);
            periodsBody.appendChild(row);
        });
    }

    function addPeriod() {
        const i = periods.length;
        periods.push({
            name: `Period ${i + 1}`,
            colour: DEMAND_COLOURS[i % DEMAND_COLOURS.length],
            price: 0,
        });
        activePeriod = i;
        renderPeriods();
        renderPalette();
        serialise();
    }

    function removePeriod(index: number) {
        if (periods.length <= 1 || index === 0) return;
        periods.splice(index, 1);
        // Re-map the grids: the removed period falls back to off-peak (0), and
        // any period above it shifts down one to keep pointing at the same row.
        for (const key of ["weekday", "weekend"]) {
            const grid = schedules[key];
            for (let m = 0; m < 12; m++) {
                for (let h = 0; h < 24; h++) {
                    if (grid[m][h] === index) grid[m][h] = 0;
                    else if (grid[m][h] > index) grid[m][h] -= 1;
                }
            }
        }
        if (activePeriod === index) activePeriod = 0;
        else if (activePeriod > index) activePeriod -= 1;
        renderPeriods();
        renderPalette();
        paintAllCells();
        serialise();
    }

    // --- The brush ---
    function renderPalette() {
        palette.innerHTML = "";
        periods.forEach((period, index) => {
            const swatch = document.createElement("button");
            swatch.type = "button";
            swatch.className =
                "demand-swatch" + (index === activePeriod ? " is-active" : "");
            swatch.style.backgroundColor = period.colour;
            swatch.title = period.name;
            const label = document.createElement("span");
            label.className = "demand-swatch-label";
            label.textContent = period.name;
            swatch.appendChild(label);
            swatch.addEventListener("click", () => {
                activePeriod = index;
                renderPalette();
            });
            palette.appendChild(swatch);
        });
    }

    // --- The grids ---
    let painting = false;

    function paintCell(key: string, m: number, h: number) {
        schedules[key][m][h] = activePeriod;
        const grid = key === "weekday" ? gridWeekday : gridWeekend;
        const cell = grid.querySelector(
            `[data-month="${m}"][data-hour="${h}"]`
        ) as HTMLElement | null;
        if (cell) cell.style.backgroundColor = colourOf(activePeriod);
    }

    function paintRow(key: string, m: number) {
        for (let h = 0; h < 24; h++) paintCell(key, m, h);
    }

    function paintColumn(key: string, h: number) {
        for (let m = 0; m < 12; m++) paintCell(key, m, h);
    }

    function paintAllCells() {
        for (const key of ["weekday", "weekend"]) {
            const grid = key === "weekday" ? gridWeekday : gridWeekend;
            grid.querySelectorAll<HTMLElement>(".demand-cell").forEach(
                (cell) => {
                    const m = Number(cell.dataset.month);
                    const h = Number(cell.dataset.hour);
                    cell.style.backgroundColor = colourOf(schedules[key][m][h]);
                }
            );
        }
    }

    function buildGrid(grid: HTMLElement, key: string) {
        grid.innerHTML = "";
        grid.classList.add("demand-grid-built");

        // Corner + hour headers.
        const corner = document.createElement("div");
        corner.className = "demand-grid-corner";
        grid.appendChild(corner);
        for (let h = 0; h < 24; h++) {
            const head = document.createElement("button");
            head.type = "button";
            head.className = "demand-hour-head";
            head.textContent = String(h);
            head.title = `Paint hour ${h}:00 across every month`;
            head.addEventListener("click", () => {
                pushHistory();
                paintColumn(key, h);
                serialise();
            });
            grid.appendChild(head);
        }

        // A label + 24 cells per month.
        for (let m = 0; m < 12; m++) {
            const label = document.createElement("button");
            label.type = "button";
            label.className = "demand-month-label";
            label.textContent = DEMAND_MONTHS[m];
            label.title = `Paint all of ${DEMAND_MONTHS[m]}`;
            label.addEventListener("click", () => {
                pushHistory();
                paintRow(key, m);
                serialise();
            });
            grid.appendChild(label);

            for (let h = 0; h < 24; h++) {
                const cell = document.createElement("div");
                cell.className = "demand-cell";
                cell.dataset.month = String(m);
                cell.dataset.hour = String(h);
                cell.style.backgroundColor = colourOf(schedules[key][m][h]);
                cell.addEventListener("mousedown", (e) => {
                    e.preventDefault();
                    // One drag is one undo step: snapshot before it starts.
                    pushHistory();
                    painting = true;
                    paintCell(key, m, h);
                });
                cell.addEventListener("mouseenter", () => {
                    if (painting) paintCell(key, m, h);
                });
                grid.appendChild(cell);
            }
        }
    }

    // Stop painting anywhere the mouse is released, and commit the result.
    const stopPainting = () => {
        if (painting) {
            painting = false;
            serialise();
        }
    };
    document.addEventListener("mouseup", stopPainting);

    // --- Enable / disable the whole editor with the switch ---
    function reflectEnabled() {
        const on = enabledBox ? enabledBox.checked : true;
        editor.classList.toggle("is-disabled", !on);
    }
    if (enabledBox) {
        enabledBox.addEventListener("change", reflectEnabled);
    }

    // --- Copy weekdays onto weekends ---
    const copyBtn = document.getElementById("demand-copy-weekday");
    if (copyBtn) {
        copyBtn.addEventListener("click", () => {
            pushHistory();
            schedules.weekend = schedules.weekday.map((row) => row.slice());
            paintAllCells();
            serialise();
        });
    }

    if (undoBtn) undoBtn.addEventListener("click", undo);

    const addBtn = document.getElementById("demand-period-add");
    if (addBtn) addBtn.addEventListener("click", addPeriod);

    // --- First paint ---
    renderPeriods();
    renderPalette();
    buildGrid(gridWeekday, "weekday");
    buildGrid(gridWeekend, "weekend");
    updateUndo();
    reflectEnabled();
    serialise();

    // --- Fill from an imported utility rate ---
    // A picked URDB tariff dispatches "demand:apply-tariff" with the market
    // fields; the time-of-use half is applied here, the monthly half by the
    // monthly-capacity editor listening for the same event. A tariff with no
    // time-of-use layer turns the switch off and clears the periods.
    document.addEventListener("demand:apply-tariff", (ev: Event) => {
        const detail = (ev as CustomEvent).detail || {};
        const incoming = Array.isArray(detail.demand_periods)
            ? detail.demand_periods
            : [];
        if (incoming.length > 0) {
            periods = incoming.map((p: any, i: number) => ({
                name: String(p.name ?? `Period ${i + 1}`),
                colour: String(
                    p.colour || DEMAND_COLOURS[i % DEMAND_COLOURS.length]
                ),
                price: Number(p.price) || 0,
            }));
            schedules.weekday = normaliseGrid(detail.demand_schedule_weekday);
            schedules.weekend = normaliseGrid(detail.demand_schedule_weekend);
            if (enabledBox) enabledBox.checked = true;
        } else {
            periods = [
                { name: "Off-peak", colour: DEMAND_COLOURS[0], price: 0 },
            ];
            schedules.weekday = normaliseGrid([]);
            schedules.weekend = normaliseGrid([]);
            if (enabledBox) enabledBox.checked = false;
        }
        activePeriod = 0;
        history.length = 0;
        updateUndo();
        renderPeriods();
        renderPalette();
        buildGrid(gridWeekday, "weekday");
        buildGrid(gridWeekend, "weekend");
        reflectEnabled();
        serialise();
    });
}

// ---------------------------------------------------------------------------
// The monthly capacity-charge editor (Market, Solution B only).
//
// monthly_costs_per_capacity is a JSONField -- empty (bill the connection once
// a year on the annual peak) or twelve prices (bill each month on its own
// peak). Its raw widget shows "[]", which is not an input. This replaces it
// with twelve labelled month boxes that keep the underlying field in sync: all
// blank writes [], any value fills the blanks with 0 and writes twelve numbers,
// which is exactly what the model's validator accepts.
// ---------------------------------------------------------------------------
function initMonthlyCapacityEditor(root?: HTMLElement) {
    if (!document.body.classList.contains("solution-b")) return;
    const scope: ParentNode = root || document;
    const field = scope.querySelector('[name="monthly_costs_per_capacity"]') as
        | HTMLInputElement
        | HTMLTextAreaElement
        | null;
    if (!field || (field as HTMLElement).dataset.monthlyEditor === "1") return;
    (field as HTMLElement).dataset.monthlyEditor = "1";

    const row = field.closest(".settings-field-row") as HTMLElement | null;
    const unitEl = row
        ? (row.querySelector(".settings-field-unit") as HTMLElement | null)
        : null;
    const unit = unitEl ? unitEl.textContent!.trim() : "";

    let values: any[] = [];
    try {
        const parsed = JSON.parse(field.value || "[]");
        if (Array.isArray(parsed)) values = parsed;
    } catch (e) {
        values = [];
    }
    const initial = (i: number) =>
        values.length === 12 && values[i] != null ? String(values[i]) : "";

    // Keep the field on the form but out of sight; the boxes drive it.
    field.style.display = "none";

    // The field's own help icon (the model help_text) explains when to use this
    // and that the annual and monthly charges are additive, so no inline note
    // is repeated here.
    const block = document.createElement("div");
    block.className = "demand-monthly-editor";
    if (row && row.parentElement) row.after(block);
    else field.after(block);

    const gridEl = document.createElement("div");
    gridEl.className = "demand-monthly-grid";
    block.appendChild(gridEl);

    const inputs: HTMLInputElement[] = [];

    function sync() {
        const raw = inputs.map((el) => el.value.trim());
        if (raw.every((v) => v === "")) {
            field.value = "[]";
        } else {
            field.value = JSON.stringify(raw.map((v) => Number(v) || 0));
        }
        // Let the live preview and any listeners see the change.
        field.dispatchEvent(new Event("input", { bubbles: true }));
        field.dispatchEvent(new Event("change", { bubbles: true }));
    }

    DEMAND_MONTHS.forEach((month, i) => {
        const cell = document.createElement("div");
        cell.className = "demand-monthly-cell";
        const label = document.createElement("label");
        label.className = "demand-monthly-month";
        label.textContent = month;
        const input = document.createElement("input");
        input.type = "number";
        input.step = "any";
        input.min = "0";
        input.className = "form-control demand-monthly-input";
        input.value = initial(i);
        if (unit) input.title = unit;
        input.addEventListener("input", sync);
        inputs.push(input);
        cell.appendChild(label);
        cell.appendChild(input);
        gridEl.appendChild(cell);
    });

    // --- Fill from an imported utility rate ---
    // The monthly half of a picked URDB tariff: twelve prices, or empty to clear.
    document.addEventListener("demand:apply-tariff", (ev: Event) => {
        const detail = (ev as CustomEvent).detail || {};
        const monthly = Array.isArray(detail.monthly_costs_per_capacity)
            ? detail.monthly_costs_per_capacity
            : [];
        inputs.forEach((el, i) => {
            el.value =
                monthly.length === 12 && monthly[i] ? String(monthly[i]) : "";
        });
        sync();
    });
}

// ---------------------------------------------------------------------------
// The "Load a North American utility rate" picker (Market, Solution B only).
//
// The search box is plain HTMX (it swaps in the result list). This wires the
// result buttons: clicking one fetches that rate's market fields and dispatches
// "demand:apply-tariff", which the demand and monthly editors fill themselves
// from. Delegated on document so it also catches HTMX-injected results.
// ---------------------------------------------------------------------------
function initUtilityTariffPicker() {
    if (!document.body.classList.contains("solution-b")) return;
    if ((document.body as any).dataset.tariffPicker === "1") return;
    (document.body as any).dataset.tariffPicker = "1";

    document.addEventListener("click", (ev) => {
        const btn = (ev.target as HTMLElement).closest(
            ".utility-tariff-option"
        ) as HTMLElement | null;
        if (!btn) return;
        ev.preventDefault();
        const url = btn.dataset.tariffUrl;
        if (!url) return;
        btn.classList.add("is-loading");
        fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
            .then((data) => {
                document.dispatchEvent(
                    new CustomEvent("demand:apply-tariff", { detail: data })
                );
                // Record which tariff this is (utility — rate, country, currency)
                // so the project keeps that provenance, not just the raw numbers.
                const source =
                    data.country && data.currency
                        ? `${data.label} (${data.country}, ${data.currency})`
                        : data.label;
                const sourceInput = document.getElementById(
                    "demand-charge-source-input"
                ) as HTMLInputElement | null;
                if (sourceInput) sourceInput.value = source;
                const picker = btn.closest(".utility-tariff-picker");
                const note = picker
                    ? (picker.querySelector(
                          ".utility-tariff-applied"
                      ) as HTMLElement | null)
                    : null;
                if (note) {
                    note.textContent = `Loaded from ${source} — edit as needed.`;
                    note.hidden = false;
                }
            })
            .catch(() => {
                /* leave the form as-is on a failed lookup */
            })
            .finally(() => btn.classList.remove("is-loading"));
    });
}

function init() {
    resizeLocationFields();
    document.addEventListener("DOMContentLoaded", function () {
        enableCountUp();
        warnResultsDownload();
        warnChartDataDownload();
        initSettingsFoldaways();
        makePlots();
        filterAssetFormOnSubmit();
        enableToggleGroups();
        enhanceMatrixFields();
        enhanceWindPowerCurveFields();
        updateComponentPreview();
        disableUploadButtonsIfNoFileIsSelected();
        makeProjectLocationMap();
        initAddressFinder();
        initPeriodPlots();
        initBatteryCyclesPlot();
        initOverviewPlot();
        initSensitivityPlot();
        initResultsScrollNav();
        validateMinMaxBase();
        checkResDownloadStatus();
        initDetailPageAssetListeners();
        initDetailPageCapacityFigures();
        initCalculateGuard();
        initUndoButtons();
        initSimulationPeriod();
        initDemandChargeEditor();
        initMonthlyCapacityEditor();
        initUtilityTariffPicker();
        initDocumentationSearch();
        initReportPdf();
        initReportTextEditing();
        initReportFigureControls();
        initReportLogo();
        initReportSave();
        applyInitialChartRanges();
        // Place the energy-bus line/node against their cards. A ResizeObserver on
        // the bus handles both the initial layout and any later reflow (fonts,
        // content) — cleaner than firing on a direct call + rAF + a 300ms timer.
        // The line/node start hidden (CSS opacity:0) and are revealed only once
        // placed, so they never flash regardless of when the observer fires.
        positionEnergyBus();
        if (typeof ResizeObserver !== "undefined") {
            document.querySelectorAll(".component-bus").forEach(function (bus) {
                new ResizeObserver(function () {
                    requestAnimationFrame(positionEnergyBus);
                }).observe(bus);
            });
        }
        window.addEventListener("resize", function () {
            requestAnimationFrame(positionEnergyBus);
        });
        // Reposition after the components section is swapped in (Save) or a tab shows
        $(document).on("htmx:afterSettle", function () {
            requestAnimationFrame(positionEnergyBus);
        });
        $(document).on("shown.bs.tab", function () {
            requestAnimationFrame(positionEnergyBus);
        });

        // Phase 3: Resize (or render) plots when a tab becomes visible
        // Use document-level delegation so dynamically-added tabs (e.g. HTMX modal content) are covered.
        $(document).on("shown.bs.tab", 'a[data-toggle="tab"]', function (e) {
            let targetSelector = $(e.target).attr("href");
            let pane = document.querySelector(targetSelector);
            if (!pane) return;
            // Solution-B lays every chart of a tab out on one scrolling page, so
            // a tab can hold 4-5 big time-series plots at once. Resizing them all
            // synchronously on each switch freezes the page, so we (a) skip the
            // resize when the width is unchanged and (b) stagger any real work one
            // plot per frame. The original UI (one plot per sub-tab) keeps the
            // simple synchronous path.
            let isSolutionB = document.body.classList.contains("solution-b");
            let plotEls = (
                Array.from(
                    pane.querySelectorAll('[id$="-plot"]')
                ) as HTMLElement[]
            ).filter((el) => el.offsetParent !== null); // skip hidden plots

            function handlePlot(el: HTMLElement) {
                let name = el.id.replace("-plot", "");
                if (initializedPlots.has(name)) {
                    let w = el.clientWidth;
                    if (isSolutionB && plotRenderedWidths.get(name) === w) {
                        return; // width unchanged → no need to re-layout
                    }
                    plotRenderedWidths.set(name, w);
                    Plotly.Plots.resize(el);
                } else if (pendingPlots.has(name)) {
                    // Queued for background render; its spinner is showing. Kick
                    // the worker, which prioritises the now-visible plot.
                    renderNextPendingPlot();
                } else {
                    makePlotNew(name);
                    initializedPlots.add(name);
                    plotRenderedWidths.set(name, el.clientWidth);
                }
            }

            if (isSolutionB && plotEls.length > 1) {
                let i = 0;
                (function step() {
                    if (i >= plotEls.length) return;
                    handlePlot(plotEls[i++]);
                    requestAnimationFrame(step);
                })();
            } else {
                plotEls.forEach(handlePlot);
            }

            // Also resize any matrix/formset charts (e.g. aging-component-chart)
            pane.querySelectorAll('[id$="-component-chart"]').forEach(function (
                div: Element
            ) {
                let el = div as HTMLElement;
                if (el.offsetParent === null) return;
                Plotly.Plots.resize(el);
            });
            // The Leaflet location map renders blank/partial if it was created
            // while its tab was hidden; recompute its size once its tab shows.
            if (
                projectLocationMap &&
                pane.querySelector("#project-location-map")
            ) {
                projectLocationMap.invalidateSize();
            }
            // The dispatch heat map may have been built at the wrong width while
            // its tab was hidden (Plotly's 700px fallback) — fix it now visible.
            requestAnimationFrame(resizeDispatchHeatmap);
        });

        // Plotly charts render at a fixed pixel size (renderPlot uses
        // responsive:false so the per-chart height logic isn't overridden), so
        // they DON'T reflow when the browser is zoomed or the window is dragged
        // to a different-size/DPI screen — the lines then spill out of, or
        // underfill, the plot area. Resize every visible, initialised plot on a
        // debounced window resize, staggered one per frame (a results tab can
        // hold 4-5 big charts). Gated on Solution-B; the original UI is unchanged.
        if (document.body.classList.contains("solution-b")) {
            const resizeVisiblePlots = debounce(function () {
                const plots = (
                    Array.from(
                        document.querySelectorAll(".js-plotly-plot")
                    ) as HTMLElement[]
                ).filter((el) => el.offsetParent !== null);
                let i = 0;
                (function step() {
                    if (i >= plots.length) return;
                    const el = plots[i++];
                    try {
                        Plotly.Plots.resize(el);
                        if (el.id) {
                            plotRenderedWidths.set(
                                el.id.replace("-plot", ""),
                                el.clientWidth
                            );
                        }
                    } catch (e) {}
                    requestAnimationFrame(step);
                })();
            }, 200);
            window.addEventListener("resize", resizeVisiblePlots);
        }
    });
    // Enable tooltips
    $(function () {
        $('[data-toggle="tooltip"]').tooltip();
    });

    // HTMX: Re-initialize dynamic widgets after htmx swaps new content
    document.addEventListener("htmx:afterSettle", function (e: any) {
        // Purge stale initializedPlots entries for modal plots; HTMX may have replaced the modal DOM
        document
            .querySelectorAll("#uploadModal [id$='-plot']")
            .forEach(function (div) {
                initializedPlots.delete(
                    (div as HTMLElement).id.replace("-plot", "")
                );
            });
        disableUploadButtonsIfNoFileIsSelected();
        checkResDownloadStatus();
        makePlots();
        // Only reset on #components swaps (validation errors), not upload modal swaps
        const _settled = e.detail.target as HTMLElement;
        if (
            _settled?.id === "components" ||
            !!_settled?.closest?.("#components")
        ) {
            detailAssetUserEdited = false;
            initDetailPageCapacityFigures();
            // Continue a "Save & calculate" flow: start the job now that the
            // edits are saved, unless the save came back with validation errors.
            if (pendingCalcAfterSave) {
                pendingCalcAfterSave = false;
                if (!document.querySelector("#components .is-invalid")) {
                    startJob(new Event("calculate"));
                }
            }
        }
    });

    // Animate height transitions when HTMX swaps #upload-modal-content.
    // Before the swap: lock the current pixel height so there's no jump.
    // After the swap: animate to the new content's scrollHeight, then release to auto.
    document.addEventListener("htmx:beforeSwap", function (e: any) {
        let target = e.detail.target as HTMLElement;
        if (!target || target.id !== "upload-modal-content") return;
        // If the modal isn't visible yet (e.g. still in Bootstrap's open animation),
        // offsetHeight is 0. Skip the animation so the initial content load isn't hidden.
        let currentHeight = target.offsetHeight;
        if (currentHeight === 0) return;
        // Lock height
        target.style.height = currentHeight + "px";
        target.style.overflow = "hidden";
        // Lock dialog width so it doesn't jump during the DOM swap
        let dialog = target.parentElement as HTMLElement;
        dialog.style.maxWidth = dialog.offsetWidth + "px";
    });
    document.addEventListener("htmx:afterSwap", function (e: any) {
        let target = e.detail.target as HTMLElement;
        if (!target || target.id !== "upload-modal-content") return;
        // Only animate if beforeSwap actually locked a height (sentinel check).
        if (!target.style.height) return;

        let dialog = target.parentElement as HTMLElement;
        let card = document.getElementById("project-detail-card");
        let hasCharts = !!target.querySelector("#modal-plot-panel");

        // --- Measure new natural height ---
        let oldHeight = parseFloat(target.style.height);
        target.style.height = "auto";
        let newHeight = target.offsetHeight;
        target.style.height = oldHeight + "px";

        // --- Determine desired dialog width ---
        let oldWidth = parseFloat(dialog.style.maxWidth);
        let newWidth: number;
        if (hasCharts && card) {
            newWidth = card.offsetWidth * 0.95;
        } else {
            // Read Bootstrap's natural width by temporarily clearing the inline override
            dialog.style.maxWidth = "";
            newWidth = dialog.offsetWidth;
            dialog.style.maxWidth = oldWidth + "px";
        }

        // Force a reflow to commit the locked start state before transitioning
        void target.offsetHeight;

        // --- Animate height ---
        target.style.transition = "height 0.35s ease";
        target.style.height = newHeight + "px";
        if (Math.abs(newHeight - oldHeight) > 1) {
            target.addEventListener(
                "transitionend",
                function () {
                    target.style.height = "";
                    target.style.overflow = "";
                    target.style.transition = "";
                },
                { once: true }
            );
        } else {
            target.style.height = "";
            target.style.overflow = "";
            target.style.transition = "";
        }

        // --- Animate dialog width ---
        dialog.style.transition = "max-width 0.35s ease";
        dialog.style.maxWidth = newWidth + "px";
        if (Math.abs(newWidth - oldWidth) > 1) {
            dialog.addEventListener(
                "transitionend",
                function () {
                    dialog.style.transition = "";
                    // Release back to Bootstrap CSS for non-chart content
                    if (!hasCharts || !card) {
                        dialog.style.maxWidth = "";
                    }
                },
                { once: true }
            );
        } else {
            dialog.style.transition = "";
            if (!hasCharts || !card) {
                dialog.style.maxWidth = "";
            }
        }

        // FIXME: This is a hack to get the input preview modal to
        // appear lower on the screen, otherwise the top will be cut off.
        if (hasCharts) {
            if (window.scrollY < 380) {
                dialog.style.transform = "translate(0, 500px)";
            } else {
                dialog.style.transform = "translate(0, 400px)";
            }
        } else {
            dialog.style.transform = "translate(0, 20%)";
        }
    });

    // Blur main content when upload modal opens; unblur when it closes.
    // Restore the correct width for whatever content is already in the modal
    // (chart preview = 95% of card; upload / wait / resolution = Bootstrap default).
    $("#uploadModal").on("show.bs.modal", function () {
        document.body.classList.add("modal-open-blur");
    });
    $("#uploadModal").on("hidden.bs.modal", function () {
        document.body.classList.remove("modal-open-blur");
    });

    // Re-render plots when modal is fully shown (modal animation may hide plots during htmx:afterSettle)
    // Also pre-render plots in hidden tab panes so they're ready when the tab is switched.
    $("#uploadModal").on("shown.bs.modal", async function () {
        await makePlots();
        let modal = this as HTMLElement;
        for (let div of modal.querySelectorAll(
            '.tab-pane:not(.active) [id$="-plot"]'
        )) {
            let name = (div as HTMLElement).id.replace("-plot", "");
            await makePlotNew(name);
            initializedPlots.add(name);
        }
    });

    // Reset Bootstrap's stuck _isTransitioning flag when cancel/close is clicked
    $("#uploadModal").on("click", '[data-dismiss="modal"]', function () {
        console.log("Resetting modal transition state");
        let md = ($("#uploadModal") as any).data("bs.modal");
        if (md) {
            md._isTransitioning = false;
        }
        ($("#uploadModal") as any).modal("hide");
    });

    // Same _isTransitioning fix for the component edit modal.
    // Bootstrap 4.1.3 leaves the flag stuck at true when the transform transition
    // is overridden to none (!important), so dismiss via X button or backdrop never
    // fires without this reset.
    $("#componentEditModal").on("click", '[data-dismiss="modal"]', function () {
        let md = ($("#componentEditModal") as any).data("bs.modal");
        if (md) {
            md._isTransitioning = false;
        }
        ($("#componentEditModal") as any).modal("hide");
    });
    $("#componentEditModal").on("click", function (e: any) {
        if (e.target === e.currentTarget) {
            let md = ($("#componentEditModal") as any).data("bs.modal");
            if (md) {
                md._isTransitioning = false;
            }
            ($("#componentEditModal") as any).modal("hide");
        }
    });

    // HTMX: Re-initialize matrix charts and Wind power curve after
    // validation-error innerHTML swap on the component detail form.
    document.addEventListener("htmx:afterSwap", function (e: any) {
        let target = e.detail.target as HTMLElement;
        if (!target || target.id !== "component-form-container") return;
        enhanceMatrixFields(target);
        enhanceWindPowerCurveFields(target);
        updateComponentPreview(target);
        initDemandChargeEditor(target);
        initMonthlyCapacityEditor(target);
    });

    // HTMX: Re-initialize matrix charts and Wind power curve after htmx swaps
    // the component create form into #picker-modal-body (the create-project-b
    // component picker modal). htmx:afterSettle fires once transitions finish,
    // ensuring the DOM is fully ready before Plotly renders.
    document.addEventListener("htmx:afterSettle", function (e: any) {
        let target = e.detail.target as HTMLElement;
        if (!target || target.id !== "picker-modal-body") return;
        enhanceMatrixFields(target);
        enhanceWindPowerCurveFields(target);
        updateComponentPreview(target);
        initDemandChargeEditor(target);
        initMonthlyCapacityEditor(target);
    });

    // HTMX: Re-initialize matrix charts, Wind power curve, and the output plot
    // when the component edit modal content is first loaded.
    document.addEventListener("htmx:afterSettle", function (e: any) {
        let target = e.detail.target as HTMLElement;
        if (!target || target.id !== "component-edit-modal-content") return;
        enhanceMatrixFields(target);
        enhanceWindPowerCurveFields(target);
        updateComponentPreview(target);
        initDemandChargeEditor(target);
        initMonthlyCapacityEditor(target);
        // Plotly.react (called inside enhanceMatrixFields) schedules DOM updates
        // asynchronously. Use double-rAF to wait for the initial render to
        // complete, then resize charts to fit the modal column. This corrects
        // the 700 px fallback that Plotly uses when the container has no
        // dimensions at render time (Plotly 1.x responsive:true only reacts to
        // window.resize, not container visibility changes).
        requestAnimationFrame(function () {
            requestAnimationFrame(function () {
                document
                    .querySelectorAll<HTMLElement>(
                        "#componentEditModal .js-plotly-plot"
                    )
                    .forEach(function (el) {
                        Plotly.Plots.resize(el);
                    });
            });
        });
        if (!document.getElementById("component-output-plot-container")) return;
        const mb = document.querySelector(
            "#componentEditModal .modal-body"
        ) as HTMLElement | null;
        function tryRender(remaining: number) {
            const container = document.getElementById(
                "component-output-plot-container"
            );
            if (!container) return;
            if (container.offsetParent !== null) {
                // Reset scroll after Plotly renders so the async chart paint
                // cannot leave the modal body scrolled down.
                (async () => {
                    await makePlotNew("component-output");
                    if (mb) mb.scrollTop = 0;
                })();
            } else if (remaining > 0) {
                requestAnimationFrame(() => tryRender(remaining - 1));
            }
        }
        tryRender(60);
    });

    // Resize any Plotly charts in the component edit modal once it is fully
    // visible. Charts that rendered while the modal was still animating
    // default to Plotly's 700 px fallback width; this corrects them once
    // the container has its real dimensions. (Plotly 1.x responsive:true
    // only fires on window.resize, not on container visibility changes.)
    $("#componentEditModal").on("shown.bs.modal", function () {
        document
            .querySelectorAll<HTMLElement>(
                "#componentEditModal .js-plotly-plot"
            )
            .forEach(function (el) {
                Plotly.relayout(el, { autosize: true });
            });
    });

    // HTMX: Close the upload modal when triggered by an HX-Trigger header
    document.addEventListener("closeUploadModal", function () {
        ($("#uploadModal") as any).modal("hide");
    });

    // HTMX: Reload the Inputs tab content when triggered by an HX-Trigger header
    document.addEventListener("reloadInputsTab", function () {
        let container = document.getElementById("tab-inputs-content");
        if (container) {
            let url = container.getAttribute("hx-get");
            if (url) {
                (window as any).htmx.ajax("GET", url, {
                    target: "#tab-inputs-content",
                    swap: "innerHTML",
                });
            }
        }
    });
}

init();

(window as any).makePlotNew = makePlotNew;
